// Mynah's web app: a small hash-routed UI over the local API. No framework — every view is a
// function that returns a DOM subtree, and the router swaps them into #view.

/**
 * True in the build that carries its own back end — the iOS and Android apps, and any copy served
 * as plain static files with nothing behind it. Replaced with a literal by esbuild, so the branch
 * that is not taken is dropped at build time along with everything only it reached.
 */
declare const MYNAH_STANDALONE: boolean;

import { localApi } from './local.js';
import { clipsFor, coversClip, type ClipManifest, type ClipSet } from '../src/shared/clips.js';
import { lookupWord as sharedLookup, type WordInfo } from '../src/shared/lookup.js';
import {
  DEFAULT_PAIR,
  courseWith,
  learnableIn,
  pairHas,
  pairValue,
  speaksLearning,
  type Language,
  type Pair,
} from '../src/shared/pair.js';
import {
  installCost,
  listingById,
  listingFor,
  type Catalogue,
  type PairListing,
} from '../src/shared/catalogue.js';
import * as corpusInstall from './corpus-install.js';
import { pairId } from '../src/shared/item-key.js';
import { inTarget, learningIs, speakIn, speakInTarget, t } from '../src/shared/say.js';
import * as clipCache from './clip-cache.js';
import { credits } from '../src/shared/credits.js';
import { wallpaperTile } from '../src/shared/wallpaper.js';
import { NEXT_DECK } from '../src/shared/api.js';
import { isLearned } from '../src/shared/srs.js';
import { faceFor } from '../src/shared/faces.js';
import { mb, percent, planFetch } from '../src/shared/clip-plan.js';
import { search, type Hit } from '../src/shared/tutor.js';
import {
  buildIndex,
  lookupWords,
  search as translateSearch,
  type Index as TranslateIndex,
  type Match as TranslateMatch,
  type WordMatch,
} from '../src/shared/translate-search.js';
import {
  BANDS,
  buildQuestion,
  DRILLS,
  estimate,
  GIVE_UP_AFTER,
  learnedNothing,
  OPTIONS,
  PER_BAND,
  suggestedDeck,
  type Band,
  type BandResult,
  type DrillKind,
  type Question,
} from '../src/shared/quiz.js';
import { deckHue, deckIcon, storyHue, storyIcon } from './deck-icons.js';
import { artHue, homeArt } from './home-art.js';
import { homeHorizon } from './skyline.js';
import { wordArt } from './word-art.js';
import { voiceQuality } from '../src/shared/pinyin.js';
import { fetchAnswering, NoAnswer } from '../src/shared/answer.js';
import type { SoundGroup } from '../src/shared/pronunciation.js';
import { phonologyFor, readingAdds, type Phonology } from '../src/shared/phonology.js';
import { CARD_MODES, type Strokes, type CardMode, type CardState, type Deck, type DeckProgress, type Grade, type ItemKind, type Section, type Sentence, type SentenceDeck, type SentenceWord, type Sentences, type Stories, type Story, type Hint, type Hints, type KanjiReadings, type TestResult, type Tutor, type Vocab, type Word } from '../src/shared/types.js';
import { readingsUsedBy } from '../src/shared/readings.js';
import { conjugate, formOf } from '../src/shared/inflection.js';

// ---------------------------------------------------------------- utilities

const $ = <T extends HTMLElement = HTMLElement>(sel: string) => document.querySelector<T>(sel)!;

/** Tiny hyperscript. Children may be nodes or strings; strings are escaped by construction. */
function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number | boolean | ((e: Event) => void)> = {},
  ...children: (Node | string | null | false | undefined)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === false || v === null || v === undefined) continue;
    if (typeof v === 'function') node.addEventListener(k.replace(/^on/u, ''), v as EventListener);
    else if (k === 'class') node.className = String(v);
    else if (k === 'html') node.innerHTML = String(v);
    else node.setAttribute(k, String(v));
  }
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    node.append(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return node;
}

/** Inline SVG from a path string, since the icons are all single-path. */
function icon(path: string, size = 16): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('aria-hidden', 'true');
  const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  p.setAttribute('d', path);
  p.setAttribute('fill', 'currentColor');
  svg.append(p);
  return svg;
}

const ICON_PLAY = 'M7 5.5v13l11-6.5z';
const ICON_PAUSE = 'M7 5h3.2v14H7zm6.8 0H17v14h-3.2z';
/** A tick, for the things you have already got. */
const ICON_TICK = 'M20 6.6 9.3 17.3 4 12l1.6-1.6 3.7 3.7L18.4 5z';
const ICON_SPEAK = 'M3 10v4h4l5 4V6L7 10H3zm12.5 2a4 4 0 0 0-2.2-3.6v7.2A4 4 0 0 0 15.5 12zm-2.2-8v2a6 6 0 0 1 0 11.9v2A8 8 0 0 0 13.3 4z';

/** Local calendar day as YYYY-MM-DD, matching what SQLite's localtime grouping produces. */
function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

let toastTimer: number | undefined;
function toast(message: string): void {
  const bar = $('#toast');
  bar.textContent = message;
  bar.classList.add('show');
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => bar.classList.remove('show'), 2200);
}

/**
 * Everything the app asks for, wherever the answer comes from.
 *
 * There are two Mynahs and this is the only place that knows it. With a server there is an HTTP
 * request; on a phone there is no server, and the identical rules run in this very web view over
 * IndexedDB (web/local.ts, src/shared/api.ts). MYNAH_STANDALONE is fixed at build time, so the
 * branch not taken is removed from the bundle rather than carried around unused.
 *
 * NOTHING ELSE IN THIS FILE MAY BRANCH ON IT. The moment the UI knows which build it is, there
 * are two apps to keep working instead of one.
 */
/**
 * How long to wait for the server to say ANYTHING.
 *
 * Twenty seconds is far longer than a reachable server takes to answer and far shorter than
 * forever, which is what it was. See shared/answer.ts for why this bounds the ANSWER and not the
 * download — the distinction is the whole of MYN-152.
 */
const ANSWER_WITHIN_MS = 20_000;

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  if (MYNAH_STANDALONE) return localApi<T>(path, init);
  // A CONNECTION THAT IS REFUSED FAILS FAST AND WAS ALWAYS HANDLED. A connection that is ACCEPTED
  // and then goes quiet — a wedged sidecar, a paused container, a proxy holding the socket — never
  // settled at all, so boot sat on its first await with "Loading…" on screen, no error, no way
  // back but a manual reload. That is the state Kennard was left in when a local server went away
  // underneath a tab (MYN-152).
  const res = await fetchAnswering(
    fetch,
    path,
    { ...init, headers: init?.body ? { 'content-type': 'application/json' } : undefined },
    ANSWER_WITHIN_MS,
  );
  const body = (await res.json()) as T & { error?: string };
  if (!res.ok) throw new Error(body.error ?? `request failed: ${res.status}`);
  return body;
}

// ---------------------------------------------------------------- local preferences

interface Prefs {
  mode: CardMode;
  showPinyin: boolean;
  showTraditional: boolean;
  autoplay: boolean;
  voice: string | null;
  useClips: boolean;
  theme: string;
  cardStyle: CardStyle;
  /** Draw new cards at random rather than easiest-first. */
  shuffle: boolean;
  /** Only introduce sentences with at most one word you have not learned. */
  almostReadable: boolean;
  /**
   * Bring cards you have marked known back into the session.
   *
   * Off by default — the point of marking one is not to see it. It exists so the toggle on the
   * card back is reversible from where you are: without this, a word can be marked known and the
   * only way back is the Words-you-know page, which is a long way to go to undo a tap.
   */
  includeKnown: boolean;
  /** Explain what each character is built from, on the answer side of a card. */
  showHints: boolean;
  /**
   * Show the drawn picture in the corner of a word that has one.
   *
   * ITS OWN SWITCH, because it is its own feature. The picture used to be drawn when showHints
   * was on -- the preference for character etymology -- and the two have nothing to do with each
   * other. All four courses ship all 31 pictures, but only zh-en and ja-en fill hints.hints, so
   * the etymology row is hidden on the English courses and took the only switch for the pictures
   * with it: they could not be turned off at all. And where the row did show, unticking a box
   * about what characters are built from also removed them (MYN-206).
   */
  showPictures: boolean;
  /** Draw each character stroke by stroke, on the answer side of a card. */
  showStrokes: boolean;
  /** Show pinyin above each word while reading a story. */
  storyPinyin: boolean;
  /** Show the English under each story line. */
  storyEnglish: boolean;
  /**
   * Whether the learner has closed the note explaining the Learned tick.
   *
   * It is worth saying once, and then it is furniture. Kept with the other preferences rather than
   * in this device's storage so it stays said on the phone as well as the laptop.
   */
  knowHintClosed: boolean;
  /**
   * The language the app talks to YOU in, when it is not simply the course's.
   *
   * THE WAY OUT. Pick a course explained in a language you cannot read and every word the app says
   * becomes unreadable, including the screen you would use to get back — one wrong tap and the only
   * remedy is clearing site data. So this is separate from the corpus, chosen from the title bar,
   * and it costs nothing to change: MYN-107 put every catalogue in the build, so switching is a
   * runtime call rather than a download. It works offline, and it works when the corpus is wrong.
   *
   * Null means "whatever the course is explained in", which is right until somebody says otherwise.
   */
  readIn: string | null;
}

const PREFS_KEY = 'mynah.prefs';
const DEFAULT_PREFS: Prefs = {
  mode: 'hanzi-en',
  showPinyin: true,
  showTraditional: false,
  autoplay: false,
  voice: null,
  useClips: true,
  theme: 'midnight',
  cardStyle: 'framed',
  readIn: null,
  shuffle: false,
  includeKnown: false,
  // Off by default: a new learner knows nothing, so this would show them an empty deck.
  almostReadable: false,
  showHints: true,
  showPictures: true,
  showStrokes: true,
  storyPinyin: true,
  // On by default: at this level the point is following the story, and a beginner who cannot
  // check the meaning has no way to tell whether they followed it.
  storyEnglish: true,
  knowHintClosed: false,
};

function loadPrefs(): Prefs {
  try {
    return { ...DEFAULT_PREFS, ...(JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}') as Partial<Prefs>) };
  } catch {
    // A corrupt or unavailable store must not stop the app booting.
    return { ...DEFAULT_PREFS };
  }
}

/**
 * Persist preferences to the SERVER, and cache them locally.
 *
 * The server is the source of truth; localStorage is only a cache, so the theme can be applied on
 * the very first paint instead of flashing the default while a request is in flight. It cannot be
 * the store: it is keyed by origin, and the desktop shell takes a new port every launch, so each
 * launch saw an empty cache and reset every preference.
 */
/** Said once per session, not once per keystroke: a broken server breaks every control at once. */
let warnedAboutSaving = false;

function savePrefs(): void {
  void api('/api/prefs', { method: 'PUT', body: JSON.stringify(prefs) }).catch(() => {
    // NOT SWALLOWED ANY MORE. This was the actual shape of "it never remembers my theme": the
    // browser cache is keyed by origin and the desktop app takes a fresh port every launch, so the
    // server is the only thing that remembers. When the write to it failed the setting was already
    // gone, and the app said nothing — it looked like it had worked, right up until the next
    // launch.
    if (warnedAboutSaving) return;
    warnedAboutSaving = true;
    toast(t('Could not save that setting — Mynah cannot reach its own server.'));
  });
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* private browsing — the local cache simply does not persist */
  }
}

// ---------------------------------------------------------------- themes

/**
 * Themes come from the vendored Lumina stylesheet: each is a block of custom properties under
 * [data-theme]. Applying one is just setting that attribute on <html>.
 *
 * `data-mode` is set alongside it because the tone colours have to switch between a light and a
 * dark set, and CSS cannot select on `color-scheme`. Reading it back off the root after the theme
 * lands is the only reliable way to know which a theme is, rather than maintaining a hand-written
 * light/dark list that would drift as themes are added.
 */
function applyTheme(id: string): void {
  const root = document.documentElement;
  root.dataset.theme = id;
  const scheme = getComputedStyle(root).colorScheme;
  root.dataset.mode = scheme.includes('dark') ? 'dark' : 'light';
}

/** Theme ids and their groupings, read out of the stylesheet so the list cannot go stale. */
function availableThemes(): string[] {
  const found = new Set<string>();
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // a stylesheet from another origin — not ours
    }
    for (const rule of Array.from(rules)) {
      const text = (rule as CSSStyleRule).selectorText;
      if (!text) continue;
      for (const m of text.matchAll(/\[data-theme=['"]?([a-z0-9-]+)['"]?\]/gu)) found.add(m[1]!);
    }
  }
  return [...found].sort();
}

/**
 * Card styles. All four render the SAME markup and differ only in CSS, so the card logic never
 * branches on which is chosen and an unused variant cannot quietly rot.
 */
type CardStyle = 'editorial' | 'framed' | 'split' | 'minimal';

const CARD_STYLES: { id: CardStyle; name: string; blurb: string }[] = [
  { id: 'editorial', name: 'Editorial', blurb: 'Centred and airy, with a tone-coloured hairline on top.' },
  { id: 'framed', name: 'Framed', blurb: 'The characters in a tinted well, metadata on a footer rail.' },
  { id: 'split', name: 'Split', blurb: 'A saturated block of the theme’s brand colour over a clean panel.' },
  { id: 'minimal', name: 'Minimal', blurb: 'Hairline rules, very large characters, small-caps labels.' },
];

// ---------------------------------------------------------------- speech

/**
 * Speech synthesis wrapper.
 *
 * Voices load asynchronously and, on some browsers, only after the list is first requested — so
 * `ready` resolves on the voiceschanged event with a timeout, and the app degrades to a disabled
 * speak button rather than failing when no Chinese voice is installed.
 */
const speech = {
  voices: [] as SpeechSynthesisVoice[],
  supported: 'speechSynthesis' in window,

  /**
   * THIS COURSE's pre-rendered clips, or null when the deployment has none for it — which is both
   * "built without audio" and "built with somebody else's". Resolved once at boot, which is
   * enough: changing course reloads the page (see the picker).
   */
  clips: null as ClipSet | null,
  /** One reusable element, so starting a new word stops the previous one. */
  player: null as HTMLAudioElement | null,
  /** Clips that failed to load — never retried, so one bad file cannot stall every play. */
  missing: new Set<string>(),

  async ready(): Promise<void> {
    // The clips ship with the app and beat any system voice, so look for them first. Their absence
    // is normal (a build without `npm run audio`), not an error.
    try {
      const res = await fetch('audio/manifest.json', { cache: 'no-cache' });
      if (res.ok) {
        const manifest = (await res.json()) as ClipManifest;
        // THE RECORDINGS BELONG TO A COURSE, and the manifest may describe several — the served
        // deployment holds every course's clips side by side, while a phone holds one. clipsFor
        // picks this pair's, or says there are none: a manifest describing a different course
        // describes clips that are not about any word now on a card, and `covers` would say "every
        // word has one". Having none is the truthful answer and the app already knows how to
        // render it: the system voice, exactly as for a build made without audio.
        this.clips = clipsFor(manifest, pairId(pair));
      }
    } catch {
      /* no shipped audio — the system voice is the fallback */
    }
    if (!this.supported) return;
    this.voices = this.chinese();
    if (this.voices.length) return;
    await new Promise<void>((resolve) => {
      const done = () => {
        window.speechSynthesis.removeEventListener('voiceschanged', done);
        resolve();
      };
      window.speechSynthesis.addEventListener('voiceschanged', done);
      window.setTimeout(done, 1200);
    });
    this.voices = this.chinese();
  },

  /** Usable Chinese voices, best first — novelty voices are excluded, not merely deprioritised. */
  chinese(): SpeechSynthesisVoice[] {
    return phonology.rankVoices(window.speechSynthesis.getVoices().filter((v) => speaksLearning(pair, v.lang)));
  },

  /** True when the only thing installed is Apple's low-quality compact build. */
  onlyCompact(): boolean {
    return this.voices.length > 0 && this.voices.every((v) => voiceQuality(v) === 'compact');
  },

  pick(): SpeechSynthesisVoice | undefined {
    if (prefs.voice) {
      const chosen = this.voices.find((v) => v.voiceURI === prefs.voice);
      if (chosen) return chosen;
    }
    // Highest-quality mainland voice available, rather than whichever zh-CN voice happens to be
    // listed first — that ordering is arbitrary and put cartoon voices in front.
    return phonology.bestVoice(this.voices);
  },

  /** True when playback uses the shipped recordings rather than a voice installed on this device. */
  usingClips(): boolean {
    return this.clips !== null && prefs.useClips;
  },

  /**
   * Is there a recording in THIS build for this one thing?
   *
   * Asked before playing rather than discovered by failing to play. A build that carries the word
   * clips and not the sentence ones — which is what a phone ships — must send a sentence to the
   * system voice while the tap that asked for it is still live, because iOS will not begin speech
   * outside a user gesture. See src/shared/clips.ts.
   */
  hasClip(clip: string): boolean {
    // Two places a recording can be: shipped in the build, or fetched onto the device afterwards.
    // Both answers are synchronous, because this one decides what a tap does.
    return coversClip(this.clips, clip, (word) => cardWords().has(word)) || clipCache.have(clip);
  },

  /**
   * A recording read off the device, ready to play.
   *
   * READ BEFORE THE TAP, NOT DURING IT. A fetched clip is a file, and reading a file is
   * asynchronous — by the time it arrives the gesture that asked for it is over, and a phone will
   * not begin audio outside one. So the card preloads its own recording when it is dealt, and the
   * tap finds it already here. This is the same lesson as MYN-88, one layer down.
   */
  loaded: null as { clip: string; url: string } | null,

  async preload(clip: string): Promise<void> {
    if (this.loaded?.clip === clip) return;
    // Only fetched clips need this. A shipped one is a path the audio element can take itself.
    if (coversClip(this.clips, clip, (word) => cardWords().has(word))) return;
    if (!clipCache.have(clip)) return;
    const url = await clipCache.url(clip);
    if (!url) return;
    if (this.loaded) URL.revokeObjectURL(this.loaded.url);
    this.loaded = { clip, url };
  },

  /** A voice installed on THIS DEVICE, as opposed to a recording shipped with the app. */
  hasVoice(): boolean {
    return this.supported && this.voices.length > 0;
  },

  /** Whether this one thing can be made audible at all, by a recording or by a voice. */
  canSpeak(clip: string): boolean {
    return (this.usingClips() && this.hasClip(clip)) || this.hasVoice();
  },

  /**
   * Whether WORD cards can be made audible — which is what the drills need to know, since they
   * only ever say single words and every build that ships any clips at all ships those.
   */
  canSpeakAnyWord(): boolean {
    return (this.usingClips() && (this.clips?.covers?.words ?? 'all') !== false) || this.hasVoice();
  },

  /**
   * `clip` names the recording when it differs from the text. Words are filed under the characters
   * they speak, but a sentence cannot be — punctuation and length make unusable filenames — so
   * sentences pass "s/<tatoeba id>" instead.
   */
  speak(text: string, clip = text, onEnd?: () => void): void {
    if (this.usingClips() && this.hasClip(clip) && !this.missing.has(clip)) {
      this.playClip(text, clip, onEnd);
      return;
    }
    this.synthesise(text, onEnd);
  },

  /**
   * Stop whatever is playing, and do NOT call its end callback.
   *
   * Read-along advances on that callback, so a stop which still fired it would start the next line
   * — pressing pause would play on.
   */
  stop(): void {
    if (this.player) {
      this.player.onended = null;
      this.player.pause();
    }
    if (this.supported) window.speechSynthesis.cancel();
  },

  playClip(text: string, clip: string, onEnd?: () => void): void {
    if (this.supported) window.speechSynthesis.cancel();
    if (!this.player) this.player = new Audio();
    const player = this.player;
    player.pause();
    // A word's filename is the characters themselves, so it must be encoded — but a sentence's
    // name carries a "/" that has to survive as a path separator, so encode the segments.
    // A clip fetched onto the device is read from there; one that shipped with the build is a
    // path the element can take itself. A word's filename is the characters themselves, so it must
    // be encoded — but a sentence's name carries a "/" that has to survive as a path separator.
    const path = clip.split('/').map(encodeURIComponent).join('/');
    player.src =
      this.loaded?.clip === clip
        ? this.loaded.url
        // Under the pair's own directory, which the manifest names. A build made before MYN-114
        // has no prefix and its clips sit at audio/ — the default keeps that working.
        : `${this.clips!.prefix ?? 'audio/'}${path}.${this.clips!.format}`;
    player.currentTime = 0;
    // A missing or unplayable clip must not leave the learner with silence: remember it and speak
    // the word with the system voice instead.
    const fallback = () => {
      this.missing.add(clip);
      this.synthesise(text, onEnd);
    };
    player.onerror = fallback;
    player.onended = onEnd ?? null;
    player.play().catch(() => {
      // Rejects for two unrelated reasons: autoplay blocked before any user gesture, where there
      // is nothing useful to say and the next play from a tap will work — and a source that could
      // not be loaded, which is the error handler's business.
      //
      // IT USED TO CLEAR onerror HERE, and that is how a missing clip became permanent silence:
      // whichever of the two arrived first won, and when the rejection won it removed the only
      // thing that would have fallen back to a voice. Leaving the handler alone costs nothing —
      // a source that loaded fine never fires error.
    });
  },

  synthesise(text: string, onEnd?: () => void): void {
    // Without a voice there is no "ended" event to wait for, so a read-along would stall here.
    // Hand control straight back instead, and let the caller decide whether to carry on.
    if (!this.supported) {
      onEnd?.();
      return;
    }
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    const voice = this.pick();
    if (voice) utter.voice = voice;
    utter.lang = voice?.lang ?? pair.learning.code;
    utter.rate = 0.85; // a touch slower than default: these are words being learned, not read
    if (onEnd) utter.onend = () => onEnd();
    window.speechSynthesis.speak(utter);
  },
};

// ---------------------------------------------------------------- app state

interface Progress {
  states: Record<string, CardState>;
  decks: DeckProgress[];
  introducedToday: number;
  introLimit: number;
  history: { day: string; count: number }[];
}

/**
 * Which language this build teaches, and which it teaches it in.
 *
 * Defaulted rather than left undefined so that anything rendering before boot finishes says
 * something true. See src/shared/pair.ts.
 */
let pair: Pair = DEFAULT_PAIR;
/**
 * How the language being learned sounds, as far as the cards need to know.
 *
 * Set when the pair lands. Until then it is Mandarin's, which is what the default pair says — and
 * nothing renders a reading before the corpus arrives anyway.
 */
let phonology: Phonology = phonologyFor(DEFAULT_PAIR.learning.code);

/**
 * THE TWO PLACES A LANGUAGE TAG MAY COME FROM, AND THERE ARE ONLY TWO.
 *
 * Almost every tag in this file belongs to text the COURSE supplied — a vocabulary word, a line
 * of a sentence, a story title — and so it is `pair.learning.code` and nothing else. Writing the
 * tag out by hand is how an English course came to announce "kitten" as Chinese to a screen
 * reader, and how the font fallback picked a Chinese face to draw it with.
 *
 * That mistake was found three times before anybody went looking: the audio manifest in MYN-116,
 * the story title in MYN-135, the tapped word in MYN-137. Each was fixed alone, and the next one
 * was found by accident months later.
 *
 * The constants below are the ONLY exception: a handful of views that no course but Mandarin ever
 * draws, because the data behind them exists in no other corpus. The pronunciation guide reads
 * `phonology.sounds`/`phonology.tones`, which phonologyFor() returns as null for everything but
 * zh. The etymology hints read `hints.hints`, which only the Chinese corpus fills. The traditional
 * row is gated on `word.t`, which only the Chinese vocabulary carries. Their text is Chinese
 * because of WHAT IT IS, not because of which course happens to be loaded.
 *
 * Naming them rather than writing the string is what makes the rule checkable: test/lang-tags
 * fails on a bare 'zh-Hans' or 'zh-Hant' anywhere under web/, so a fourth one cannot be added
 * quietly and then found by accident.
 */
/**
 * The sentence-final marks worth putting back after segmentation, in every script a course uses.
 *
 * ONE SET RATHER THAN ONE LANGUAGE'S PAIR. Chinese and Japanese write ？ and ！ full-width and
 * English writes them narrow; a card that only knew the wide ones turned every English question
 * into a statement. Anything a future course marks a question with belongs here too.
 */
const FINAL_MARKS = new Set(['？', '！', '?', '!']);

const MANDARIN_SIMPLIFIED = 'zh-Hans';
/** As MANDARIN_SIMPLIFIED, for the row that shows a word's traditional form beside it. */
const MANDARIN_TRADITIONAL = 'zh-Hant';

/**
 * The tones a run of syllables is actually SAID with.
 *
 * Mandarin raises the first of two third tones, and the writing keeps the original mark — so a
 * card that colours by what is written teaches a sound nobody makes. A language with no tones
 * answers with nothing, and every caller then has nothing to colour, which is correct.
 */
function spokenTonesOf(tones: number[]): number[] {
  return phonology.tones ? phonology.tones.spoken(tones) : [];
}
let vocab: Vocab;
let sentences: Sentences;
let stories: Stories;
let tutor: Tutor;
let hints: Hints;
/** Stroke order, or null for a writing system this build has no data for. */
let strokes: Strokes | null = null;

/**
 * Who the server thinks we are.
 *
 * In local mode this is always the one local user and there is nothing to show — the desktop app
 * has no sign-in and must not grow the furniture of one. The sign-out only appears where there is
 * genuinely a session to end.
 */
let me: { id: string; displayName: string; mode: string; canSignOut: boolean } | null = null;
let progress: Progress;
let prefs = loadPrefs();

/** A deck of either kind, reduced to what the study view needs. */
interface AnyDeck {
  id: string;
  name: string;
  blurb: string;
  /**
   * Which home section this belongs to.
   *
   * AND NO HEADING BESIDE IT. The corpus still carries `group` — prose naming the section in the
   * reader's language — and the app deliberately does not take a copy: a heading sitting next to
   * a key is how the next person comes to match on the heading, which is the whole of MYN-148.
   * Nothing here displays it; the section's own name comes from stops().
   */
  section: Section;
  kind: ItemKind;
  size: number;
  /** Only meaningful for word decks — the part of speech, used in the production prompt. */
  pos: string | null;
}

/**
 * Which section a deck belongs to when its corpus does not say.
 *
 * FOR OLD CORPORA ONLY. A phone keeps the course it downloaded and does not refetch it because the
 * app updated, so one shipped before MYN-148 has decks with a `group` and no `section`. The ids
 * have been len- / ask- / topic- in every course since the beginning, which is what made them
 * usable as the key in the first place.
 *
 * Not the primary path, deliberately. Reading a prefix is a contract nobody declared; the corpus
 * saying what a deck IS, is one everybody can see.
 */
function sectionFromId(id: string): Section {
  if (id.startsWith('len-')) return 'length';
  if (id.startsWith('ask-')) return 'questions';
  if (id.startsWith('topic-')) return 'situations';
  return 'situations';
}

function allDecks(): AnyDeck[] {
  return [
    ...vocab.decks.map((d) => ({
      // The vocabulary has one section and always has, so the app supplies it rather than asking
      // every corpus to repeat it. This is also why the Words tile was the only one still working.
      id: d.id, name: d.name, blurb: d.blurb, section: 'words' as Section,
      kind: 'word' as ItemKind, size: d.words.length, pos: d.pos,
    })),
    ...sentences.decks.map((d) => ({
      id: d.id, name: d.name, blurb: d.blurb,
      section: d.section ?? sectionFromId(d.id),
      kind: 'sentence' as ItemKind, size: d.sentences.length, pos: null,
    })),
  ];
}

/**
 * How many words the computed deck currently holds, as last reported by the server.
 *
 * Kept because a deck has a size and this one's is not knowable from the corpus: it is whatever
 * the plan came back with. Zero before anything has asked, which reads as "nothing yet" rather
 * than as a wrong number.
 */
let plannedSize = 0;

/**
 * The deck with that id, INCLUDING the one that is computed rather than shipped.
 *
 * THE BUG THIS FIXES. MYN-170 taught the server's resolveDeck about `next-up` and left the client
 * looking only at the corpus, so "Study these" navigated to #/study/next-up and studyView's first
 * line — deckById(id) — came back undefined and rendered "That deck does not exist." The endpoint
 * was tested and the journey was not: /api/next?deck=next-up answered correctly the whole time,
 * and the button in front of the learner did not.
 *
 * NOT ADDED TO allDecks(), which is a different question. That list is what the deck lists render
 * and what stopProgress totals, and this deck's words are already inside the Top-N decks — so
 * putting it there would show two tiles for it and count its words twice.
 */
const deckById = (id: string): AnyDeck | undefined => {
  if (id === NEXT_DECK) {
    return {
      id: NEXT_DECK,
      name: t('What to learn next'),
      blurb: t('Chosen for what they open up, and re-chosen as you learn.'),
      section: 'words',
      kind: 'word',
      size: plannedSize,
      pos: null,
    };
  }
  return allDecks().find((d) => d.id === id);
};

/**
 * The set of words the learner has learned, by their characters.
 *
 * "Learned" is the same bar the deck list uses — spacing has reached a day — so nothing in the UI
 * can disagree with anything else about what you know. Computed from progress the app already
 * holds, rather than asking the server a second time.
 */
function knownWords(): Set<string> {
  const known = new Set<string>();
  vocab.words.forEach((w, id) => {
    const st = progress.states[`word:${id}`];
    // KNOWN IS DECLARED OR LEARNED, the same bar the deck list and unknownWordCounts use: a word
    // whose spacing has reached a day, OR one the learner marked "I already know this" outright.
    // Marking a word known leaves its interval at zero, so isLearned alone would miss it and the
    // card would underline a word the learner just told us they know (MYN-223).
    if ((st && isLearned(st)) || knownItems.word.has(id)) known.add(w.s);
  });
  return known;
}

async function refreshProgress(): Promise<void> {
  progress = await api<Progress>('/api/progress');
}

// ---------------------------------------------------------------- shared pieces

/**
 * The reading to print beside a word, or nothing when it would just be the word again.
 *
 * MYN-194 taught the word card and the sentence row this; six other places print a reading and
 * none of them had learned it, so a Japanese kana word said itself twice in the known-words chip,
 * the plan row, the new-words row, the hover aid, the answer line and the tapped-word strip.
 * One rule, asked by everyone who prints one (MYN-203).
 */
function readingFor(word: { s: string; p: string }): string {
  return readingAdds(word.s, word.p) ? word.p : '';
}

/** Pinyin with one span per syllable, coloured by tone. */
function pinyinEl(word: Word, opts: { hidden?: boolean; spoken?: boolean } = {}): HTMLElement {
  const sylls = phonology.syllables(word.p);
  const tones = opts.spoken ? spokenTonesOf(word.tones) : word.tones;
  const wrap = el('div', { class: `pinyin${opts.hidden ? ' hidden-pinyin' : ''}` });
  sylls.forEach((s, i) => {
    wrap.append(el('span', { class: 'syl', 'data-tone': String(tones[i] ?? 5) }, s));
  });
  if (opts.hidden) {
    wrap.title = `Click to show ${pair.reading?.name ?? 'the reading'}`;
    wrap.addEventListener('click', () => {
      prefs.showPinyin = true;
      savePrefs();
      render();
    });
  }
  return wrap;
}

function speakButton(
  text: string,
  opts: { big?: boolean; label?: string; clip?: string; iconOnly?: boolean } = {},
): HTMLElement {
  const btn = el(
    'button',
    {
      class: `speak${opts.big ? ' big' : ''}`,
      type: 'button',
      onclick: (e: Event) => {
        e.stopPropagation();
        speech.speak(text, opts.clip ?? text);
      },
    },
    icon(ICON_SPEAK, opts.big ? 20 : 15),
    // A speaker icon needs no caption; the label is only there where the button stands alone.
    opts.iconOnly ? null : (opts.label ?? t('Listen')),
  );
  if (opts.iconOnly) {
    btn.classList.add('icon-only');
    btn.setAttribute('aria-label', `Listen to ${text}`);
    btn.title = `Listen to ${text}`;
  }
  if (!speech.canSpeak(opts.clip ?? text)) {
    // Per card, not per app: a build can hold the word recordings and not the sentence ones, and
    // then whether this button can make a sound depends on which card it is sitting on.
    btn.setAttribute('disabled', 'true');
    btn.title =
      `No audio available: this build has no recording for it, and no ${pair.learning.everyday} voice is installed.`;
  }
  return btn;
}

/** A checkbox whose checked state is set as a property — `el` attributes cannot carry a boolean. */
function checkbox(on: boolean, onChange: (on: boolean) => void): HTMLInputElement {
  const box = el('input', {
    type: 'checkbox',
    onchange: (e: Event) => onChange((e.currentTarget as HTMLInputElement).checked),
  }) as HTMLInputElement;
  box.checked = on;
  return box;
}

function progressRing(fraction: number): HTMLElement {
  const r = 20;
  const circ = 2 * Math.PI * r;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'ring');
  svg.setAttribute('viewBox', '0 0 46 46');
  for (const cls of ['track', 'fill']) {
    const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    c.setAttribute('class', cls);
    c.setAttribute('cx', '23');
    c.setAttribute('cy', '23');
    c.setAttribute('r', String(r));
    if (cls === 'fill') {
      c.setAttribute('stroke-dasharray', String(circ));
      c.setAttribute('stroke-dashoffset', String(circ * (1 - fraction)));
    }
    svg.append(c);
  }
  return el('div', { class: 'ring-wrap' }, svg);
}

/**
 * The deck's mark inside its progress ring.
 *
 * Combining them keeps one element saying both what the deck is and how far through it you are —
 * a separate icon and ring side by side would crowd the tile and say the same thing twice.
 */
function deckBadge(deckId: string, fraction: number): HTMLElement {
  const wrap = progressRing(fraction);
  wrap.style.color = `var(--${deckHue(deckId)})`;
  wrap.append(deckIcon(deckId));
  wrap.title = `${Math.round(fraction * 100)}% learned`;
  return wrap;
}

// ---------------------------------------------------------------- decks view

// ---------------------------------------------------------------- home

/**
 * The home page: a journey down the page, not a grid of tiles.
 *
 * The first attempt reused the deck card — a rounded box with a small glyph and three lines of
 * text — and it was right to reject it. The front door looked like every other page, which is
 * exactly the problem it was meant to solve: arriving somewhere should feel different from
 * browsing a catalogue.
 *
 * So: no boxes. Each stop is an illustration with a person in it and a line of text beside it,
 * alternating down a path. The path fills in behind you as you learn, which is the one piece of
 * information the page really owes a learner — not how many decks exist, but how far along they
 * are.
 */
interface Stop {
  id: string;

  name: string;
  blurb: string;
  href: string;
  /**
   * The decks this stop covers, named by section.
   *
   * This was the deck's `group` — a heading, in whichever language the corpus wrote it — compared
   * against an English literal. Only zh-en ever matched, so most of this page led nowhere on every
   * other course (MYN-148).
   */
  section?: Section;
}

/**
 * The places you can go. Not a sequence — you can start anywhere, and nothing here is a prerequisite
 * for anything else, which is why they are not strung on a line.
 *
 * EACH ONE IS TITLED IN CHINESE. The themes name typefaces — Inter, VT323, JetBrains Mono — but
 * nothing ever loads them, so all 44 fall back to the same system sans and every page came out
 * looking the same. Bundling a display face is possible but it has to be self-hosted (Google Fonts
 * is unreachable from mainland China and would break offline anyway), and a downloaded face is a
 * licence and a payload to carry.
 *
 * The better answer was already installed: the CJK face every device ships. Set large, 词语 and 故事
 * look nothing like Inter, they cost nothing, they work offline, and on a Mandarin app they are the
 * most on-brand type available. The English sits underneath, so a beginner who cannot read them yet
 * loses nothing.
 */
/**
 * Built when it is asked for, not once at module scope.
 *
 * Two of these describe the languages by name, and the pair is not known until the app has
 * fetched it. A list frozen at import time would be a list that says "Mandarin" in a build that
 * teaches something else.
 */
function stops(): Stop[] {
  return [
  { id: 'words', name: 'Words', blurb: 'Start with the ones people actually say.', href: '#/decks/words', section: 'words' },
  { id: 'length', name: 'Say something', blurb: 'Whole sentences, from two words up to eight.', href: '#/decks/length', section: 'length' },
  // NOT '吗, 什么, 哪儿, 为什么'. Four Mandarin particles, on the front screen of every course,
  // advertising a Japanese deck with Chinese. Both catalogues had already declined to translate
  // them literally — zh says "how to ask, and where each question word goes" and ja says the
  // same — so the English was the only place still naming one language's words (MYN-144).
  // THE WORDS THE OTHER DECKS NEVER SHOW IN A SENTENCE. Half of every course's vocabulary was in
  // that position — studied on a card and never met in context, because every other family takes
  // the easiest sentences it can find and a word ranked past a few hundred is in none of them.
  { id: 'in-context', name: 'In context', blurb: 'Every word you study, in a sentence that uses it.', href: '#/decks/in-context', section: 'in-context' },
  { id: 'questions', name: 'Ask something', blurb: 'How to ask, and where the question word goes.', href: '#/decks/questions', section: 'questions' },
  { id: 'situations', name: 'Out in the world', blurb: 'The shop, the doctor, somebody’s kitchen.', href: '#/decks/situations', section: 'situations' },
  { id: 'stories', name: 'Stories', blurb: 'Read aloud, one line at a time, with the words lit up.', href: '#/stories' },
  { id: 'drills', name: 'Drills', blurb: 'Fast practice. Nothing here touches your schedule.', href: '#/drills' },
  {
    id: 'ask',
    name: 'Ask me',
    // Built by t() HERE rather than left as a template for the renderer to translate. Every other
    // blurb is a plain literal that t(stop.blurb) looks up at render; this one interpolates, so by
    // the time it reached the renderer the pattern was gone and the lookup could never match. It
    // sat in English inside a Japanese course with a perfectly good translation in the catalogue.
    blurb: t`Grammar explained, and ${pair.explainedIn.name} turned into ${pair.learning.everyday}.`,
    href: '#/ask',
  },
    // Offered only where there is a guide. A build that teaches a language nobody has written one
    // for should not have a Pronunciation tile leading to an empty page.
    ...(phonology.sounds
      ? [{ id: 'pronounce', name: 'Pronunciation', blurb: 'Tones, initials, finals, and the sounds that shift.', href: '#/pronounce' }]
      : []),
  { id: 'test', name: 'Where you are', blurb: 'A short test that estimates how much you can read.', href: '#/test' },
  { id: 'known', name: 'Words you know', blurb: 'What you have marked as known. Mynah stops asking and aims at the rest.', href: '#/known' },
  { id: 'made', name: 'Made for you', blurb: 'Sentences you can read every word of — thousands already here, and more written to order.', href: '#/made' },
  ];
}

/**
 * One deck tile, hue and all.
 *
 * The colour goes on the TILE so `currentColor` inside it means this deck's own hue — the glow, the
 * stack edges and the spotlight then all agree without any of them naming a colour.
 */
function deckTile(deck: AnyDeck): HTMLElement {
  const p = progress.decks.find((x) => x.deckId === deck.id);
  const learned = p?.learned ?? 0;
  const due = p?.due ?? 0;
  const seen = p?.seen ?? 0;
  // BUILT LIKE THE CARD IT STANDS FOR. A card is a WELL — a tinted inset panel holding what you
  // are looking at — over a RAIL, a footer below a hairline carrying everything that is not the
  // thing itself. A deck tile was a badge and a paragraph in a row, which is why matching the
  // corner radius in MYN-156 changed so little: the shapes were never the difference.
  const tile = el(
    'a',
    { class: 'deck', href: `#/study/${deck.id}` },
    el(
      'div',
      { class: 'deck-well' },
      deckBadge(deck.id, deck.size ? learned / deck.size : 0),
      el('div', { class: 'deck-name' }, deck.name),
      el('div', { class: 'deck-blurb' }, deck.blurb),
    ),
    el(
      'div',
      { class: 'deck-rail' },
      t`${learned} learned · ${seen} seen · ${deck.size} total`,
      due > 0 ? el('span', { class: 'due' }, t`  ·  ${due} due`) : null,
    ),
  );
  // THE DECK'S OWN HUE, AS ITS TONE. A card tints its well and its hairline from --tone-accent;
  // giving a deck the same variable makes the whole tile take its own colour the way a card takes
  // its tone's, rather than only the mark doing so.
  tile.style.color = `var(--${deckHue(deck.id)})`;
  tile.style.setProperty('--tone-accent', `var(--${deckHue(deck.id)})`);
  return tile;
}

/**
 * Where a deck came from, for the back link on the study page.
 *
 * Going all the way home from a card would be a long way back for someone working through the
 * question decks — the useful step back is the shelf you took it off, not the front door.
 */
function areaOf(deck: AnyDeck): Stop | null {
  return stops().find((x) => x.section !== undefined && x.section === deck.section) ?? null;
}

/** Totals across a stop's decks, or null when the stop is not made of decks. */
function stopProgress(section?: Section): { learned: number; total: number; due: number } | null {
  if (!section) return null;
  let learned = 0;
  let total = 0;
  let due = 0;
  for (const deck of allDecks().filter((d) => d.section === section)) {
    const p = progress.decks.find((x) => x.deckId === deck.id);
    learned += p?.learned ?? 0;
    due += p?.due ?? 0;
    total += deck.size;
  }
  return { learned, total, due };
}

/**
 * The deck to pick up, and how much is waiting.
 *
 * Whichever deck has the most due: that is where the backlog is. With nothing due it falls back to
 * the deck furthest along, because what you were last making progress on is the likeliest thing you
 * meant to carry on with.
 */
function nextUp(): { deck: AnyDeck; due: number; totalDue: number } | null {
  let totalDue = 0;
  let best: { deck: AnyDeck; due: number } | null = null;
  let furthest: { deck: AnyDeck; learned: number } | null = null;
  for (const deck of allDecks()) {
    const p = progress.decks.find((x) => x.deckId === deck.id);
    const due = p?.due ?? 0;
    const learned = p?.learned ?? 0;
    totalDue += due;
    if (due > 0 && (!best || due > best.due)) best = { deck, due };
    if (learned > 0 && (!furthest || learned > furthest.learned)) furthest = { deck, learned };
  }
  if (best) return { deck: best.deck, due: best.due, totalDue };
  if (furthest) return { deck: furthest.deck, due: 0, totalDue };
  return null;
}

/** Every surface that lights under the cursor. */
const LIT = '.place, .deck, .quiz-opt';

/**
 * Let the tiles know where the pointer is.
 *
 * Two custom properties on whichever tile is under the cursor, which a radial gradient reads to put
 * a highlight exactly under the pointer. Done once on the container rather than per tile — forty
 * listeners for one pointer is thirty-nine too many — and written inside a frame so a fast sweep
 * across the page cannot queue up more style writes than the browser will paint.
 */
function trackPointer(scope: HTMLElement): void {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  let pending: { tile: HTMLElement; x: number; y: number } | null = null;
  let frame = 0;

  scope.addEventListener('pointermove', (e) => {
    const tile = (e.target as HTMLElement).closest<HTMLElement>(LIT);
    if (!tile) return;
    const rect = tile.getBoundingClientRect();
    pending = { tile, x: e.clientX - rect.left, y: e.clientY - rect.top };
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      if (!pending) return;
      pending.tile.style.setProperty('--mx', `${pending.x}px`);
      pending.tile.style.setProperty('--my', `${pending.y}px`);
    });
  });
}

/**
 * A tile's second line: the same label in the language being learned.
 *
 * An empty span rather than nothing, because the tiles are a grid and a missing row in one of them
 * shifts everything under it. Empty and unlabelled is invisible; absent is a layout bug.
 */
function nativeLine(label: string, code: string): HTMLElement {
  return el('div', { class: 'place-zh', lang: code }, inTarget(label) ?? '');
}

/**
 * What a card mode is called, in terms of this pair.
 *
 * "汉字 → English" is not a phrase to translate — it names the two languages, and for another pair
 * it names two different ones. The audio mode is the exception: it is about hearing rather than
 * about either language, so it is an ordinary sentence.
 */
/**
 * The card modes this course can actually put on a card.
 *
 * "Listen to meaning" hides the word and plays it, which is the point of it — and with nothing to
 * play it is a blank card with a button that does nothing. A pair with no recordings and no system
 * voice for the language cannot offer it, so it is not offered.
 */
function usableModes(): typeof CARD_MODES {
  return CARD_MODES.filter((m) => m.id !== 'audio' || speech.canSpeakAnyWord());
}

/**
 * Fall back when the stored mode is one this course cannot do.
 *
 * The mode is one preference for the whole app, so a mode chosen for a course with recordings is
 * still selected after switching to one without — nobody chose it here, and it lands them on a
 * blank card. Same rule, and the same reason, as a stored theme this build does not ship.
 */
function settleMode(): void {
  if (usableModes().some((m) => m.id === prefs.mode)) return;
  // NOT SAVED. This is what to render now, not a decision the learner made — and writing it down
  // would throw away a real preference for good. A device whose voice list loads slowly, or a
  // course installed for an afternoon, would each have quietly rewritten a choice somebody made on
  // purpose. Leaving the stored value alone means it comes back when the course that supports it
  // does.
  prefs.mode = usableModes()[0]?.id ?? DEFAULT_PREFS.mode;
}

function modeName(id: CardMode): string {
  if (id === 'audio') return t('Listen → meaning');
  const [from, to] =
    id === 'hanzi-en' ? [pair.learning, pair.explainedIn] : [pair.explainedIn, pair.learning];
  return `${from.name} → ${to.name}`;
}

function homeView(): HTMLElement {
  const view = el('div', { class: 'home' });
  // The horizon of the place the language comes from — the Great Wall, Fuji, London — keyed to what
  // is being learned, drawn behind everything along the foot of the page (MYN-235).
  const horizon = homeHorizon(pair.learning.code);
  if (horizon) view.append(horizon);
  const next = nextUp();
  const hour = new Date().getHours();
  const english = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const inLearning = inTarget(english);

  // A greeting and one sentence about the state of things — no statistics, no chrome.
  const hello = el('div', { class: 'hello' });
  hello.append(
    // The learner's own language leads. The greeting is addressed TO them, and it is the one line
    // on the page that is not something to be learned. Underneath it, the same words in the
    // language they came for — which is why that line uses the OTHER catalogue.
    el('h1', {}, t(english)),
    el(
      'p',
      {},
      next
        ? next.due > 0
          ? t`${next.totalDue} due across your decks.`
          : t('Nothing is due. A good day to start something new.')
        : t('Everything below is new. Words are the place to begin.'),
    ),
  );
  // Under the greeting, the same words in the language they came for — which is why this line uses
  // the other catalogue, and why it is simply absent when that catalogue has nothing to say.
  if (inLearning) {
    hello.insertBefore(
      el('div', { class: 'hello-zh', lang: pair.learning.code }, inLearning),
      hello.children[1] ?? null,
    );
  }
  if (next) {
    hello.append(
      el(
        'a',
        { class: 'hello-go', href: `#/study/${next.deck.id}` },
        next.due > 0 ? t`Carry on with ${next.deck.name}` : t`Back to ${next.deck.name}`,
        el('span', { class: 'hello-arrow' }, '→'),
      ),
    );
  } else {
    hello.append(
      el('a', { class: 'hello-go', href: '#/study/top-100' }, t`Start with ${deckById('top-100')?.name ?? 'Top 100'}`, el('span', { class: 'hello-arrow' }, '→')),
    );
  }
  view.append(hello);

  // Independent places, not a route. Nothing connects them and nothing implies an order, because
  // there is none — a learner can start with stories or with words and neither is wrong.
  const places = el('div', { class: 'places' });

  for (const stop of stops()) {
    // A SECTION WITH NOTHING IN IT IS NOT A PLACE. ja-en has no question decks — nobody has built
    // them — so a tile promising them led to "0 decks · 0 of 0 learned", which reads as breakage
    // rather than as absence. Stops with no section (Stories, Drills, Ask me) are pages in their
    // own right and are always shown.
    if (stop.section && !allDecks().some((d) => d.section === stop.section)) continue;
    const stats = stopProgress(stop.section);
    const art = el('div', { class: 'place-art' });
    art.append(homeArt(stop.id, pair.glyphs ?? []));

    // The hue goes on the TILE, not on the picture. Everything inside inherits it, so
    // `currentColor` in the hover treatment means THIS place's colour — set only on the
    // illustration, it left the highlight picking up the page's ink and washing every tile the
    // same grey.
    const tile = el('a', { class: 'place', href: stop.href });
    tile.style.color = `var(--${artHue(stop.id)})`;
    tile.append(
      // The sheen that sweeps across on hover. An element rather than a pseudo because it has to
      // move independently of the highlight that sits under it.
      el('span', { class: 'place-sheen', 'aria-hidden': 'true' }),
      art,
      // The learner's own language leads and the one they came for follows, the same way round as
      // the greeting: the labels on a signpost are read, not studied, and they belong in the
      // language the learner already has. The second line is a taste of the other one, and is
      // simply absent for a pair that has no word for it.
      el('div', { class: 'place-name' }, t(stop.name)),
      nativeLine(stop.name, pair.learning.code),
      el('div', { class: 'place-blurb' }, t(stop.blurb)),
    );
    if (stats && stats.total) {
      tile.append(
        el(
          'div',
          { class: 'place-note' },
          stats.learned > 0
            ? t`${stats.learned.toLocaleString()} of ${stats.total.toLocaleString()}`
            : t('Not started'),
          stats.due > 0 ? el('span', { class: 'due' }, t` · ${stats.due} due`) : null,
        ),
      );
    }
    places.append(tile);
  }
  trackPointer(places);
  view.append(places);
  return view;
}

/** One study area's decks, and nothing else. */
function areaView(id: string): HTMLElement {
  const stop = stops().find((s) => s.id === id);
  if (!stop?.section) return el('p', { class: 'empty' }, t('No such section.'));

  const decks = allDecks().filter((d) => d.section === stop.section);
  // Reachable by typing the address even when the home page leaves the tile out, so it says what
  // is true rather than counting to zero in a table of decks that is not there.
  if (decks.length === 0) {
    return el(
      'div',
      {},
      el(
        'div',
        { class: 'page-head' },
        el('a', { class: 'back-link', href: '#/' }, t('← Home')),
        el('h1', {}, t(stop.name)),
      ),
      el(
        'div',
        { class: 'known-empty' },
        // NO LANGUAGE NAME. It was `The ${pair.learning.name} course`, which reads "The 英语
        // course" to somebody using the app in English — a course names itself from its own
        // explainer's point of view (MYN-147), and naming it here buys nothing the heading above
        // has not already said.
        el('p', {}, t('This course has nothing here yet.')),
        el('a', { class: 'more', href: '#/' }, t('Go and study →')),
      ),
    );
  }
  const stats = stopProgress(stop.section)!;

  const view = el('div');
  view.append(
    el(
      'div',
      { class: 'page-head' },
      el('a', { class: 'back-link', href: '#/' }, t('← Home')),
      el('h1', {}, t(stop.name)),
      el('p', {}, t(stop.blurb)),
      el(
        'p',
        { class: 'field-hint' },
        // COUNTS WHAT IS ON THE SCREEN. The computed deck is a tile like the others, so a count
        // that skipped it said "4 decks" above five of them. Its words are already inside the
        // Top-N decks, so it adds nothing to the learned or total figures — only to the count.
        t`${decks.length + (stop.section === 'words' ? 1 : 0)} decks · ${stats.learned.toLocaleString()} of ${stats.total.toLocaleString()} learned`,
        stats.due > 0 ? t` · ${stats.due} due now` : '',
      ),
    ),
  );

  const grid = el('div', { class: 'decks' });
  // THE COMPUTED DECK LEADS THE WORDS, because it is the answer to the question the other tiles
  // raise. Top 100 / Top 500 / Top 1000 is a syllabus and says nothing about where THIS learner
  // is; this one holds only what they do not know yet, in the order that opens the most.
  if (stop.section === 'words') grid.append(planTile());
  for (const deck of decks) grid.append(deckTile(deck));
  trackPointer(grid);
  view.append(grid);
  return view;
}

/**
 * The tile for the deck with no fixed contents.
 *
 * Built rather than borrowed from deckTile, because its rail cannot say "0 learned · 30 total":
 * by construction nothing in it is learned — the moment a word is, it leaves. What it has to say
 * instead is what the deck is FOR, which is how many sentences it opens.
 */
function planTile(): HTMLElement {
  const tile = el(
    'a',
    { class: 'deck', href: '#/next' },
    el(
      'div',
      { class: 'deck-well' },
      deckBadge(NEXT_DECK, 0),
      el('div', { class: 'deck-name' }, t('What to learn next')),
      el('div', { class: 'deck-blurb' }, t('Chosen for what they open up, and re-chosen as you learn.')),
    ),
    el('div', { class: 'deck-rail' }, t('Working it out…')),
  );
  tile.style.color = `var(--${deckHue(NEXT_DECK)})`;
  tile.style.setProperty('--tone-accent', `var(--${deckHue(NEXT_DECK)})`);
  void (async () => {
    const plan = await api<PlanReply>('/api/plan');
    plannedSize = plan.size;
    const rail = tile.querySelector('.deck-rail');
    if (!rail) return;
    rail.replaceChildren(
      document.createTextNode(
        plan.items.length === 0
          ? t('Nothing left to suggest')
          : t`${plan.size} words · opens ${plan.opens} sentences`,
      ),
    );
  })();
  return tile;
}

// ---------------------------------------------------------------- what you already know

interface KnownReply {
  kind: 'word' | 'sentence';
  total: number;
  items: { id: number; knownAt: number }[];
}

/** How many of the list to render at once. Long enough to scroll, short enough to paint. */
const KNOWN_PAGE = 200;

let knownShown = KNOWN_PAGE;

/**
 * The words you know.
 *
 * A RECORD, NOT A CHORE. The first cut of this page opened on the whole vocabulary in frequency
 * order and invited the learner to tick through it — six and a half thousand words, presented as
 * something to get through before the app was any use. That is backwards. Words arrive here one at
 * a time, from the card, at the moment the learner sees one they already have; this page is where
 * they accumulate and the only thing to do here is take one back.
 *
 * Newest first, because the interesting end is what you just marked — that is where a mistake will
 * be, and taking it back is the only action this page offers.
 */
function knownView(): HTMLElement {
  const view = el('div');
  const head = el('div', { class: 'page-head' });
  const counted = el('p', { class: 'field-hint' }, t('Counting…'));
  head.append(
    el('a', { class: 'back-link', href: '#/' }, t('← Home')),
    el('h1', {}, t('Words you know')),
    el(
      'p',
      {},
      t("Everything you have marked as already known while studying. These never come up again, and the sentences built from them count as readable."),
    ),
    counted,
  );
  view.append(head);

  const body = el('div', { class: 'known-body' }, el('p', { class: 'empty' }, t('Loading…')));
  view.append(body);

  async function fill(): Promise<void> {
    const reply = await api<KnownReply>('/api/known?kind=word');
    counted.textContent = reply.items.length
      ? `${reply.items.length.toLocaleString()} of ${reply.total.toLocaleString()} words`
      : '';

    if (!reply.items.length) {
      body.replaceChildren(
        el(
          'div',
          { class: 'known-empty' },
          el('p', {}, t('Nothing here yet.')),
          el(
            'p',
            { class: 'field-hint' },
            t('When a card comes up that you already know, press '),
            el('kbd', {}, 'K'),
            t(' or use “I already know this” under the answer. It will stop coming up, and land here.'),
          ),
          el('a', { class: 'more', href: '#/' }, t('Go and study →')),
        ),
      );
      return;
    }

    const unmark = async (id: number) => {
      await api('/api/known', {
        method: 'POST',
        body: JSON.stringify({ kind: 'word', ids: [id], known: false }),
      });
      await refreshProgress();
      await fill();
    };

    const page = reply.items.slice(0, knownShown);
    const grid = el('div', { class: 'word-chips' });
    for (const item of page) {
      const w = vocab.words[item.id];
      if (w) grid.append(chipFor(item.id, w, unmark));
    }

    const parts: HTMLElement[] = [grid];
    const rest = reply.items.length - page.length;
    if (rest > 0) {
      parts.push(
        el(
          'button',
          {
            class: 'more',
            type: 'button',
            onclick: () => {
              knownShown += KNOWN_PAGE;
              void fill();
            },
          },
          t`Show ${Math.min(KNOWN_PAGE, rest)} more · ${rest.toLocaleString()} left`,
        ),
      );
    }
    body.replaceChildren(...parts);
  }

  void fill();
  return view;
}

/**
 * One word you know, and the way back.
 *
 * The whole chip is the button because the only thing it does is undo — there is nothing else to
 * click, and a separate little × would be a smaller target for no gain.
 */
function chipFor(id: number, w: Word, unmark: (id: number) => Promise<void>): HTMLElement {
  return el(
    'button',
    {
      class: 'word-chip on',
      type: 'button',
      'data-tip': t('Put this back in the rotation'),
      onclick: () => void unmark(id),
    },
    el('span', { class: 'chip-hanzi', lang: pair.learning.code }, w.s),
    el('span', { class: 'chip-pinyin' }, readingFor(w)),
    el('span', { class: 'chip-gloss' }, w.e[0] ?? ''),
  );
}

// ---------------------------------------------------------------- sentences from your words

interface MadeReply {
  available: boolean;
  known: number;
  items: { id: number; s: string; e: string; words: { t: string; id: number }[]; madeAt: number }[];
}

// ---------------------------------------------------------------- what to learn next

interface PlanReply {
  known: number;
  size: number;
  opens: number;
  items: {
    id: number;
    s: string;
    p: string;
    e: string[];
    pos: string;
    completes: number;
    approaches: number;
  }[];
}

/**
 * The deck that decides itself.
 *
 * Every other deck is a fixed list, which is the right shape for a syllabus and the wrong one for
 * the question a learner actually has — what should I do next — because the answer changes every
 * time they answer a card. This one is computed from what they know, holds only what they do not,
 * and is re-planned from scratch on every visit. Nothing is stored, so nothing goes stale.
 *
 * The ORDER is the feature, and it is arithmetic rather than opinion: each next word is the one
 * that finishes the most nearly-finished sentences. On Japanese that puts particles and the core
 * verbs first — よ, か, 何, する, の, 食べる — and leaves 16% more of the corpus readable after a
 * hundred words than frequency order does. Nobody had to assert that grammar comes first.
 */
function nextView(): HTMLElement {
  const view = el('div');
  const head = el('div', { class: 'page-head' });
  head.append(
    el('a', { class: 'back-link', href: '#/' }, t('← Home')),
    el('h1', {}, t('What to learn next')),
    el(
      'p',
      {},
      t("Chosen for what they open up rather than for how common they are: each one finishes sentences you are already one word away from. The list changes as you learn."),
    ),
  );
  const summary = el('p', { class: 'field-hint' }, t('Working it out…'));
  const list = el('div', { class: 'plan-list' });
  const go = el('a', { class: 'btn btn-primary write-btn plan-go', href: `#/study/${NEXT_DECK}` }, t('Study these'));
  go.hidden = true;
  view.append(head, summary, go, list);

  void (async () => {
    const plan = await api<PlanReply>('/api/plan');
    plannedSize = plan.size;
    if (plan.items.length === 0) {
      // Either everything is known or the corpus has nothing left to unlock. Both are real
      // answers, and neither is an error.
      summary.textContent = t(
        'Nothing left to suggest — every word this course can teach you is already marked known.',
      );
      return;
    }
    summary.replaceChildren(
      document.createTextNode(
        t`These ${plan.size} words make ${plan.opens} more sentences readable, on top of the ${plan.known} words you already know.`,
      ),
    );
    go.hidden = false;
    list.replaceChildren(
      ...plan.items.map((w, i) =>
        el(
          'div',
          { class: 'plan-row' },
          el('span', { class: 'plan-rank' }, String(i + 1)),
          el(
            'div',
            { class: 'plan-word' },
            el('span', { class: 'plan-han', lang: pair.learning.code }, w.s),
            prefs.showPinyin && readingFor(w) ? el('span', { class: 'plan-p' }, readingFor(w)) : null,
          ),
          el(
            'div',
            { class: 'plan-sense' },
            el('span', { class: 'plan-e' }, w.e.join('; ')),
            el('span', { class: 'plan-pos' }, w.pos),
          ),
          // What this word BUYS, which is the whole argument for its position in the list.
          el(
            'div',
            { class: 'plan-gain' },
            w.completes > 0
              ? el('span', { class: 'plan-opens' }, t`opens ${w.completes}`)
              : el('span', { class: 'plan-builds' }, t('builds towards')),
            el('span', { class: 'plan-near' }, t`${w.approaches} nearly`),
          ),
          speakButton(w.s, { iconOnly: true }),
        ),
      ),
    );
  })();
  return view;
}

interface ReadableReply {
  known: number;
  corpus: number;
  readable: number;
  offset: number;
  items: (Sentence & { at: number })[];
  more: boolean;
  next: { id: number; s: string; p: string; e: string; unlocks: number }[];
}

/**
 * The sentences already on this machine that the learner can read every word of.
 *
 * SHOWN ABOVE THE COMPOSER, and that ordering is the point of the whole page rather than a layout
 * preference. This costs nothing, needs no key, works offline, and is real language somebody meant
 * -- and every sentence has a recording, which composed text can never have. Writing new ones is
 * what you reach for when this runs dry, which for Chinese is much sooner than for Japanese.
 */
function readableSection(): HTMLElement {
  const box = el('section', { class: 'readable' });
  const count = el('p', { class: 'readable-count' }, t('Counting…'));
  const list = el('div', { class: 'readable-list' });
  const nextUp = el('div', { class: 'readable-next' });
  const more = el('button', { class: 'ghost more-btn', type: 'button' }, t('Show more'));
  more.hidden = true;
  box.append(el('h2', {}, t('Ready to read')), count, list, more, nextUp);

  let shown = 0;

  const row = (s: Sentence & { at: number }): HTMLElement =>
    el(
      'div',
      { class: 'readable-row' },
      el(
        'div',
        { class: 'readable-line' },
        // The recording, which is the thing a composed sentence cannot have.
        speakButton(s.s, { clip: `s/${s.tid}`, iconOnly: true }),
        sentenceEl(s, { hidePinyin: !prefs.showPinyin }),
      ),
      el('p', { class: 'readable-en' }, s.en),
    );

  async function load(offset: number): Promise<void> {
    const reply = await api<ReadableReply>(`/api/readable?offset=${offset}&limit=30`);
    if (reply.known === 0) {
      count.textContent = t(
        'Nothing marked known yet. Mark words as known and the sentences you can read appear here.',
      );
      more.hidden = true;
      return;
    }
    count.replaceChildren(
      el('strong', {}, String(reply.readable)),
      document.createTextNode(
        t` of the ${reply.corpus} sentences in this course use only the ${reply.known} words you know.`,
      ),
    );
    const rows = reply.items.map(row);
    if (offset === 0) list.replaceChildren(...rows);
    else list.append(...rows);
    shown = offset + reply.items.length;
    more.hidden = !reply.more;

    // WHAT ONE MORE WORD BUYS. A count on its own is a score; this is the same fact turned into
    // something to do, and it is most useful exactly when the count is discouraging.
    nextUp.replaceChildren();
    if (reply.next.length > 0) {
      nextUp.append(el('h3', {}, t('One word away')));
      const strip = el('div', { class: 'next-words' });
      for (const w of reply.next.slice(0, 6)) {
        strip.append(
          el(
            'div',
            { class: 'next-word' },
            el('span', { class: 'nw-han', lang: pair.learning.code }, w.s),
            prefs.showPinyin && readingFor(w) ? el('span', { class: 'nw-p' }, readingFor(w)) : null,
            el('span', { class: 'nw-e' }, w.e),
            el('span', { class: 'nw-count' }, t`+${w.unlocks}`),
          ),
        );
      }
      nextUp.append(
        strip,
        el(
          'p',
          { class: 'field-hint' },
          t('How many more sentences each of these would open up, if you learned it next.'),
        ),
      );
    }
  }

  more.addEventListener('click', () => {
    void load(shown);
  });
  void load(0);
  return box;
}

/**
 * Sentences written out of the words you have marked known.
 *
 * The promise is absolute and worth restating because it is what makes this different from the
 * corpus decks: you can read EVERY word here. That holds because the server checks it — each
 * sentence is spelled out of your known words before it is kept, and one unknown character throws
 * the whole sentence away.
 *
 * Which is also why nothing extra had to be built to show them. A sentence made only of known words
 * is made only of vocabulary words, so the pinyin, the tones and the recorded audio for every word
 * in it already exist; this view hands them to the same renderer the sentence cards use.
 */
function madeView(): HTMLElement {
  const view = el('div');
  const head = el('div', { class: 'page-head' });
  head.append(
    el('a', { class: 'back-link', href: '#/' }, t('← Home')),
    el('h1', {}, t('Made for you')),
    el(
      'p',
      {},
      t("Every word in here is one you have marked as known — the sentences this course already holds first, and then new ones written for you if a model is set up."),
    ),
  );
  view.append(head);

  const theme = el('input', {
    class: 'theme-input',
    type: 'text',
    placeholder: t('About anything in particular? (optional)'),
    maxlength: '120',
  }) as HTMLInputElement;
  const button = el('button', { class: 'primary write-btn', type: 'button' }, t('Write me some'));
  const status = el('p', { class: 'field-hint' }, '');
  const bar = el('div', { class: 'make-bar' }, theme, button);
  const list = el('div', { class: 'made-list' }, el('p', { class: 'empty' }, t('Loading…')));
  // The composer's own half of the page, so that "no model here" replaces THIS and not the
  // sentences above it — which need no model and are the reason the page is worth opening.
  const composer = el('section', { class: 'composer' });

  const asWords = (words: { t: string; id: number }[]): SentenceWord[] =>
    words.map((w) => {
      const v = vocab.words[w.id];
      return { t: w.t, p: v?.p ?? '', tones: v?.tones ?? [] };
    });

  async function paint(reply: MadeReply): Promise<void> {
    if (!reply.available) {
      composer.replaceChildren(
        el('h2', {}, t('Write me new ones')),
        el(
          'div',
          { class: 'known-empty' },
          el('p', {}, t('No model is set up on this machine.')),
          el(
            'p',
            { class: 'field-hint' },
            t("Everything above works without one. To have new sentences written as well, set DEEPSEEK_API_KEY before starting the app."),
          ),
        ),
      );
      return;
    }
    composer.replaceChildren(el('h2', {}, t('Write me new ones')), bar, status, list);
    button.toggleAttribute('disabled', reply.known < 8);
    if (reply.known < 8) {
      status.textContent = `Mark a few more words as known first — ${reply.known} is not enough to write with.`;
    }

    if (!reply.items.length) {
      list.replaceChildren(
        el(
          'div',
          { class: 'known-empty' },
          el('p', {}, t('Nothing written yet.')),
          el('p', { class: 'field-hint' }, t`Built from the ${reply.known} words you have marked known.`),
        ),
      );
      return;
    }

    const rows: HTMLElement[] = [];
    for (const item of reply.items) {
      const row = el('div', { class: 'made' });
      row.append(
        sentenceEl({ w: asWords(item.words), s: item.s }, { hidePinyin: !prefs.showPinyin }),
        el('p', { class: 'made-en' }, item.e),
        el(
          'button',
          {
            class: 'made-drop',
            type: 'button',
            'data-tip': t('Remove this one'),
            onclick: async () => {
              await api(`/api/made/${item.id}`, { method: 'DELETE' });
              await load();
            },
          },
          '×',
        ),
      );
      rows.push(row);
    }
    list.replaceChildren(...rows);
  }

  async function load(): Promise<void> {
    await paint(await api<MadeReply>('/api/made'));
  }

  button.addEventListener('click', async () => {
    button.toggleAttribute('disabled', true);
    button.textContent = t('Writing…');
    status.textContent = '';
    try {
      const reply = await api<MadeReply & { made: number; asked: number; rejected: { why: string }[] }>(
        '/api/made',
        { method: 'POST', body: JSON.stringify({ theme: theme.value }) },
      );
      // Said out loud, because a verifier quietly throwing away most of what it is given is the
      // failure mode of this whole feature and is otherwise invisible.
      status.textContent =
        `${reply.made} kept` +
        (reply.rejected.length ? ` · ${reply.rejected.length} thrown out for using words you have not marked` : '');
      await paint(reply);
    } catch (err) {
      status.textContent = err instanceof Error ? err.message : String(err);
    } finally {
      button.toggleAttribute('disabled', false);
      button.textContent = t('Write me some');
    }
  });

  view.append(readableSection(), composer);
  void load();
  return view;
}

// ---------------------------------------------------------------- study view

/** A card the session has shown, kept so it can be revisited. */
interface HistoryEntry {
  id: number;
  kind: ItemKind;
  state: CardState | null;
}

interface StudySession {
  deckId: string;
  kind: ItemKind;
  /** Every card shown this session, oldest first — what makes going back possible. */
  history: HistoryEntry[];
  /** Which of them is on screen. Normally the last, but Previous walks it back. */
  cursor: number;
  /** Whether the card is showing its back. */
  flipped: boolean;
  reviewed: number;
  done: boolean;
  /** Cards passed over this session. Skipping records no grade, so it must not touch the schedule. */
  skipped: Set<number>;
}

let session: StudySession | null = null;

/** The card currently on screen, or null when the deck is finished. */
function currentCard(): HistoryEntry | null {
  if (!session) return null;
  return session.history[session.cursor] ?? null;
}

type NextCard = { id: number | null; kind: ItemKind; state?: CardState };

/**
 * Ask for the next card WITHOUT putting it on screen.
 *
 * Split from showing it so the request can be in flight while the outgoing card is still sliding
 * away, without the new word appearing on the card that is leaving.
 */
async function fetchNextCard(deckId: string): Promise<NextCard> {
  const skipped = session?.skipped ?? new Set<number>();
  const query = new URLSearchParams({ deck: deckId });
  if (skipped.size) query.set('exclude', [...skipped].join(','));
  if (prefs.shuffle) query.set('shuffle', '1');
  if (prefs.almostReadable && deckById(deckId)?.kind === 'sentence') query.set('maxUnknown', '1');
  if (prefs.includeKnown) query.set('showKnown', '1');
  return api<NextCard>(`/api/next?${query}`);
}

/** Put a fetched card on screen. Synchronous, so it can be timed against an animation. */
function showNextCard(deckId: string, res: NextCard): void {
  if (!session || session.deckId !== deckId) return;
  session.kind = res.kind;
  session.flipped = false;
  session.done = res.id === null;
  if (res.id !== null) {
    // A CARD NEVER FOLLOWS ITSELF. A reload that lands on the card already on screen — toggling the
    // Known or Readable filter on and off re-fetches without excluding it, and the oldest due card
    // is usually what comes back — must refresh that entry, not stack a copy. Pushing a duplicate
    // filled the history with the same word, and Previous/Next then walked 他, 他, 他 (MYN-226).
    const tail = session.history[session.history.length - 1];
    if (tail && tail.id === res.id && tail.kind === res.kind && session.cursor === session.history.length - 1) {
      tail.state = res.state ?? tail.state;
    } else {
      session.history.push({ id: res.id, kind: res.kind, state: res.state ?? null });
      session.cursor = session.history.length - 1;
    }
  }
  render();
  // In listening mode the audio IS the prompt, so it always plays; elsewhere only if asked for.
  if (res.id !== null && (prefs.mode === 'audio' || prefs.autoplay)) {
    const { text, clip } = spokenOf(res.kind, res.id);
    speech.speak(text, clip);
  }
}

async function loadNextCard(deckId: string): Promise<void> {
  showNextCard(deckId, await fetchNextCard(deckId));
}

let cardWordSet: Set<string> | null = null;

/**
 * The words that have a card of their own.
 *
 * Not every recording in the audio folder is one of these — 1,481 of them are words that appear
 * only INSIDE sentences — and the phone build ships these and not those. Built once, and not
 * before the vocabulary has landed, so an empty set is never cached.
 */
function cardWords(): Set<string> {
  if (!cardWordSet && vocab) cardWordSet = new Set(vocab.words.map((w) => w.s));
  return cardWordSet ?? new Set<string>();
}

/** What a card says aloud, and which recording to play for it. */
function spokenOf(kind: ItemKind, id: number): { text: string; clip: string } {
  if (kind === 'sentence') {
    const s = sentences.sentences[id]!;
    return { text: s.s, clip: `s/${s.tid}` };
  }
  const w = vocab.words[id]!;
  return { text: w.s, clip: w.s };
}

/**
 * The mounted card and action row.
 *
 * Flipping has to MUTATE these rather than re-render. render() replaces the whole view, so the
 * card that comes back is a brand-new element already carrying .flipped — and a fresh node has no
 * previous computed state to transition from, so the browser jumps straight to the final
 * transform and the rotation never plays. Keeping the node and toggling a class on it is what
 * makes the flip an animation rather than a swap.
 */
let liveCard: HTMLElement | null = null;
let liveActions: HTMLElement | null = null;

function setFlipped(next: boolean): void {
  if (!session || !currentCard() || session.flipped === next) return;
  session.flipped = next;

  if (liveCard) {
    liveCard.classList.toggle('flipped', next);
    liveCard.setAttribute('aria-pressed', String(next));
    liveCard.setAttribute('aria-label', cardLabel(next));
  }
  // Only the actions change with the flip, so only they are rebuilt.
  if (liveActions) {
    const fresh = actionsFor(currentCard()!.state);
    liveActions.replaceWith(fresh);
    liveActions = fresh;
  }
}

function flipCard(): void {
  setFlipped(true);
}

const cardLabel = (flipped: boolean): string =>
  flipped ? 'Card back. Click to flip to the front.' : 'Card front. Click to reveal the answer.';

/**
 * Record that the learner turned this card over and moved on.
 *
 * THE ONE SIGNAL LEFT, and it is given by moving rather than by answering a question about
 * yourself. Four grades asked for a self-assessment on top of the recall, which is the part that
 * was confusing: you have just tried to remember a word, and before you can continue you must also
 * decide whether that attempt was "hard" or "good".
 *
 * A single neutral grade keeps the spacing working. It is coarser than four, and it is a signal
 * people actually give rather than one they guess at — a card you keep seeing and never mark
 * Learned drifts out slowly, and one you mark Learned is gone.
 */
async function markSeen(grade: Grade = 'good'): Promise<void> {
  const card = currentCard();
  if (!session || !card || !session.flipped) return;
  const { deckId } = session;
  session.reviewed++;
  await api('/api/review', {
    method: 'POST',
    body: JSON.stringify({ id: card.id, kind: card.kind, deckId, mode: prefs.mode, grade }),
  });
  await refreshProgress();
}

/**
 * Put a card you did not know back into the short queue.
 *
 * Forgetting is a judgement about the answer you just saw, so it only means anything once the card
 * is flipped — the back face, where this control lives, is the only place it is offered. It is an
 * ordinary move otherwise: the card leaves like any other, graded 'again' on the way, which the
 * scheduler turns into a ten-minute step and a lower ease. Advancing without it stays 'good', the
 * implicit "I had that one" the app has always meant by moving on (MYN-233).
 */
function forgetCard(): void {
  if (!session || !session.flipped) return;
  void goForward(0, 'again');
}

/**
 * The one control for forgetting, on the answer rail of both kinds of card.
 *
 * Quiet on purpose. The four grade buttons this app used to carry were taken away so the card
 * could be the interface; this is the one judgement the card could not otherwise express, said in
 * the smallest way that still reads as a button rather than a label.
 */
function forgotButton(): HTMLElement {
  return el(
    'button',
    {
      class: 'forgot',
      type: 'button',
      'data-tip': t('Put it back — I did not know it  ·  F'),
      onclick: (e: Event) => {
        e.stopPropagation();
        forgetCard();
      },
    },
    t('I forgot'),
  );
}

/** Whether the learner has asked the system not to animate things. Checked live, not cached. */
function stillMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Deal the card away and deal the next one in.
 *
 * THE MOTION IS THE ANSWER TO "WHICH WAY DID I JUST GO". Swapping the content in place says
 * nothing, and in a deck you move through in both directions that is a real loss — the arrows are
 * the only other clue and they are gone from under the cursor by the time the card changes.
 *
 * Animated on the STAGE rather than the card: the card itself carries the flip, and a translate
 * fighting a rotateY on the same element gives the card a wobble on its way out. The stage also
 * carries the shadow, so the whole thing leaves the table together.
 *
 * The out and the fetch overlap. Waiting for the network before starting the animation would put a
 * stutter exactly where the movement should be, and the card being dealt away is honest about what
 * is happening either way.
 */
/**
 * Where a card sits while a finger is dragging it.
 *
 * Shared with the throw below rather than written twice, because the throw has to BEGIN at exactly
 * these values. Two copies that drifted apart would show up as a flicker at the handover, which is
 * the one frame nobody would think to look at.
 */
const draggedTransform = (dx: number): string => `translateX(${dx}px) rotate(${dx / 26}deg)`;
const draggedOpacity = (dx: number): number => Math.max(0.45, 1 - Math.abs(dx) / 620);

/** How far out of the frame a card is thrown, as a fraction of its own width. */
const DEAL_OUT = 0.62;

async function dealTo(
  direction: 1 | -1,
  /** Work that must NOT touch the DOM — a fetch. Runs while the old card is leaving. */
  prepare: (() => Promise<void>) | null,
  /** The swap itself. Runs only once the old card is gone. */
  commit: () => void,
  /**
   * Where the card already is, in pixels, because a finger put it there.
   *
   * THE THROW CONTINUES THE DRAG. Resetting the transform first and animating from the centre made
   * one gesture look like two — the card followed the finger, snapped back to the middle, and then
   * flew off again as though a second thing had happened. Zero for the arrows and the keyboard,
   * where the card really is at rest.
   */
  from = 0,
): Promise<void> {
  const stage = document.querySelector<HTMLElement>('.card-stage');
  if (!stage || stillMotion()) {
    if (prepare) await prepare();
    commit();
    return;
  }
  // Both keyframes in pixels rather than one of each: interpolating px to % is a calc() the
  // browser has to resolve, and there is nothing to gain by making it.
  const width = stage.offsetWidth || 320;
  const reach = DEAL_OUT * width;
  // Always FURTHER OUT than the finger got. A hard swipe crosses more than 62% of the card's width
  // — the screen is wider than the card — and a fixed destination would then have the card travel
  // back towards the middle on its way out, which is the opposite of being thrown.
  const to = direction * Math.max(reach, Math.abs(from) + width * 0.18);
  // A card already half thrown has half as far left to travel, and giving it the full time is the
  // other half of why this looked wrong — it visibly slowed down at the moment it was let go.
  const left = Math.min(1, Math.abs(to - from) / reach);
  // The tilt continues as well. A flat 7° at the end meant a hard swipe — which has the card at
  // fourteen degrees under the finger — rotated BACK as it left, a wobble at exactly the moment
  // the card is supposed to be sailing away. 7° stays the floor, which is what the arrows give.
  const tilt = direction * (from === 0 ? 7 : Math.max(7, Math.abs(to) / 26));
  const out = stage.animate(
    [
      { transform: draggedTransform(from), opacity: String(draggedOpacity(from)) },
      { transform: `translateX(${to}px) rotate(${tilt}deg)`, opacity: 0 },
    ],
    {
      duration: Math.round(260 * Math.max(0.34, left)),
      // From rest a card has to start moving; from a throw it is already moving and should carry
      // its speed rather than gather it again.
      easing: from === 0 ? 'cubic-bezier(0.4, 0, 0.9, 0.5)' : 'cubic-bezier(0.15, 0.55, 0.4, 1)',
      fill: 'forwards',
    },
  );

  // THE RENDER GOES BETWEEN THE TWO HALVES, and getting this wrong was worse than no animation at
  // all: with the swap running alongside the outgoing animation, the old card slid away already
  // wearing the new word and the incoming card arrived with the same text — which says the card
  // did not change. Only the FETCH overlaps, because a request touches nothing on screen.
  await Promise.all([out.finished.catch(() => undefined), prepare?.()]);
  commit();

  const dealt = document.querySelector<HTMLElement>('.card-stage');
  if (!dealt) return;
  dealt.animate(
    [
      { transform: `translateX(${direction * -58}%) rotate(${direction * -6}deg)`, opacity: 0 },
      { transform: 'translateX(0) rotate(0deg)', opacity: 1 },
    ],
    // Slower coming in than going out, which is what makes it land rather than arrive.
    { duration: 400, easing: 'cubic-bezier(0.16, 0.9, 0.3, 1)' },
  );
}

/**
 * One card movement at a time. The arrows, a swipe and the arrow keys all land here, and a second
 * press before the first has finished its fetch-and-deal used to run a whole second advance on top
 * of it — most visibly grading the flipped card twice, since goForward fires the grade before it
 * awaits anything (MYN-227). The flag is released when the animation settles.
 */
let advancing = false;

function goBack(from = 0): void {
  if (!session || session.cursor <= 0 || advancing) return;
  advancing = true;
  void dealTo(
    1,
    null,
    () => {
      session!.cursor--;
      session!.flipped = false;
      // Stepping back off the end of a finished session shows the card again, not the panel saying
      // there are none left. The session is still finished; you are just not standing at the end.
      session!.done = false;
      render();
    },
    from,
  ).finally(() => {
    advancing = false;
  });
}

/**
 * Step forward. Within the history that is a replay; at the end it asks the server for a new card,
 * which is also what "skip" means — advancing without recording a grade.
 */
async function goForward(from = 0, grade: Grade = 'good'): Promise<void> {
  if (!session || advancing) return;
  advancing = true;
  try {
    await stepForward(from, grade);
  } finally {
    advancing = false;
  }
}

async function stepForward(from: number, grade: Grade = 'good'): Promise<void> {
  if (!session) return;
  // Turning a card over and moving on IS the review. Not awaited: the card should start leaving
  // the moment the arrow is pressed, and whether the write landed changes nothing on screen. The
  // grade is 'good' for an ordinary advance and 'again' when the learner said they forgot it.
  if (session.flipped) void markSeen(grade);
  if (session.cursor < session.history.length - 1) {
    await dealTo(
      -1,
      null,
      () => {
        session!.cursor++;
        session!.flipped = false;
        render();
      },
      from,
    );
    return;
  }
  const card = currentCard();
  if (card) session.skipped.add(card.id);
  const deckId = session.deckId;
  let next: NextCard | null = null;
  await dealTo(
    -1,
    async () => {
      next = await fetchNextCard(deckId);
    },
    () => {
      if (next) showNextCard(deckId, next);
    },
    from,
  );
}

function studyView(deckId: string): HTMLElement {
  const deck = deckById(deckId);
  if (!deck) return el('p', { class: 'empty' }, t('That deck does not exist.'));

  if (!session || session.deckId !== deckId) {
    session = {
      deckId,
      kind: deck.kind,
      history: [],
      cursor: -1,
      flipped: false,
      reviewed: 0,
      done: false,
      skipped: new Set(),
    };
    void loadNextCard(deckId);
    return el('div', { class: 'loading' }, t('Loading…'));
  }

  const view = el('div');

  const modeSeg = el('div', { class: 'seg', role: 'group', 'aria-label': t('Card mode') });
  for (const m of usableModes()) {
    modeSeg.append(
      el(
        'button',
        {
          type: 'button',
          'aria-pressed': String(prefs.mode === m.id),
          title: t(m.hint),
          onclick: () => {
            prefs.mode = m.id;
            savePrefs();
            if (session) session.flipped = false;
            render();
            const playing = currentCard();
            if (m.id === 'audio' && playing) {
              const { text, clip } = spokenOf(playing.kind, playing.id);
              speech.speak(text, clip);
            }
          },
        },
        modeName(m.id),
      ),
    );
  }

  // The breadcrumb stays on the page — it says WHERE you are. The controls go up into the title
  // bar, because they say what the next card will be, which is a property of the session rather
  // than of this moment in it. Between them they used to make two stacked bars above the card.
  view.append(
    el(
      'div',
      { class: 'study-bar' },
      (() => {
        const area = areaOf(deck);
        return el(
          'a',
          { class: 'back', href: area ? `#/decks/${area.id}` : '#/' },
          area ? t`← ${t(area.name)}` : t('← Home'),
        );
      })(),
      el('span', { class: 'deck-title' }, deck.name),
    ),
  );

  document.querySelector('#bar-tools')?.replaceChildren(
    el(
      'div',
      { class: 'study-tools' },
      deck.kind === 'sentence'
        ? el(
            'button',
            {
              type: 'button',
              class: `btn toggle${prefs.almostReadable ? ' on' : ''}`,
              'aria-pressed': String(prefs.almostReadable),
              title: prefs.almostReadable
                ? t('Only new sentences with at most one word you have not learned. Reviews still come as they fall due.')
                : t('Show only sentences you can almost read — at most one unlearned word.'),
              onclick: () => {
                prefs.almostReadable = !prefs.almostReadable;
                savePrefs();
                // Turning it ON fetches a fresh card. Unlike shuffle — where whatever you are
                // looking at is still a legitimate card — the card on screen may be one you
                // cannot read, which would contradict the toggle the moment you set it. The old
                // card stays reachable through Previous, since it is still in the history.
                if (prefs.almostReadable && session) void loadNextCard(session.deckId);
                else render();
                toast(
                  prefs.almostReadable
                    ? 'Only sentences you can almost read'
                    : 'All sentences in this deck',
                );
              },
            },
            prefs.almostReadable ? `◉ ${t('Readable')}` : `◎ ${t('Readable')}`,
          )
        : null,
      el(
        'button',
        {
          type: 'button',
          class: `btn toggle${prefs.includeKnown ? ' on' : ''}`,
          'aria-pressed': String(prefs.includeKnown),
          title: prefs.includeKnown
            ? t('Cards you have marked known are in this session. Use the toggle on a card to put one back.')
            : t('Bring back cards you have marked known, so you can un-mark one.'),
          onclick: () => {
            prefs.includeKnown = !prefs.includeKnown;
            savePrefs();
            // Turning it OFF is the direction that can contradict what you are looking at: the
            // card on screen may be one you have marked known, and leaving it up would say the
            // filter had not taken. Turning it ON leaves the card alone — it is still a card you
            // are entitled to see.
            if (!prefs.includeKnown && session) void loadNextCard(session.deckId);
            else render();
            toast(prefs.includeKnown ? 'Including cards you know' : 'Skipping cards you know');
          },
        },
        prefs.includeKnown ? `◉ ${t('Known')}` : `◎ ${t('Known')}`,
      ),
      el(
        'button',
        {
          type: 'button',
          class: `btn toggle${prefs.shuffle ? ' on' : ''}`,
          'aria-pressed': String(prefs.shuffle),
          title: prefs.shuffle
            ? t('New cards are drawn at random. Reviews that are due still come first.')
            : t('New cards come easiest-first. Turn on to draw them at random.'),
          onclick: () => {
            prefs.shuffle = !prefs.shuffle;
            savePrefs();
            // Re-render so the button shows its own state; the current card is left alone, since
            // shuffling should change what comes NEXT, not yank away what you are looking at.
            render();
            toast(prefs.shuffle ? 'Shuffling new cards' : 'New cards in order, easiest first');
          },
        },
        prefs.shuffle ? `🔀 ${t('Shuffled')}` : `🔀 ${t('Shuffle')}`,
      ),
    ),
  );

  const dp = progress.decks.find((d) => d.deckId === deckId);
  const done = dp ? dp.learned / Math.max(1, dp.total) : 0;
  view.append(el('div', { class: 'progress-line' }, el('i', { style: `width:${(done * 100).toFixed(1)}%` })));

  const card = currentCard();
  if (session.done || !card) {
    // THE ARROWS STAY. Telling somebody the deck is finished is right; taking away their way back
    // to the cards they just saw is not — it left leaving the deck as the only move. The panel
    // sits where the card was, with Previous still walking back through the session.
    view.append(cardRow(donePanel(deck), modeSeg));
    return view;
  }

  view.append(cardRow(cardStage(card.id, deck), modeSeg));
  liveActions = actionsFor(card.state);
  view.append(liveActions);
  return view;
}

/** A chevron, drawn rather than typed, so it scales with the button and lines up in every face. */
const ICON_CHEVRON = 'M15.2 4.6 8 11.8l7.2 7.2-1.6 1.6-8.8-8.8 8.8-8.8z';

/**
 * The card, with the way through it on either side.
 *
 * MOVED OUT FROM UNDER THE CARD. A row of two wide buttons below it took a band of the screen and
 * read as the most important thing on it, which they are not — the card is. Beside it, they are
 * where a hand already is and where the arrow keys already point.
 *
 * They are still SIZED AS REAL CONTROLS, not as quiet ghosts at the edges. That was the previous
 * design and it is what made a session feel stuck: if the way forward is faint, there is no way
 * forward. Circles, an outline, a hover, and a clear disabled state at the first card.
 */
/**
 * @param modes the card-mode switch, which rides in the footer line beside the position. Passed in
 *   rather than built here because studyView owns it — it is the same control either way, and two
 *   of them would be two answers to one question.
 */
function cardRow(stage: HTMLElement, modes: HTMLElement): HTMLElement {
  const atStart = !session || session.cursor <= 0;
  const arrow = (back: boolean) =>
    el(
      'button',
      {
        class: `card-arrow${back ? ' back' : ''}`,
        type: 'button',
        disabled: back ? atStart : false,
        'aria-label': back ? t('Previous card') : t('Next card, without grading'),
        title: back
          ? atStart
            ? t('This is the first card of the session')
            : t('Previous card  ·  ←')
          : t('Next card, without grading  ·  →'),
        // Wrapped, not passed by reference: these now take a pixel offset, and handing the
        // click handler straight over would feed a MouseEvent in as the card's position.
        onclick: back ? () => goBack() : () => void goForward(),
      },
      icon(ICON_CHEVRON, 22),
    );

  return el(
    'div',
    { class: 'card-row' },
    arrow(true),
    el(
      'div',
      { class: 'card-column' },
      stage,
      // THE FOOTER LINE: where you are, and what the card is showing you.
      //
      // Half of feeling stuck is not knowing your position, so the count stays. The mode switch
      // sits beside it because the alternative was worse both ways — in the title bar it was
      // HIDDEN below 760px, on the grounds that "the deck page offers the same three modes", which
      // the deck page does not, so on a phone the control existed nowhere at all and the last mode
      // chosen on a wide screen was permanent. Given a row of its own above the card it was
      // visible and cost the card a strip of height it needs more.
      //
      // Down here it is always reachable, at every width, and takes no space the card wanted.
      el(
        'div',
        { class: 'card-footer' },
        el(
          'span',
          { class: 'nav-pos' },
          t`${(session?.cursor ?? 0) + 1} of ${session?.history.length ?? 0}`,
        ),
        modes,
      ),
    ),
    arrow(false),
  );
}

const ICON_FLIP = 'M12 5V1L7 6l5 5V7a6 6 0 1 1-6 6H4a8 8 0 1 0 8-8z';

/** A small pitch contour for the tone pills on the back of the card. */
function miniContour(tone: number): SVGSVGElement {
  const paths: Record<number, string> = {
    1: 'M2 4 H24',
    2: 'M2 13 L24 3',
    3: 'M2 6 C7 14, 16 15, 24 3',
    4: 'M2 3 L24 14',
    5: 'M8 8 H18',
  };
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 26 17');
  svg.setAttribute('aria-hidden', 'true');
  const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  p.setAttribute('d', paths[tone] ?? paths[5]!);
  if (tone === 5) p.setAttribute('stroke-dasharray', '3 3');
  svg.append(p);
  return svg;
}

/**
 * The card: a single 3D element carrying both faces.
 *
 * The whole card is the click target and is a real <button>, so the flip is reachable by keyboard
 * and announced to assistive tech rather than being a mouse-only flourish.
 */
/**
 * Whether this device wants a swipe rather than a pair of arrows.
 *
 * A phone, in other words: a coarse pointer on a narrow screen. Checked live rather than cached
 * because a tablet can gain a trackpad and a window can be resized, and a stale answer here means
 * either no way forward or two.
 */
function wantsSwipe(): boolean {
  return window.matchMedia('(pointer: coarse) and (max-width: 760px)').matches;
}

/** Far enough across to mean it. Proportional to the card, floored so a narrow card is not twitchy. */
const SWIPE_COMMIT_PX = 74;

/** Under this, it was a tap for the flip, not a drag. */
const SWIPE_SLOP_PX = 8;

/**
 * Drag the card sideways to move through the session.
 *
 * The deal animation already says the cards are a stack you move along; on a phone the gesture for
 * that is the obvious one, and the two arrow buttons were a mouse affordance sitting on top of the
 * card's own width. So the card follows the finger and carries on in the direction it was thrown.
 *
 * THREE THINGS THIS MUST NOT BREAK, each of which is why the handler is shaped the way it is:
 *
 *  - the tap that flips. Nothing happens until the finger has travelled past the slop, and once it
 *    has, the click that follows is swallowed — otherwise every swipe would also turn the card.
 *  - scrolling the page. The gesture is only claimed once it is clearly more horizontal than
 *    vertical, so a thumb dragging down the page still drags the page.
 *  - the first card. There is nothing behind it, so a backwards drag pulls against a rubber band
 *    and springs back rather than pretending.
 */
function dragToDeal(stage: HTMLElement): void {
  let startX = 0;
  let startY = 0;
  let dx = 0;
  let dragging = false;
  let claimed = false;
  let pointer: number | null = null;

  // Divided down rather than matched: a card that rotates degree-for-degree with the finger reads
  // as broken rather than physical. The same two functions describe where the THROW starts.
  const paint = (): void => {
    stage.style.transform = draggedTransform(dx);
    stage.style.opacity = String(draggedOpacity(dx));
  };

  const clear = (): void => {
    stage.style.transform = '';
    stage.style.opacity = '';
    stage.style.transition = '';
  };

  stage.addEventListener('pointerdown', (e: PointerEvent) => {
    if (!wantsSwipe() || pointer !== null || !session || session.done) return;
    // A drag that starts on the speaker or on a tappable word belongs to that control.
    if ((e.target as HTMLElement).closest('button, a')) return;
    pointer = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;
    dx = 0;
    dragging = false;
    claimed = false;
    stage.style.transition = 'none';
  });

  stage.addEventListener('pointermove', (e: PointerEvent) => {
    if (pointer !== e.pointerId) return;
    const moveX = e.clientX - startX;
    const moveY = e.clientY - startY;
    if (!claimed) {
      if (Math.abs(moveX) < SWIPE_SLOP_PX && Math.abs(moveY) < SWIPE_SLOP_PX) return;
      // Whichever axis won the first few pixels owns the gesture for the rest of it. Deciding once
      // is what stops a card twitching sideways halfway down a scroll.
      if (Math.abs(moveY) >= Math.abs(moveX)) {
        pointer = null;
        clear();
        return;
      }
      claimed = true;
      dragging = true;
      stage.setPointerCapture(e.pointerId);
    }
    const atStart = session!.cursor <= 0;
    // Rubber band against the end of the history: it moves, so the gesture is not dead, but it
    // plainly does not want to go.
    dx = atStart && moveX > 0 ? moveX * 0.28 : moveX;
    paint();
  });

  const release = (e: PointerEvent): void => {
    if (pointer !== e.pointerId) return;
    pointer = null;
    if (!dragging) return;
    const went = dx;
    dragging = false;
    stage.releasePointerCapture?.(e.pointerId);

    // NOT cleared first. Clearing put the card back in the middle for the frame before the throw
    // began, so one gesture read as two: the drag, a snap back, then a fresh animation. The throw
    // takes over from exactly where the finger was, and the element is replaced when the next card
    // lands, which is what tidies the inline style away.
    if (went <= -SWIPE_COMMIT_PX) {
      void goForward(went);
      return;
    }
    if (went >= SWIPE_COMMIT_PX && session && session.cursor > 0) {
      goBack(went);
      return;
    }
    // Not far enough: put it back, with a spring rather than a cut.
    stage.style.transition = stillMotion()
      ? 'none'
      : 'transform 0.22s cubic-bezier(0.16, 0.9, 0.3, 1), opacity 0.22s ease';
    dx = 0;
    paint();
    window.setTimeout(clear, 240);
  };

  stage.addEventListener('pointerup', release);
  stage.addEventListener('pointercancel', release);

  // The click that follows a drag is not a tap, and letting it through would flip every card the
  // learner swipes past. Captured on the way down so the card's own handler never sees it.
  stage.addEventListener(
    'click',
    (e: Event) => {
      if (!claimed) return;
      claimed = false;
      e.stopPropagation();
      e.preventDefault();
    },
    true,
  );
}

function cardStage(itemId: number, deck: AnyDeck): HTMLElement {
  const flipped = session!.flipped;
  // Tint the card with its own leading tone — colour-coding it to what it teaches.
  const lead =
    deck.kind === 'sentence'
      ? spokenTonesOf(sentences.sentences[itemId]!.w[0]!.tones)[0] ?? 5
      : spokenTonesOf(vocab.words[itemId]!.tones)[0] ?? 5;

  // A div with a button role rather than a real <button>: the card contains its own controls —
  // the speaker, and now every word — and nesting interactive elements inside a button is invalid
  // HTML that browsers handle inconsistently. The role and key handling keep it operable.
  const inner = el('div', {
    class: `card-3d${flipped ? ' flipped' : ''}`,
    role: 'button',
    tabindex: '0',
    'aria-pressed': String(flipped),
    'aria-label': cardLabel(flipped),
    style: `--tone-accent: var(--tone-${lead})`,
    onclick: () => {
      if (session) setFlipped(!session.flipped);
    },
    onkeydown: (e: Event) => {
      const k = (e as KeyboardEvent).key;
      if (k === 'Enter' || k === ' ') {
        e.preventDefault();
        if (session) setFlipped(!session.flipped);
      }
    },
  });
  inner.append(
    deck.kind === 'sentence'
      ? sentenceFront(sentences.sentences[itemId]!, deck)
      : cardFront(vocab.words[itemId]!, deck),
    deck.kind === 'sentence'
      ? sentenceBack(sentences.sentences[itemId]!, itemId)
      : cardBack(vocab.words[itemId]!, itemId),
  );
  liveCard = inner;
  // Read this card's recording off the device now, while nothing is waiting, so the Listen button
  // finds it already there. Here rather than where a card is fetched, because stepping BACK through
  // the session builds a card too and it deserves its audio just as much.
  void speech.preload(spokenOf(deck.kind, itemId).clip);
  const stage = el('div', { class: 'card-stage', 'data-card': prefs.cardStyle }, inner);
  dragToDeal(stage);
  return stage;
}

/**
 * The level a word sits at, as this pair names its levels.
 *
 * Null when the pair has no such scheme, which is most of them — HSK is a Chinese institution and
 * a deck of Spanish has nothing to put in its place. A chip reading "undefined 3" is worse than no
 * chip, and an empty one is worse than both.
 */
function levelChip(word: Word): HTMLElement | null {
  if (!word.hsk || !pair.level) return null;
  const label =
    // A scheme that spells its own bands out wins: see Pair.level for why Japanese has to.
    pair.level.labels?.[String(word.hsk)] ??
    (word.hsk === 7 ? pair.level.top : `${pair.level.name} ${word.hsk}`);
  return el('span', { class: 'chip' }, label);
}

function metaChips(word: Word): HTMLElement {
  return el(
    'div',
    { class: 'card-meta' },
    el('span', { class: 'chip' }, word.pos),
    levelChip(word),
    el('span', { class: 'chip' }, t`#${word.r} most common`),
  );
}

/**
 * What the card's rail says to do.
 *
 * "Click" on a phone is the app describing a mouse that is not there. Resolved per render rather
 * than once, because the answer changes when a window is resized or a keyboard is attached.
 */
const tapWord = (): string => t(window.matchMedia('(pointer: coarse)').matches ? 'tap' : 'click');
const flipText = (): string => t`${tapWord()} to flip`;

/**
 * A sentence rendered word by word: characters above, tone-coloured pinyin below.
 *
 * The spacing is the teaching. Chinese is written without gaps, so a beginner cannot see where
 * one word ends and the next begins — showing the segmentation is half of what makes a sentence
 * learnable rather than a wall of characters.
 */
/** The word a sentence card is currently explaining, if any. Cleared whenever the card changes. */
let lookedUp: string | null = null;
/** Its dictionary form, as the corpus recorded it for this token. See setLookup. */
let lookedUpBase: string | null = null;
/**
 * The story line a word was tapped in, when it was tapped in one.
 *
 * A card shows one sentence across two faces and the panel belongs on both, so there the panel goes
 * into every host. A story shows eight lines at once, and painting every host there put the same
 * explanation under all eight.
 */
let lookupHost: HTMLElement | null = null;
let lookupTimer: number | undefined;

/** How long an explanation stays before it dismisses itself, once the pointer is off it. */
const LOOKUP_MS = 5_000;

/**
 * Show, replace or dismiss the word explanation.
 *
 * This swaps the panel IN PLACE rather than calling render(). A full render rebuilds the card
 * element, and this runs from a ten-second timer — a dismissal landing while the card is mid-flip
 * would replace the node and kill the rotation, which is precisely the bug MYN-8 fixed. It is also
 * simply wasteful to rebuild a card because a word was tapped.
 */
function setLookup(word: string | null, host: HTMLElement | null = null, base?: string): void {
  lookedUp = word;
  // THE DICTIONARY FORM THE SENTENCE'S OWN SEGMENTER FOUND, carried rather than worked out here.
  // It is a fact about this token in this sentence -- 入った is 入る in one line and the app has no
  // way to rediscover that from four characters -- so it travels with the tap (MYN-179).
  lookedUpBase = word ? (base ?? null) : null;
  lookupHost = word ? host : null;
  armLookupTimer();
  paintLookup();
}

/** (Re)start the countdown. Hovering the panel holds it — see wordPanel. */
function armLookupTimer(): void {
  window.clearTimeout(lookupTimer);
  if (lookedUp) lookupTimer = window.setTimeout(() => setLookup(null), LOOKUP_MS);
}

function paintLookup(): void {
  // Anchored to whichever container holds the sentence — the well on the prompt side, the detail
  // band on the answer side, where the English now leads.
  const hosts = '.face .well, .face .card-body, .story-line .line-zh';
  for (const host of Array.from(document.querySelectorAll<HTMLElement>(hosts))) {
    host.querySelector('.word-panel')?.remove();
    if (!lookedUp || !host.querySelector('.sent')) continue;
    // A tap inside a story answers for that line alone.
    if (lookupHost && host !== lookupHost) continue;
    const panel = wordPanel(lookedUp, lookedUpBase);
    if (!panel) continue;
    // Above the sentence's own speaker, so the reading order stays sentence, explanation, listen.
    host.insertBefore(panel, host.querySelector<HTMLElement>(':scope > .speak'));
  }
}

/** This app's state, handed to the shared rule. See src/shared/lookup.ts. */
const lookupWord = (text: string): WordInfo | null => sharedLookup(vocab, sentences, stories, text);

/**
 * The panel that appears under a sentence when a word is tapped.
 *
 * It leads with the MEANING, because that is what the tap was asking for — the characters are
 * already on the card above, and so is the pinyin under every word, so repeating either here just
 * pushes the answer into the middle of the row.
 */
function wordPanel(text: string, base?: string | null): HTMLElement | null {
  const info = lookupWord(text);
  if (!info) return null;
  // WHAT SHAPE THE WORD IS IN, when the shape has a name. The gloss above is the verb's, because
  // a dictionary holds 食べる and not 食べました -- so without this the panel answers a question
  // about a word the learner cannot see the connection to (src/shared/inflection.ts).
  const form = base ? formOf(text, base) : null;
  return el(
    'div',
    {
      class: 'word-panel',
      // A fixed countdown would fight anyone still reading it, so the pointer being over the panel
      // holds it open and the clock restarts on the way out.
      onmouseenter: () => window.clearTimeout(lookupTimer),
      onmouseleave: armLookupTimer,
    },
    el('span', { class: 'wp-gloss' }, info.e[0] ?? '—'),
    el('span', { class: 'wp-han', lang: pair.learning.code }, text),
    info.extra ? el('span', { class: 'chip' }, info.extra.pos) : null,
    form && base
      ? el(
          'span',
          { class: 'wp-form' },
          // ONE PHRASE, so a language that says it the other way round can -- ja reads {1}の{0}.
          // And its own phrase rather than the obvious "{0} of {1}", which the catalogues already
          // hold: that key is the card counter, "5 of 100", and writing this over it would have
          // renumbered every deck in Japanese as 676の1.
          t`${t(form)}, from ${base}`,
        )
      : null,
    speakButton(text, { iconOnly: true }),
    el(
      'button',
      {
        class: 'wp-close',
        type: 'button',
        'aria-label': t('Close'),
        title: t('Close'),
        onclick: (e: Event) => {
          e.stopPropagation();
          setLookup(null);
        },
      },
      '×',
    ),
  );
}

// Takes just the words rather than a Sentence: a story line carries the same shape, and rendering
// both through one function is what keeps a tapped word behaving identically wherever it appears.
function sentenceEl(
  sentence: { w: SentenceWord[]; s?: string },
  opts: { hidePinyin?: boolean; markNew?: boolean; newWords?: ReadonlySet<string> } = {},
): HTMLElement {
  const row = el('div', { class: 'sent' });
  // Marking which words are new shows WHY a sentence was offered and what it is actually teaching.
  const known = opts.markNew ? knownWords() : null;
  // WHICH WORDS HAVE A READING WORTH PRINTING, decided for the ROW and not word by word. The row
  // bottom-aligns precisely so the readings line up (see .sent), so a word that simply drops its
  // own sinks by the height of the line the others still stand on — the same thing the punctuation
  // block had to be pulled out of the flow to avoid. So where ANY word in the sentence has
  // something to add, every word keeps the line and the ones that read as themselves leave it
  // empty; where none does — an English course, whose words carry no reading at all — there is no
  // line at all and the sentence is one row of words.
  const readings = sentence.w.map((w) => (readingAdds(w.d ?? w.t, w.p) ? w.p : ''));
  const showReadings = readings.some(Boolean);
  for (const [i, w] of sentence.w.entries()) {
    // An explicit set wins: a made story already knows exactly which of its words are new, and
    // working it out again from the schedule would give a different and wronger answer.
    // KNOWN BY THE BASE FORM, not the surface. knownWords() holds the learned headwords, which are
    // the dictionary forms, and a Japanese sentence says 食べました where the word is 食べる — so an
    // inflected token is resolved to its base first and to its surface only if it has no base. The
    // same match readable.ts and unknownWordCounts make, and Chinese, which sets no base, is
    // unchanged (MYN-222).
    const isNew = opts.newWords ? opts.newWords.has(w.t) : known ? !known.has(w.b ?? w.t) : false;
    const tap = (e: Event) => {
      // Never flip the card: tapping a word is a different intent from turning the card over.
      e.stopPropagation();
      const target = e.currentTarget as HTMLElement;
      // The highlight is a flash of acknowledgement, not a state. It fades over a second, so it
      // cannot linger and compete with the underline that marks a word as new.
      for (const other of Array.from(document.querySelectorAll('.sw.tapped'))) {
        other.classList.remove('tapped');
      }
      void target.offsetWidth; // restart the animation if the same word is tapped again
      target.classList.add('tapped');
      const line = target.closest<HTMLElement>('.story-line .line-zh');
      setLookup(lookedUp === w.t && lookupHost === line ? null : w.t, line, w.b);
    };
    const block = el('span', {
      class: `sw${isNew ? ' new' : ''}`,
      role: 'button',
      tabindex: '0',
      title: t`${w.d ?? w.t} — tap for its meaning`,
      onclick: tap,
      onkeydown: (e: Event) => {
        const k = (e as KeyboardEvent).key;
        if (k !== 'Enter' && k !== ' ') return;
        e.preventDefault();
        tap(e);
      },
    });
    // The word as written, but every lookup above and below still keys on w.t — see SentenceWord.
    // The language comes from the course: this was hardcoded zh-Hans, so an English course
    // announced English text to a screen reader as Chinese.
    block.append(el('span', { class: 'h', lang: pair.learning.code }, w.d ?? w.t));
    if (showReadings) {
      const tones = spokenTonesOf(w.tones);
      const py = el('span', { class: `p${opts.hidePinyin ? ' hidden-pinyin' : ''}` });
      phonology.syllables(readings[i] ?? '').forEach((syl, n) => {
        py.append(el('span', { class: 'syl', 'data-tone': String(tones[n] ?? 5) }, syl));
      });
      block.append(py);
    }
    row.append(block);
  }

  // Segmentation drops punctuation, which cost nothing while the corpus held statements only. It
  // costs a lot now: 这是什么？ rendered as 这 是 什么 gives a question card no sign that it is a
  // question. The final mark is put back — but only the question and exclamation marks, which
  // carry meaning the words do not. A full stop after every statement would be clutter.
  //
  // BOTH WIDTHS. This listed the full-width pair alone, so it restored the mark for Chinese and
  // Japanese and dropped it for the 1,479 English questions that end in a plain "?" — every one
  // of them rendered as a statement on the card. Punctuation is the one thing here that must
  // cover every course at once, so it is named rather than written into the test (MYN-144).
  const final = sentence.s?.trim().slice(-1);
  if (final !== undefined && FINAL_MARKS.has(final)) {
    row.append(el('span', { class: 'sw punct', 'aria-hidden': 'true' }, el('span', { class: 'h' }, final)));
  }
  return row;
}

function sentenceFront(sentence: Sentence, deck: AnyDeck): HTMLElement {
  const face = el('div', { class: 'face front' });
  const well = el('div', { class: 'well' });

  if (prefs.mode === 'audio') {
    well.append(
      speakButton(sentence.s, { big: true, label: 'Play again', clip: `s/${sentence.tid}` }),
      el('p', { class: 'prompt-hint' }, t('Listen, then recall what it means.')),
    );
  } else if (prefs.mode === 'en-hanzi') {
    well.append(
      el('div', { class: 'gloss gloss-hero' }, sentence.en),
      el('p', { class: 'prompt-hint' }, t('How would you say this?')),
    );
  } else {
    well.append(sentenceEl(sentence, { hidePinyin: !prefs.showPinyin }));
    well.append(speakButton(sentence.s, { clip: `s/${sentence.tid}` }));
  }

  face.append(
    well,
    el(
      'div',
      { class: 'rail' },
      el('div', { class: 'card-meta' }, el('span', { class: 'chip' }, t`${sentence.n} words`)),
      el('span', { class: 'spacer' }),
      el('span', { class: 'prompt-hint' }, flipText()),
    ),
  );
  return face;
}

function sentenceBack(sentence: Sentence, itemId: number): HTMLElement {
  const face = el('div', { class: 'face back' });
  face.append(knowToggle('sentence', itemId));

  // The back is the ANSWER, and in the dominant mode the answer being checked is the meaning — so
  // the English leads, with the Chinese and its per-word pinyin below as the supporting detail.
  const well = el('div', { class: 'well' });
  well.append(el('div', { class: 'gloss gloss-hero' }, sentence.en));

  const body = el('div', { class: 'card-body sentence-detail' });
  // Only on the answer side: marking new words on the prompt would give the game away. The word
  // panel is inserted next to this sentence, so it travels with the Chinese rather than the well.
  body.append(sentenceEl(sentence, { markNew: true }));
  body.append(speakButton(sentence.s, { clip: `s/${sentence.tid}` }));

  face.append(
    well,
    body,
    el(
      'div',
      { class: 'rail' },
      el(
        'div',
        { class: 'card-meta' },
        el('span', { class: 'chip' }, t`${sentence.n} words`),
        el('span', { class: 'chip' }, t`Tatoeba #${sentence.tid}`),
      ),
      el('span', { class: 'spacer' }),
      forgotButton(),
      el('span', { class: 'prompt-hint' }, t`${tapWord()} to flip back`),
    ),
  );
  return face;
}

/** The prompt side of a WORD card. The well carries the question; the rail carries the rest. */
function cardFront(word: Word, deck: AnyDeck): HTMLElement {
  const face = el('div', { class: 'face front' });
  const well = el('div', { class: 'well' });

  if (prefs.mode === 'audio') {
    well.append(
      speakButton(word.s, { big: true, label: 'Play again' }),
      el('p', { class: 'prompt-hint' }, t('Listen, then recall what it means.')),
    );
  } else if (prefs.mode === 'en-hanzi') {
    well.append(
      el('div', { class: 'gloss gloss-hero' }, word.e[0] ?? ''),
      el('p', { class: 'prompt-hint' }, t`Which ${deck.pos ?? 'word'} is this?`),
    );
  } else {
    well.append(el('div', { class: 'hanzi', lang: pair.learning.code }, word.s));
    if (prefs.showTraditional && word.t) {
      well.append(el('div', { class: 'hanzi-trad', lang: MANDARIN_TRADITIONAL }, word.t));
    }
    if (pair.reading) well.append(pinyinEl(word, { hidden: !prefs.showPinyin }));
    // Under the pinyin, not off on the rail: the sound and the spelling of the sound belong
    // together, since pairing them is the thing the learner is trying to do.
    well.append(speakButton(word.s));
  }

  // The front withholds the answer, so it shows only what the mode has already given away.
  const rail = el(
    'div',
    { class: 'rail' },
    el(
      'div',
      { class: 'card-meta' },
      levelChip(word),
      el('span', { class: 'chip' }, `#${word.r}`),
    ),
    el('span', { class: 'spacer' }),
    el('span', { class: 'prompt-hint' }, flipText()),
  );

  face.append(well, rail);
  return face;
}

/** The answer side of a WORD card — the whole picture, however the question was asked. */
/**
 * What a character is built from.
 *
 * Shown on the ANSWER side only. The semantic half of a character is a strong clue to its meaning —
 * 女 beside 马 all but says "this is about a woman" — so putting it on the prompt would hand over
 * the very thing the card is asking. As an explanation after the fact it teaches the system; as a
 * prompt it would just make the card easy.
 */
/**
 * How a character is written: every stroke, in order, drawn.
 *
 * Knowing what a character means is not knowing how to write one, and stroke order is not
 * decoration — it is what makes handwriting legible, what dictionaries are indexed by, and what
 * somebody copying from a printed glyph gets wrong in a way that is hard to unlearn.
 *
 * The whole character is there faintly from the start and each stroke is then drawn over it, so
 * the panel answers two questions at once: what am I aiming at, and which part comes next. Tapping
 * replays it, because the order is the thing you came for and once is rarely enough.
 */
function strokePanel(text: string): HTMLElement | null {
  if (!prefs.showStrokes || !strokes) return null;
  const chars = [...text].filter((c) => strokes!.strokes[c]);
  if (!chars.length) return null;

  const panel = el('div', { class: 'strokes' }, el('div', { class: 'hints-head' }, t('How it is written')));
  const row = el('div', { class: 'stroke-row' });
  for (const ch of chars) row.append(strokeFigure(ch));
  panel.append(row);
  return panel;
}

/** How long one stroke takes to draw, and the pause before the next begins. */
const STROKE_MS = 420;
const BETWEEN_MS = 110;

function strokeFigure(ch: string): HTMLElement {
  const data = strokes!.strokes[ch]!;
  const box = strokes!.box;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${box} ${box}`);
  svg.setAttribute('class', 'stroke-svg');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', `${ch} — ${data.length} strokes`);

  const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  // The source is a font coordinate system: y increases upward and the glyph sits on a baseline.
  // The transform comes with the data rather than being assumed here.
  g.setAttribute('transform', strokes!.transform);
  svg.append(g);

  const d = (points: [number, number][]) =>
    `M ${points.map(([x, y]) => `${x} ${y}`).join(' L ')}`;
  const drawn: SVGPathElement[] = [];
  for (const points of data) {
    // The ghost: the finished character, faint, so there is something to aim at from the start.
    const ghost = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    ghost.setAttribute('d', d(points));
    ghost.setAttribute('class', 'stroke-ghost');
    g.append(ghost);
  }
  for (const points of data) {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', d(points));
    path.setAttribute('class', 'stroke-ink');
    g.append(path);
    drawn.push(path);
  }

  let playing: Animation[] = [];
  const play = (): void => {
    // Cancel the last run first. Each stroke's animation holds its end state with fill:forwards,
    // and that beats the inline dashoffset set below — so without this a replay begins with the
    // whole character already drawn and then redraws it on top of itself.
    for (const a of playing) a.cancel();
    playing = drawn.map((path, i) => {
      const length = path.getTotalLength();
      path.style.strokeDasharray = String(length);
      path.style.strokeDashoffset = String(length);
      // Somebody who has asked for less motion still needs the ORDER, so the strokes appear one
      // after another — they simply do not sweep.
      const quiet = stillMotion();
      return path.animate([{ strokeDashoffset: length }, { strokeDashoffset: 0 }], {
        duration: quiet ? 1 : STROKE_MS,
        delay: i * (quiet ? BETWEEN_MS : STROKE_MS + BETWEEN_MS),
        easing: quiet ? 'linear' : 'cubic-bezier(0.35, 0, 0.4, 1)',
        fill: 'forwards',
      });
    });
  };

  const figure = el(
    'button',
    {
      class: 'stroke-figure',
      type: 'button',
      'aria-label': t`Play the strokes of ${ch} again`,
      title: t`${data.length} strokes`,
      onclick: (e: Event) => {
        e.stopPropagation();
        play();
      },
    },
    svg,
  );
  // Drawn once when it appears, which is what the panel is for. requestAnimationFrame because
  // getTotalLength needs the path to be in the document.
  requestAnimationFrame(play);
  return figure;
}

function hintPanel(text: string, kana?: string): HTMLElement | null {
  if (!prefs.showHints) return null;
  const rows: HTMLElement[] = [];
  let built = false;
  let read = false;

  // WHICH READING THIS WORD IS SAYING, worked out once for the whole word rather than per
  // character: the answer for 生 in 学生 depends on what 学 took, which is the whole reason this
  // is an alignment and not a lookup (src/shared/readings.ts).
  const readingsOf = (ch: string): KanjiReadings | null => (hints.hints[ch] as Hint | undefined)?.readings ?? null;
  const spoken = kana && [...text].some((ch) => readingsOf(ch)) ? readingsUsedBy(text, kana, readingsOf) : null;
  // Told apart from "there was nothing to align": one is a word read some other way, the other is
  // a Mandarin card, a card with no kana, or hints that carry no readings at all.
  const alignable = Boolean(kana) && [...text].some((ch) => readingsOf(ch));
  let at = -1;

  for (const ch of text) {
    at++;
    const hint = hints.hints[ch] as Hint | undefined;
    if (!hint) continue;
    // THE CHARACTER'S OWN LANGUAGE, not Mandarin's. This said zh-Hans for every course, which was
    // invisible while only the Chinese course had hints — and after MYN-162 a zh-Hans tag picks a
    // Chinese face, so the first Japanese hints would have drawn their kanji in Chinese shapes
    // inside the panel that exists to explain those shapes.
    const lang = pair.learning.code;
    const row = el('div', { class: 'hint-row' }, el('span', { class: 'hint-char', lang }, ch));
    if (hint.kind === 'sound') {
      row.append(
        el(
          'span',
          { class: 'hint-parts' },
          el('b', { lang }, hint.semantic),
          hint.sense ? el('span', { class: 'hint-sense' }, hint.sense) : null,
          el('span', { class: 'hint-plus' }, '+'),
          el('b', { lang }, hint.phonetic),
          el('span', { class: 'hint-sense' }, t`${hint.phoneticReading}, the sound`),
        ),
      );
    } else if (hint.kind === 'parts') {
      // The same two halves, WITHOUT the claim about the sound. In Japanese the phonetic series
      // often outlives the reading — 読 is ドク where its 売 is バイ — so the structure is shown
      // and the pronunciation is left to the card, which knows it for certain.
      row.append(
        el(
          'span',
          { class: 'hint-parts' },
          el('b', { lang }, hint.semantic),
          hint.sense ? el('span', { class: 'hint-sense' }, hint.sense) : null,
          el('span', { class: 'hint-plus' }, '+'),
          el('b', { lang }, hint.other),
        ),
      );
    } else if (hint.kind !== 'readings') {
      row.append(
        el(
          'span',
          { class: 'hint-parts' },
          el('span', { class: 'hint-sense' }, hint.sense),
          el('span', { class: 'hint-kind' }, hint.kind === 'picture' ? t('a picture') : t('an idea')),
        ),
      );
    }
    if (hint.kind !== 'readings') built = true;
    if (hint.readings) {
      row.append(readingRow(hint.readings, lang, spoken?.[at]?.reading ?? null));
      read = true;
    }
    rows.push(row);
  }

  if (!rows.length) return null;
  // THE HEADING HAS TO MATCH THE ROWS. It said "How it is built" for every panel, which was true
  // while a hint was always a decomposition; a kanji whose panel is nothing but its readings is
  // not being told how it is built, and a heading that says otherwise is the app misreading its
  // own card back to the learner.
  const head = built && read ? t('How it is built and read') : built ? t('How it is built') : t('How it is read');
  const panel = el('div', { class: 'hints' }, el('div', { class: 'hints-head' }, head));
  for (const r of rows) panel.append(r);
  // A WORD WITH A READING OF ITS OWN, said out loud. 今日 is きょう, which is neither 今 nor
  // 日 -- and a panel that has just listed six readings, none of them in use, has told the learner
  // something untrue by implication unless it says so.
  //
  // WHAT IT DOES NOT CLAIM. Not "this word is not read from its kanji", which was the first
  // wording and is too strong for 日本: 本 really is ホン there, and only 日 is irregular. What
  // holds for every one of these words is the instruction rather than the diagnosis -- read the
  // word whole, do not try to lay its kana over its characters.
  if (read && alignable && !spoken) {
    panel.append(el('div', { class: 'hints-note' }, t('Read as a whole, not kanji by kanji.')));
  }
  return panel;
}

/**
 * The four shapes a verb or adjective takes, under the card.
 *
 * ONLY WHERE THE DICTIONARY SAYS. `c` is JMdict's own class and is absent for most words —
 * including a few verbs it does not class — and a word without one gets no panel rather than a
 * table built on a guess about whether it is 見て or 走って.
 *
 * THE WORD ITSELF IS NOT REPEATED in the rows. It is at the top of the card in letters twice this
 * size, and the four forms are all it: printing 食べる above 食べます would be showing the same
 * word five times to say four things about it.
 */
function changePanel(word: Word): HTMLElement | null {
  const forms = conjugate(word.s, word.c);
  if (!forms.length) return null;
  const lang = pair.learning.code;
  const panel = el('div', { class: 'hints changes' }, el('div', { class: 'hints-head' }, t('How it changes')));
  for (const { name, form } of forms) {
    panel.append(
      el(
        'div',
        { class: 'change-row' },
        el('span', { class: 'change-name' }, t(name)),
        el('span', { class: 'change-form', lang }, form),
      ),
    );
  }
  return panel;
}

/**
 * One kanji's on and kun readings, as a strip under the character.
 *
 * WHY BOTH ARE LABELLED, in the words a textbook uses. "on'yomi" and "kun'yomi" are terms the
 * learner will meet everywhere and will otherwise meet with no explanation attached; naming them
 * here, next to the readings themselves, is the cheapest place they can be learned. Written in
 * the alphabet and not as 音 and 訓, because this file renders every course and a kanji in it is
 * a kanji in the Mandarin one too -- the catalogues say 音読み and 訓読み where that is the
 * reading language (test/one-language.test.ts).
 *
 * THE OKURIGANA IS SHOWN AND DIMMED. KANJIDIC writes a kun reading as い.きる: い is what 生 is
 * read as, きる is the kana that follow it on the page. Dropping the tail would leave い, which is
 * not a word anyone can say; keeping the dot would show punctuation that means nothing to a
 * beginner. So the tail is rendered faintly and the dot goes -- the split is visible without
 * anything having to be explained.
 */
function readingRow(readings: KanjiReadings, lang: string, inUse: string | null): HTMLElement {
  const strip = el('span', { class: 'hint-readings' });
  const add = (label: string, list: string[], kind: string) => {
    if (!list.length) return;
    const line = el('span', { class: `hint-reading hint-${kind}` }, el('span', { class: 'hint-reading-label' }, label));
    for (const reading of list) {
      const [stem, okurigana] = reading.split('.');
      // Separated by the gap in the stylesheet rather than by a character. A comma is wrong in
      // Japanese, a 、 or a ・ is a Japanese character in a file the Mandarin course renders too,
      // and the space does the job in either.
      // THE ONE THIS WORD IS SAYING, marked rather than moved. Reordering per card would be the
      // other way to answer it, and it would make the same panel look different on every card
      // that shares a kanji — the list is worth recognising from one card to the next.
      const said = el('span', { class: `hint-said${reading === inUse ? ' is-said' : ''}`, lang }, stem);
      if (okurigana) said.append(el('span', { class: 'hint-okurigana' }, okurigana));
      line.append(said);
    }
    strip.append(line);
  };
  add(t("on'yomi"), readings.on, 'on');
  add(t("kun'yomi"), readings.kun, 'kun');
  return strip;
}

/**
 * "I already know this", as a mark on the card itself.
 *
 * ON THE BACK ONLY, which is not an aesthetic choice. The front is a recall test, and a control
 * that says "known" before you have tried to remember is an answer sitting next to the question.
 *
 * A TOGGLE, NOT A BUTTON THAT ADVANCES. It shows its own state and the same tap undoes it, so
 * changing your mind costs nothing and there is no moment where you have marked something and
 * cannot see that you did. Advancing on mark would save a keystroke and hide the only feedback
 * the control has.
 */
function knowToggle(kind: ItemKind, id: number): HTMLElement {
  const set = knownItems[kind === 'sentence' ? 'sentence' : 'word'];
  const btn = el('button', {
    class: `know-toggle${set.has(id) ? ' on' : ''}`,
    type: 'button',
    'aria-pressed': String(set.has(id)),
    'data-tip': set.has(id) ? t('Put this back in the rotation') : t('Never show this again  ·  K'),
  }) as HTMLButtonElement;
  // A CHECKBOX, so the word under the pointer is the thing being set. With the label swapping
  // between "Still learning" and "Learned" you had to read it to work out what a click would do —
  // the control described the present state and the action at the same time, in the same words.
  // Now the label is constant and only the box changes.
  btn.append(el('span', { class: 'box' }, icon(ICON_TICK, 12)), el('span', {}, t('Learned')));

  btn.addEventListener('click', (e) => {
    // The card flips on click; this is a control on the card, not a way of turning it over.
    e.stopPropagation();
    void toggleKnown(kind, id);
  });
  return btn;
}

/** Mark or unmark, and repaint the toggle in place. */
async function toggleKnown(kind: ItemKind, id: number): Promise<void> {
  const set = knownItems[kind === 'sentence' ? 'sentence' : 'word'];
  const next = !set.has(id);
  // Set locally first: the tick should fill the instant it is tapped, not a round trip later.
  if (next) set.add(id);
  else set.delete(id);
  repaintKnowToggle(kind, id);
  try {
    await api('/api/known', {
      method: 'POST',
      body: JSON.stringify({ kind, ids: [id], known: next }),
    });
    await refreshProgress();
  } catch (err) {
    // Put it back. A toggle that stays on after the write failed is a lie about what is stored.
    if (next) set.delete(id);
    else set.add(id);
    repaintKnowToggle(kind, id);
    toast(t`Could not save that: ${(err as Error).message}`);
  }
}

function repaintKnowToggle(kind: ItemKind, id: number): void {
  const on = knownItems[kind === 'sentence' ? 'sentence' : 'word'].has(id);
  for (const btn of document.querySelectorAll<HTMLButtonElement>('.know-toggle')) {
    btn.classList.toggle('on', on);
    btn.setAttribute('aria-pressed', String(on));
    btn.setAttribute('data-tip', on ? t('Put this back in the rotation') : t('Never show this again  ·  K'));
    // The label never changes; only the box does.
  }
}

function cardBack(word: Word, itemId: number): HTMLElement {
  const face = el('div', { class: 'face back' });
  face.append(knowToggle('word', itemId));

  const well = el('div', { class: 'well' });
  well.append(el('div', { class: 'hanzi', lang: pair.learning.code }, word.s));
  if (prefs.showTraditional && word.t) {
    well.append(el('div', { class: 'hanzi-trad', lang: MANDARIN_TRADITIONAL }, word.t));
  }
  well.append(
    el(
      'div',
      { class: 'reading' },
      // A pair with no readings shows the word and the speaker, and nothing between them — and so
      // does a word that reads as itself, see readingAdds.
      pair.reading && readingAdds(word.s, word.p) ? pinyinEl(word) : null,
      speakButton(word.s, { iconOnly: true }),
    ),
  );

  // A SMALL MARK IN THE CORNER RATHER THAN A BAND UNDER THE MEANING. Given its own row the picture
  // cost about a hundred vertical pixels, which on a phone was the difference between a card that
  // fits and one that spills. In the corner of the well it costs none: the well is already as tall
  // as the characters in it, and the space beside them was empty.
  // THE COURSE SAYS WHICH PICTURE THIS WORD HAS. Looking one up by the word's own spelling found
  // a picture only where the course spelled the concept the way Chinese does (MYN-173).
  const picture = prefs.showPictures ? wordArt(pair.pictures?.[word.s]) : null;
  if (picture) {
    const frame = el('div', { class: 'word-mark' });
    frame.append(picture);
    well.append(frame);
  }

  const spoken = spokenTonesOf(word.tones);
  // ONLY WHERE THERE ARE TONES TO SHOW. phonology.ts says a language without them gets no
  // colouring, no pills and no tone drill; this row never asked, so it read every Japanese word's
  // tones as the empty list, fell through to the neutral 5, and drew a numbered pill with a pitch
  // contour under a language that has no pitch to contour. The English courses got the same row
  // with nothing in it, which is invisible but still costs the well's 13px gap on every card.
  if (phonology.tones) {
    const toneRow = el('div', { class: 'tone-row' });
    phonology.syllables(word.p).forEach((syl, i) => {
      const tone = spoken[i] ?? 5;
      toneRow.append(
        el(
          'span',
          { class: 'tone-pill', style: `--pill-tone: var(--tone-${tone})` },
          el('span', { class: 'n' }, String(tone)),
          el('b', {}, syl),
          miniContour(tone),
        ),
      );
    });
    well.append(toneRow);
  }

  const body = el('div', { class: 'card-body' });
  // The gloss is sized to BE the answer, which is right for "person; people" and wrong for
  // "(marks a completed action or a change of state)" — at headline size that wraps to three lines
  // and stops looking like an answer at all. Stepped down by length, because the thing that makes
  // it unreadable is the wrapping, not the words.
  const meaning = word.e[0] ?? '';
  const weight = meaning.length > 46 ? ' tiny' : meaning.length > 26 ? ' long' : '';
  const gloss = el('div', { class: `gloss${weight}` }, meaning);
  if (word.e.length > 1) gloss.append(el('span', { class: 'extra' }, word.e.slice(1).join(' · ')));
  body.append(gloss);
  if (spoken.some((t, i) => t !== word.tones[i])) {
    body.append(
      el(
        'div',
        { class: 'sandhi-note' },
        t`Written ${word.p}, but said with the first third tone raised — two third tones cannot follow each other.`,
      ),
    );
  }

  // How the character is built. The picture goes in the well instead (below): the two answer
  // different questions — what it means, and why it looks like that — and a word can have neither.
  const built = hintPanel(word.s, word.p);
  if (built) body.append(built);

  // And how it CHANGES, for a word that changes. A dictionary lists 食べる and a sentence says
  // 食べました; the card has shown only the first of those, which is the half a learner cannot use
  // (MYN-180).
  const changes = changePanel(word);
  if (changes) body.append(changes);

  // And how it is WRITTEN, which is a third question again — knowing what a character means is
  // not knowing how to put one on paper, and a learner copying from a printed glyph gets the
  // order wrong in a way that is hard to unlearn.
  const written = strokePanel(word.s);
  if (written) body.append(written);

  face.append(
    well,
    body,
    el(
      'div',
      { class: 'rail' },
      metaChips(word),
      el('span', { class: 'spacer' }),
      forgotButton(),
      el('span', { class: 'prompt-hint' }, t`${tapWord()} to flip back`),
    ),
  );
  return face;
}

/**
 * What to do next, stated plainly.
 *
 * Previously the only route forward was to reveal and then grade, with nothing saying so — which
 * is what made advancing feel confusing. Now the heading says what the buttons will do, and Skip
 * is always offered for a card you simply do not want right now.
 */
/**
 * What is left to say under the card. Increasingly little, which is the point.
 *
 * There was a full-width "Show answer · Space" button here, to do the thing the card already does
 * when you tap it, captioned with the shortcut that also does it — three ways to turn a card over,
 * one of them taking a band of the screen. The card's own rail says so — "tap to flip" on a phone. That is the
 * control, and it is the one your thumb is already on.
 *
 * Before that there were four grade buttons. The direction is deliberate: the card is the
 * interface, and every row under it was competing with the word.
 */
function actionsFor(state: CardState | null): HTMLElement {
  void state;
  // BUILT ON BOTH FACES, shown on one.
  //
  // The card fills whatever the column gives it, so anything that appears underneath takes the
  // height out of the card — and this appeared on the back only. Measured: the row goes from 0 to
  // 62px and the card loses 61 of them, so turning a card over made it visibly shrink. A card is a
  // physical object; it cannot change size because you looked at the other side.
  //
  // Hidden rather than emptied, so the space reserved is exactly the space the words will need at
  // whatever width this is — a fixed height guessed here would be wrong on the first phone that
  // wrapped the sentence differently.
  const actions = el('div', {
    class: `answer-actions${session!.flipped ? '' : ' reserved'}`,
  });
  // Closed for good, and with it the space it was holding: the card grows into it, on both faces,
  // so the size stays one size. See MYN-90.
  if (prefs.knowHintClosed) return actions;

  actions.append(
    el(
      'div',
      { class: 'action-head' },
      el('b', {}, t('Know it already?')),
      el(
        'span',
        {},
        t('Tick Learned on the card and it stops coming up. Otherwise carry on — it will be back.'),
      ),
      el(
        'button',
        {
          class: 'hint-close',
          type: 'button',
          'aria-label': t('Close this note'),
          title: t('Close this note'),
          onclick: () => {
            prefs.knowHintClosed = true;
            savePrefs();
            render();
          },
        },
        '×',
      ),
    ),
  );
  return actions;
}

function donePanel(deck: AnyDeck): HTMLElement {
  const dp = progress.decks.find((d) => d.deckId === deck.id);
  const capped = progress.introducedToday >= progress.introLimit && (dp?.seen ?? 0) < (dp?.total ?? 0);
  const noun = deck.kind === 'sentence' ? 'sentences' : 'words';
  const onlySkipped = session!.skipped.size > 0;
  return el(
    'div',
    { class: 'done-panel' },
    el('h2', {}, capped ? t`That is today’s new ${noun}` : t('Nothing due in this deck')),
    el(
      'p',
      {},
      capped
        ? t`You have met ${progress.introLimit} new ${noun} today. Come back when the reviews fall due, or raise the daily limit in settings.`
        : onlySkipped
          ? t('Everything else is scheduled for later. The cards you skipped will come back next session.')
          : t('Everything you have seen is scheduled for later. Reviews reappear as they come due.'),
    ),
    onlySkipped
      ? el(
          'button',
          {
            class: 'btn',
            type: 'button',
            onclick: () => {
              if (!session) return;
              session.skipped.clear();
              session.done = false;
              void loadNextCard(session.deckId);
            },
          },
          t`Bring back ${session!.skipped.size} skipped`,
        )
      : null,
    el('a', { class: 'btn btn-primary', href: '#/', style: 'margin-left:8px' }, t('Back to decks')),
  );
}

// ---------------------------------------------------------------- pronunciation view

type PronounceTab = 'tones' | 'initials' | 'finals' | 'sandhi' | 'drill';
let pronounceTab: PronounceTab = 'tones';

/** A sketch of the pitch contour — the shape is the point, so it is drawn rather than described. */
function contourEl(tone: number): SVGSVGElement {
  const paths: Record<number, string> = {
    1: 'M6 8 L94 8',
    2: 'M6 28 L94 7',
    3: 'M6 13 C 22 30, 44 31, 58 26 S 84 12, 94 5',
    4: 'M6 6 L94 29',
    5: 'M38 18 L62 18',
  };
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'contour');
  svg.setAttribute('viewBox', '0 0 100 34');
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.setAttribute('aria-hidden', 'true');
  const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  p.setAttribute('d', paths[tone] ?? paths[5]!);
  p.setAttribute('stroke', `var(--tone-${tone})`);
  if (tone === 5) p.setAttribute('stroke-dasharray', '4 4');
  svg.append(p);
  return svg;
}

function tonesPanel(): HTMLElement {
  const wrap = el('div');
  wrap.append(
    el(
      'p',
      { class: 'field-hint' },
      t("Mandarin uses pitch to distinguish words. The same syllable \"ma\" is five different words depending on how the pitch moves. Tap any example to hear it."),
    ),
  );
  const grid = el('div', { class: 'tone-grid', style: 'margin-top:14px' });
  for (const tone of phonology.tones?.all ?? []) {
    grid.append(
      el(
        'div',
        { class: 'tone-card', 'data-tone': String(tone.n) },
        el('h3', {}, tone.name),
        contourEl(tone.n),
        el('p', {}, tone.contour),
        el('p', { class: 'cue' }, tone.cue),
        el(
          'button',
          {
            class: 'tone-ex',
            type: 'button',
            style: 'background:none;border:0;cursor:pointer;padding:8px 0 0;color:inherit',
            onclick: () => speech.speak(tone.example.hanzi),
          },
          el('span', { class: 'h', lang: MANDARIN_SIMPLIFIED }, tone.example.hanzi),
          el('span', { class: 'p', style: `color:var(--tone-${tone.n})` }, tone.example.pinyin),
          el('span', { class: 'm' }, tone.example.meaning),
          icon(ICON_SPEAK, 15),
        ),
      ),
    );
  }
  wrap.append(grid);
  return wrap;
}

function soundPanel(groups: SoundGroup[]): HTMLElement {
  const wrap = el('div', { class: 'sound-table' });
  for (const g of groups) {
    const rows = el('div', { class: 'rows' });
    for (const r of g.rows) {
      rows.append(
        el(
          'button',
          { class: 'row', type: 'button', onclick: () => speech.speak(r.hanzi) },
          el('span', { class: 'sym' }, r.sound),
          el(
            'span',
            { class: 'ex' },
            el('span', { class: 'h', lang: MANDARIN_SIMPLIFIED }, r.hanzi),
            el('span', { class: 'p' }, r.example),
          ),
          el(
            'span',
            { class: 'note' },
            r.note,
            r.warn ? el('span', { class: 'warn' }, r.warn) : null,
          ),
          el('span', { class: 'play' }, icon(ICON_SPEAK, 16)),
        ),
      );
    }
    wrap.append(
      el('div', { class: 'sound-group' }, el('h3', {}, g.title), el('p', {}, g.intro), rows),
    );
  }
  return wrap;
}

function sandhiPanel(): HTMLElement {
  const wrap = el('div');
  wrap.append(
    el(
      'p',
      { class: 'field-hint' },
      t("Tones change when syllables meet. These rules are not optional or regional — they are how the words are actually said, even though the writing keeps the original tone marks."),
    ),
  );
  for (const rule of phonology.tones?.rules ?? []) {
    const examples = el('div', { class: 'rule-ex' });
    for (const ex of rule.examples) {
      examples.append(
        el(
          'button',
          { type: 'button', onclick: () => speech.speak(ex.hanzi) },
          el('span', { class: 'h', lang: MANDARIN_SIMPLIFIED }, ex.hanzi),
          el('span', { class: 'was' }, ex.written),
          el('span', { class: 'arrow' }, '→'),
          el('span', { class: 'now' }, ex.spoken),
          el('span', { class: 'm' }, ex.meaning),
        ),
      );
    }
    wrap.append(
      el(
        'div',
        { class: 'rule', style: 'margin-top:14px' },
        el('h3', {}, rule.title),
        el('p', { class: 'what' }, rule.rule),
        rule.why ? el('p', { class: 'why' }, rule.why) : null,
        examples,
      ),
    );
  }
  return wrap;
}

// --- tone drill

interface ToneDrill {
  wordId: number;
  answered: number | null;
  right: number;
  asked: number;
}
let toneDrill: ToneDrill | null = null;

/** Single-syllable words from the common decks — one syllable means one unambiguous tone to name. */
function toneDrillPool(): number[] {
  const top = vocab.decks.find((d) => d.id === 'top-500') ?? vocab.decks[0]!;
  return top.words.filter((id: number) => {
    const w = vocab.words[id]!;
    return w.tones.length === 1 && w.tones[0] !== 5;
  });
}

function newToneDrill(keepScore = true): void {
  const pool = toneDrillPool();
  const wordId = pool[Math.floor(Math.random() * pool.length)]!;
  toneDrill = {
    wordId,
    answered: null,
    right: keepScore ? (toneDrill?.right ?? 0) : 0,
    asked: keepScore ? (toneDrill?.asked ?? 0) : 0,
  };
}

function toneDrillPanel(): HTMLElement {
  if (!toneDrill) newToneDrill(false);
  const d = toneDrill!;
  const word = vocab.words[d.wordId]!;
  const answer = word.tones[0]!;

  const wrap = el('div');
  wrap.append(
    el(
      'p',
      { class: 'field-hint' },
      t('Listen and name the tone. The pinyin is shown without its tone mark, so the answer is in the audio only.'),
    ),
  );

  const opts = el('div', { class: 'drill-opts' });
  for (const tone of [1, 2, 3, 4, 5]) {
    const btn = el(
      'button',
      {
        type: 'button',
        onclick: () => {
          if (d.answered !== null) return;
          d.answered = tone;
          d.asked++;
          if (tone === answer) d.right++;
          render();
        },
      },
      `${tone}`,
    );
    if (d.answered !== null) {
      if (tone === answer) btn.classList.add('right');
      else if (tone === d.answered) btn.classList.add('wrong');
    }
    opts.append(btn);
  }

  const feedback = el('div', { class: 'drill-feedback' });
  if (d.answered !== null) {
    // Only reachable in a language with tones — the drill is not offered otherwise — but the
    // type does not know that, and a name is better than a non-null assertion.
    const info = phonology.tones?.describe(answer)?.name.toLowerCase() ?? String(answer);
    feedback.append(
      d.answered === answer
        ? el('span', { style: 'color:var(--good);font-weight:600' }, t`Correct — ${word.p}, ${info}.`)
        : el('span', { style: 'color:var(--danger);font-weight:600' }, t`It was tone ${answer} — ${word.p}, ${word.e[0]}.`),
    );
  }

  wrap.append(
    el(
      'div',
      { class: 'drill', style: 'margin-top:16px' },
      el('div', { class: 'drill-score' }, d.asked ? t`${d.right} of ${d.asked} correct` : t('Tap play to begin')),
      el('div', { class: 'syl' }, d.answered === null ? (phonology.tones?.strip(word.p) ?? word.p) : word.p),
      el('div', { class: 'han', lang: pair.learning.code }, d.answered === null ? '—' : word.s),
      el('div', { style: 'margin-top:14px' }, speakButton(word.s, { big: true, label: 'Play' })),
      opts,
      feedback,
      el(
        'div',
        { style: 'margin-top:16px' },
        el(
          'button',
          {
            class: 'btn btn-primary',
            type: 'button',
            onclick: () => {
              newToneDrill();
              render();
              if (toneDrill) speech.speak(vocab.words[toneDrill.wordId]!.s);
            },
          },
          d.answered === null ? t('Skip') : t('Next word'),
        ),
      ),
    ),
  );
  return wrap;
}

function pronounceView(): HTMLElement {
  // The tile that leads here is not offered without a guide, but a bookmark or a typed hash still
  // arrives — and what it used to arrive at was a page about Mandarin in a build that teaches
  // something else. Saying so plainly is the whole of the fix.
  if (!phonology.sounds) {
    return el(
      'div',
      {},
      el('div', { class: 'page-head' }, el('a', { class: 'back-link', href: '#/' }, t('← Home'))),
      el('p', { class: 'empty' }, t`Mynah has no pronunciation guide for ${pair.learning.name} yet.`),
    );
  }
  const view = el('div');
  view.append(
    el(
      'div',
      { class: 'page-head' },
      el('h1', {}, t('Pronunciation')),
      el(
        'p',
        {},
        t("The sounds of Mandarin and the rules that change them in running speech. Every row speaks itself — tap to hear it."),
      ),
    ),
  );

  const tabs: { id: PronounceTab; name: string }[] = [
    { id: 'tones', name: 'Tones' },
    { id: 'initials', name: 'Initials' },
    { id: 'finals', name: 'Finals' },
    { id: 'sandhi', name: 'Tone changes' },
    { id: 'drill', name: 'Tone drill' },
  ];
  const seg = el('div', { class: 'seg', style: 'margin-bottom:18px;display:inline-flex;flex-wrap:wrap' });
  for (const tab of tabs) {
    seg.append(
      el(
        'button',
        {
          type: 'button',
          'aria-pressed': String(pronounceTab === tab.id),
          onclick: () => {
            pronounceTab = tab.id;
            render();
          },
        },
        tab.name,
      ),
    );
  }
  view.append(seg);

  if (!speech.canSpeakAnyWord()) {
    view.append(
      el(
        'p',
        { class: 'notice', style: 'margin-bottom:14px' },
        t`No ${pair.learning.everyday} voice is installed on this device, so playback is unavailable. On macOS, add one under System Settings → Accessibility → Spoken Content → System Voice → Manage Voices.`,
      ),
    );
  } else if (!speech.usingClips() && speech.onlyCompact()) {
    view.append(
      el(
        'p',
        { class: 'notice', style: 'margin-bottom:14px' },
        `Only Apple’s “compact” ${pair.learning.everyday} voices are installed — they are the robotic-sounding ones, and ` +
          'a poor model for pronunciation. System Settings → Accessibility → Spoken Content → System Voice ' +
          `→ Manage Voices → ${pair.learning.everyday}, and download a better one, then reload.`,
      ),
    );
  }

  view.append(
    pronounceTab === 'tones'
      ? tonesPanel()
      : pronounceTab === 'initials'
        ? soundPanel(phonology.sounds?.initials ?? [])
        : pronounceTab === 'finals'
          ? soundPanel(phonology.sounds?.finals ?? [])
          : pronounceTab === 'sandhi'
            ? sandhiPanel()
            : toneDrillPanel(),
  );
  return view;
}

// ---------------------------------------------------------------- progress view

function progressView(): HTMLElement {
  const view = el('div');
  view.append(
    el(
      'div',
      { class: 'page-head' },
      el('h1', {}, t('Progress')),
      el('p', {}, t('Everything here lives in a single file on this machine. Nothing is uploaded.')),
    ),
  );

  const states = Object.values(progress.states);
  const learned = states.filter((s) => isLearned(s)).length;
  const reviews = progress.history.reduce((n, h) => n + h.count, 0);
  // Match on today's date rather than taking the last row: with no reviews today, the last row
  // is some earlier day, and reporting its count as "today" would be wrong.
  const byDay = new Map(progress.history.map((h) => [h.day, h.count]));
  const today = byDay.get(dayKey(new Date())) ?? 0;

  view.append(
    el(
      'div',
      { class: 'stat-grid' },
      el('div', { class: 'stat' }, el('b', {}, String(states.length)), el('span', {}, t('words seen'))),
      el('div', { class: 'stat' }, el('b', {}, String(learned)), el('span', {}, t('words learned'))),
      el('div', { class: 'stat' }, el('b', {}, String(reviews)), el('span', {}, t('reviews in 30 days'))),
      el('div', { class: 'stat' }, el('b', {}, String(today)), el('span', {}, t('reviews today'))),
    ),
  );

  // 30-day activity strip.
  const heat = el('div', { class: 'heat' });
  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = dayKey(d);
    const n = byDay.get(key) ?? 0;
    const level = n === 0 ? 0 : n < 10 ? 1 : n < 25 ? 2 : n < 50 ? 3 : 4;
    heat.append(el('i', { 'data-l': String(level), title: t`${key}: ${n} reviews` }));
  }
  view.append(el('div', { class: 'section' }, el('h2', {}, t('Last 30 days')), heat));

  const table = el('table', { class: 'table' });
  table.append(
    el(
      'thead',
      {},
      el(
        'tr',
        {},
        el('th', {}, t('Deck')),
        el('th', { class: 'num' }, t('Learned')),
        el('th', { class: 'num' }, t('Seen')),
        el('th', { class: 'num' }, t('Due')),
        el('th', { class: 'num' }, t('Total')),
      ),
    ),
  );
  const tbody = el('tbody');
  for (const deck of vocab.decks) {
    const p = progress.decks.find((d) => d.deckId === deck.id);
    tbody.append(
      el(
        'tr',
        {},
        el('td', {}, deck.name),
        el('td', { class: 'num' }, String(p?.learned ?? 0)),
        el('td', { class: 'num' }, String(p?.seen ?? 0)),
        el('td', { class: 'num' }, String(p?.due ?? 0)),
        el('td', { class: 'num' }, String(deck.words.length)),
      ),
    );
  }
  table.append(tbody);
  view.append(el('div', { class: 'section' }, el('h2', {}, t('By deck')), table));

  // WHO THE DATA CAME FROM, OUT OF THE DATA. Every corpus declares its own source and licence,
  // and they differ by pair rather than by app: the stroke data for Chinese is derived from two
  // Arphic fonts under the Arphic Public License, and the Japanese equivalent will be KanjiVG
  // under CC-BY-SA. Hardcoding any of that here would make a corpus installed later carry the
  // wrong credit, which is a licence problem and not a cosmetic one.
  // Which sources to name is decided in one place (src/shared/credits.ts) so it can be tested and
  // so a feature the reader cannot see is never credited; the wording, which has to go through t(),
  // stays here.
  const lines = credits({ vocab, sentences, strokes, hints }).map((c) => {
    switch (c.role) {
      case 'vocabulary':
        return t`Vocabulary from ${c.name} (${c.license}), built ${c.builtAt ?? ''}.`;
      case 'sentences':
        return t`Sentences from ${c.name} (${c.license}).`;
      case 'stroke-order':
        return c.derivedFrom
          ? t`Stroke order from ${c.name}, derived from ${c.derivedFrom} (${c.license}).`
          : t`Stroke order from ${c.name} (${c.license}).`;
      case 'hints':
        // Hints always name what they were derived from (KanjiVG / KANJIDIC2 / Make Me a Hanzi),
        // so only the derived-from wording is ever emitted — see credits.ts.
        return t`Character hints from ${c.name}, derived from ${c.derivedFrom ?? ''} (${c.license}).`;
    }
  });
  view.append(
    el(
      'div',
      { class: 'field-hint', style: 'margin-top:26px' },
      ...lines.map((line) => el('p', { style: 'margin:2px 0' }, line)),
    ),
  );
  return view;
}

// ---------------------------------------------------------------- choosing a course

/**
 * "What do you want to learn?"
 *
 * ONE question, because the other one moved. Which language the app talks to you IN is now the
 * title bar's, where it is reachable from every screen and from an app you cannot read; asking it
 * again here would be asking the same question in two places, which is how two answers start to
 * disagree. So this page says what your language is, points at the bar, and gets on with the only
 * decision left.
 */
function languagesView(): HTMLElement {
  const view = el('div');

  // THE ROUTE IS GATED, NOT ONLY THE DOOR TO IT.
  //
  // MYN-109 hid the Settings row where a swap cannot work, and stopped there. Typing #/languages on
  // a hosted instance still rendered the whole picker, offered every course in the catalogue, and
  // installed one — 4.4 MB of Japanese corpus downloaded into storage the hosted build never reads,
  // because it gets its corpus from the server. Nothing broke; the app carried on serving the
  // server's pair. But a screen that takes an action which cannot have an effect is a screen that
  // lies, and this one took four megabytes of somebody's connection to do it.
  // A SERVER THAT ALREADY HOLDS EVERY COURSE (MYN-142). Checked before the install path because
  // the two are not variations of one screen: this one has nothing to download, so it has no size,
  // no progress bar and nothing to resume. Offering it the transfer UI would invent a cost that
  // does not exist and a wait that does not happen.
  if (!corpusState.installable && serverCourses.choosable) {
    view.append(hostedPicker());
    return view;
  }

  if (!corpusState.installable) {
    view.append(
      el(
        'div',
        { class: 'page-head' },
        el('a', { class: 'back-link', href: '#/' }, t('← Home')),
        el('h1', {}, t`${pair.learning.name} for ${pair.explainedIn.name} speakers`),
      ),
      el(
        'div',
        { class: 'known-empty' },
        el('p', {}, t('This copy teaches one course, and which one is set by whoever runs it.')),
        el(
          'p',
          { class: 'field-hint' },
          t('The app you install on a phone can hold any course in the catalogue and swap between them. This one cannot, so there is nothing to choose here.'),
        ),
        el('a', { class: 'more', href: '#/' }, t('Go and study →')),
      ),
    );
    return view;
  }

  view.append(
    el(
      'div',
      { class: 'page-head' },
      el('a', { class: 'back-link', href: '#/' }, t('← Home')),
      el('h1', {}, t('What do you want to learn?')),
      el(
        'p',
        {},
        t('One course at a time. Installing a new one downloads it and removes what the old one had on this device — your progress in each is kept separately, so going back finds it where you left it.'),
      ),
    ),
  );

  const body = el('div', { class: 'section' }, el('p', { class: 'empty' }, t('Loading…')));
  view.append(body);

  void (async () => {
    const catalogue = await corpusInstall.loadCatalogue();
    if (!catalogue || catalogue.pairs.length === 0) {
      body.replaceChildren(
        el('p', { class: 'field-hint' }, t('No list of courses could be reached. Try again when you are online.')),
      );
      return;
    }
    const here = await corpusInstall.installedId(catalogue);
    body.replaceChildren(picker(catalogue, here));
  })();
  return view;
}

/**
 * Choosing among courses the server already has.
 *
 * NO TRANSFER, SO NO TRANSFER UI. Every course is on the server, so this is a list and a button —
 * no megabytes, no progress bar, nothing to resume. The install picker below is the other case and
 * shares nothing with this but the question.
 *
 * Every course is offered, including ones explained in a language you do not read: a Japanese
 * course glossed in Chinese is a real course, and hiding it because the app currently speaks
 * English would be deciding for somebody what they can read. The language of the app is the bar's
 * question, and it is said here rather than answered.
 */
function hostedPicker(): HTMLElement {
  const view = el('div');
  const mine = serverCourses.chosen;
  const reading = uiLanguage(pair);

  /**
   * The reader's own word for a language.
   *
   * HARVESTED, because no single field holds it. A course names languages from ITS explainer's
   * point of view — en-ja calls English 英語 — so learning.name is the reader's word only by
   * coincidence. What is reliable is that a course explained in the language being READ names
   * both of its languages in that language, so those are the ones worth asking.
   *
   * Falls back to the endonym, which is always true and sometimes all there is: reading the app in
   * Chinese, no shipped course explains Japanese in Chinese, so Japanese stays 日本語.
   */
  const named = new Map<string, string>();
  for (const { pair: p } of serverCourses.courses) {
    if (p.explainedIn.code !== reading) continue;
    named.set(p.learning.code, p.learning.name);
    named.set(p.explainedIn.code, p.explainedIn.name);
  }
  const inMyWords = (l: Language): string => named.get(l.code) ?? l.endonym;

  view.append(
    el(
      'div',
      { class: 'page-head' },
      el('a', { class: 'back-link', href: '#/' }, t('← Home')),
      el('h1', {}, t('What do you want to learn?')),
      el(
        'p',
        {},
        // The promise this screen is allowed to make, and the one the install path cannot: nothing
        // is downloaded, nothing is cleared. Progress is kept per course on the server, so leaving
        // a course is not losing it — see item-key.test.ts.
        t('Everything here is already installed, so switching is immediate. Your progress in each course is kept separately — come back and you are where you left off.'),
      ),
    ),
  );

  // ONE QUESTION, AND THE OTHER ONE IS THE BAR'S.
  //
  // This listed every course the server holds, which is every PERMUTATION: English explained in
  // Japanese, English explained in Mandarin, Japanese explained in English, Mandarin explained in
  // English. Four rows for two independent choices, and n squared rows for n languages.
  //
  // The language the app explains itself in is already chosen, at the top of every screen. So the
  // only question left here is what to LEARN, and the rows are languages rather than pairs. The
  // install path has worked this way since it shipped — "I want to learn" beside a read-only "I
  // read" that points at the bar — and this is the same screen for a server that holds them all
  // (MYN-171).
  const offered = learnableIn(serverCourses.courses, reading);
  // The course in use, when it is NOT explained in the language being read — which happens only
  // when somebody has overridden the app's language from the bar. Shown anyway, and labelled with
  // what it actually explains itself in, because a learner must be able to see what they are
  // studying even when it is not on the list of what they could start.
  const odd = serverCourses.courses.find(
    (c) => c.id === mine && c.pair.explainedIn.code !== reading,
  );

  // THE APP'S LANGUAGE, NOT THE CURRENT COURSE'S. This line says what the list below is explained
  // in, and the list is everything explained in the language being READ. Showing the course's
  // instead read "Explained in English" above a list of courses explained in Japanese, whenever
  // somebody had overridden the bar — a heading contradicting the thing it heads. The one course
  // that differs carries its own label on its own row.
  const readingLanguage =
    serverCourses.courses.find((c) => c.pair.explainedIn.code === reading)?.pair.explainedIn ??
    (pair.explainedIn.code === reading ? pair.explainedIn : null);
  view.append(
    el(
      'div',
      { class: 'course-reading' },
      el('span', { class: 'field-label' }, t('Explained in')),
      el(
        'span',
        { class: 'course-mine', lang: reading },
        readingLanguage ? inMyWords(readingLanguage) : reading,
      ),
      el('span', { class: 'field-hint' }, t('Change this at the top of any screen.')),
    ),
  );

  if (offered.length === 0 && !odd) {
    // A language the app can speak but teaches nothing in. Said out loud and named both ways
    // round, the way the install path says it, because silence here reads as a bug.
    view.append(
      el(
        'div',
        { class: 'known-empty' },
        el('p', {}, t('Not built yet')),
        el(
          'p',
          { class: 'field-hint' },
          t`No course is explained in ${readingLanguage ? inMyWords(readingLanguage) : reading} yet. Change the language at the top of the screen to see what there is.`,
        ),
      ),
    );
    return view;
  }

  const list = el('div', { class: 'course-list' });
  let busy = false;

  for (const { id, pair: p } of [...offered, ...(odd ? [odd] : [])]) {
    const here = id === mine;
    const row = el('div', { class: `course-card${here ? ' on' : ''}` });
    const go = el(
      'button',
      { class: here ? 'btn' : 'btn btn-primary', type: 'button' },
      here ? t('Studying this') : t('Start this'),
    ) as HTMLButtonElement;
    go.disabled = here;

    go.addEventListener('click', () => {
      if (busy) return;
      busy = true;
      go.disabled = true;
      go.textContent = t('Switching…');
      void api('/api/courses', { method: 'PUT', body: JSON.stringify({ id }) })
        .then(() => {
          // A FULL RELOAD, not a re-render. Every corpus the app is holding — vocabulary,
          // sentences, stories, the tutor, the voice, the clip manifest — belongs to the course
          // just left. Re-rendering would paint the new course's name over the old one's cards.
          location.hash = '#/';
          location.reload();
        })
        .catch((err: unknown) => {
          busy = false;
          go.disabled = false;
          go.textContent = t('Start this');
          toast(t`Could not switch course: ${(err as Error).message}`);
        });
    });

    row.append(
      el(
        'div',
        { class: 'course-card-main' },
        // The course named in its OWN languages, never translated — the same rule the bar's
        // language list follows. Somebody switching to Japanese is looking for 日本語.
        // THE READER'S WORD LEADS, the endonym follows. MYN-142 shipped the endonym alone,
        // borrowing the title bar's rule — but that control never translates for a reason that
        // does not carry here: it exists so somebody who CANNOT read the app can escape, and this
        // list is read by somebody who can. An English reader scanning for Japanese should find
        // the word "Japanese", not decode 日本語 to discover they were already looking at it.
        //
        // The endonym still earns its place beside it. It is the script the course is taught in,
        // so seeing it is a fair part of choosing, and it is the one name that is never missing.
        el(
          'div',
          { class: 'course-card-name' },
          el('span', {}, inMyWords(p.learning)),
          inMyWords(p.learning) === p.learning.endonym
            ? null
            : el(
                'span',
                { class: 'course-card-also', lang: p.learning.code },
                `\u00a0·\u00a0${p.learning.endonym}`,
              ),
        ),
        // NOT REPEATED ON EVERY ROW. Every course on this list is explained in the language being
        // read — that is what puts it on the list — so saying so once above beats saying it four
        // times. The exception is the course in use when somebody has overridden the app's
        // language from the bar: that one IS explained in something else, and hiding it would
        // leave the learner unable to see what they are actually studying.
        p.explainedIn.code === reading
          ? null
          : el('div', { class: 'course-card-sub' }, t`explained in ${inMyWords(p.explainedIn)}`),
      ),
      go,
    );
    list.append(row);
  }

  view.append(list);
  return view;
}

/** The chooser, once there is something to choose from. */
function picker(catalogue: Catalogue, installedId: string | null): HTMLElement {
  const wrap = el('div', { class: 'course-pick' });

  // The pair this build shipped with is the one in use when nothing has been installed over it.
  const bundled = listingFor(catalogue, DEFAULT_PAIR)?.id ?? catalogue.pairs[0]!.id;
  const current = installedId ?? listingFor(catalogue, pair)?.id ?? bundled;
  const mine = uiLanguage(pair);

  const learnSel = el('select', { id: 'pick-learn' }) as HTMLSelectElement;

  /** A language as it should be named to somebody who speaks it: "中文 · Mandarin". */
  const label = (lang: Language): string =>
    lang.endonym === lang.name ? lang.name : `${lang.endonym} · ${lang.name}`;

  const learnable = new Map<string, Language>();
  for (const p of catalogue.pairs) {
    if (!learnable.has(p.learning.code)) learnable.set(p.learning.code, p.learning);
  }
  for (const lang of learnable.values()) {
    learnSel.append(el('option', { value: lang.code }, label(lang)));
  }
  const start = listingById(catalogue, current);
  learnSel.value = start ? start.learning.code : [...learnable.keys()][0]!;

  const myLanguage =
    catalogue.pairs.find((p) => p.explainedIn.code === mine)?.explainedIn ?? pair.explainedIn;

  wrap.append(
    el(
      'div',
      { class: 'course-row' },
      el('label', { class: 'field' }, el('span', { class: 'field-label' }, t('I want to learn')), learnSel),
      // Not a control. The bar owns this, and saying so is what stops somebody hunting for it here.
      el(
        'div',
        { class: 'field' },
        el('span', { class: 'field-label' }, t('I read')),
        el('p', { class: 'course-mine' }, myLanguage.endonym),
        el('span', { class: 'field-hint' }, t('Change this at the top of any screen.')),
      ),
    ),
  );

  const panel = el('div', { class: 'course-panel' });
  const bar = el('div', { class: 'fetch-bar' });
  const fill = el('div', { class: 'fetch-bar-fill' });
  bar.append(fill);
  const status = el('p', { class: 'field-hint' });
  const action = el('button', { class: 'btn btn-primary', type: 'button' }) as HTMLButtonElement;
  wrap.append(panel, bar, el('div', { class: 'course-actions' }, action, status));
  bar.hidden = true;

  let busy = false;

  /** The course for what you want to learn, explained in the language you read. */
  const chosen = (): PairListing | undefined =>
    catalogue.pairs.find((p) => p.learning.code === learnSel.value && p.explainedIn.code === mine);

  const paint = (): void => {
    const listing = chosen();
    if (!listing) {
      // A combination nobody has built. Said out loud, because silence here reads as a bug — and
      // named both ways round, so it is clear WHICH half is missing.
      const wanted = learnable.get(learnSel.value);
      panel.replaceChildren(
        el('h2', {}, t('Not built yet')),
        el(
          'p',
          { class: 'field-hint' },
          t`There is no ${wanted?.name ?? ''} course explained in ${myLanguage.endonym} yet.`,
        ),
      );
      action.hidden = true;
      status.textContent = '';
      return;
    }
    action.hidden = false;

    // What this course IS, before anybody commits to a download.
    const chips = el('div', { class: 'course-chips' });
    if (listing.reading) chips.append(el('span', { class: 'chip' }, listing.reading.name));
    if (listing.hasStrokes) chips.append(el('span', { class: 'chip' }, t('stroke order')));
    if (listing.level) chips.append(el('span', { class: 'chip' }, listing.level.name));
    if (listing.altScript) chips.append(el('span', { class: 'chip' }, listing.altScript.name));
    if (listing.audio) {
      chips.append(el('span', { class: 'chip' }, t`${listing.audio.clips.toLocaleString()} recordings`));
    }

    const cost = installCost(listing);
    panel.replaceChildren(
      el('h2', {}, t`${listing.learning.name} for ${listing.explainedIn.name} speakers`),
      chips,
      el(
        'p',
        { class: 'field-hint' },
        // Both numbers. One is wrong to somebody on a metered connection and the other is wrong to
        // somebody short of space — see installCost.
        t`${mb(cost.transfer)} to download, ${mb(cost.disk)} on this device.`,
      ),
      listing.audio
        ? el(
            'p',
            { class: 'field-hint' },
            t`The recordings — ${mb(listing.audio.bytes)} — are fetched afterwards, in deck order, and studying never waits for them.`,
          )
        : el('p', { class: 'field-hint' }, t('No recordings yet; it will speak in a system voice.')),
    );

    const isHere = listing.id === current;
    action.textContent = isHere ? t('Installed') : t`Install ${listing.learning.name}`;
    action.disabled = isHere || busy;
    status.textContent = isHere
      ? t('This is the course on this device.')
      : listing.id === bundled
        ? t('Comes with the app — nothing to download.')
        : '';
  };

  const swap = async (): Promise<void> => {
    const listing = chosen();
    if (!listing || busy) return;
    busy = true;
    action.disabled = true;
    bar.hidden = false;
    fill.style.width = '0%';
    try {
      if (listing.id === bundled) {
        // Already inside the app. Nothing to fetch; just stop overriding it.
        status.textContent = t('Switching…');
        await corpusInstall.useBundled();
      } else {
        await corpusInstall.install(catalogue, listing.id, (p) => {
          fill.style.width = `${p.total ? Math.round((p.done / p.total) * 100) : 0}%`;
          status.textContent = t`${mb(p.done)} of ${mb(p.total)}`;
        });
      }
      // THE RECORDINGS GO WITH THE COURSE THEY BELONG TO. They are the 189 MB that makes "one at a
      // time" mean anything, and they are clips of words that are no longer on any card — keeping
      // them would be keeping the whole weight of a course nobody is taking.
      status.textContent = t('Clearing the old recordings…');
      await clipCache.clear().catch(() => undefined);
      // The course now explains itself in the language being read, so a preference saying
      // otherwise has been satisfied and should stop overriding anything.
      if (prefs.readIn === listing.explainedIn.code) {
        prefs.readIn = null;
        savePrefs();
      }
      status.textContent = t('Done. Reopening…');
      // A reload rather than a re-render: the title, the document's language, both message
      // catalogues and every view are chosen from the corpus at boot (MYN-107). Rebuilding all of
      // that in place would be a second boot path to keep working.
      location.hash = '#/';
      location.reload();
    } catch (err) {
      bar.hidden = true;
      busy = false;
      action.disabled = false;
      status.textContent = err instanceof Error ? err.message : String(err);
    }
  };

  // The half-way state, named. Changing the bar translates everything the app says about itself
  // and nothing the CORPUS says — the backs of the cards are still written in whatever language
  // the installed course explains in. It is a perfectly usable state and it is not the one
  // somebody asked for, so it says so and offers the rest of the way.
  if (pair.explainedIn.code !== mine) {
    wrap.prepend(
      el(
        'div',
        { class: 'course-note' },
        el(
          'p',
          {},
          t`Your cards are still explained in ${pair.explainedIn.endonym}. Install a course below to change that too.`,
        ),
      ),
    );
  }

  learnSel.addEventListener('change', paint);
  action.addEventListener('click', () => void swap());
  paint();
  return wrap;
}

// ---------------------------------------------------------------- stories

/**
 * Read-along state, for the story currently open.
 *
 * `line` is which line is lit; `playing` is whether the reader is running on its own. They are
 * separate because a learner can tap a single line to hear it without starting the whole story,
 * and because pausing should leave the place lit rather than reset it.
 */
let reader: { storyId: string; line: number; playing: boolean } | null = null;

/**
 * Whether this copy can be handed a different course, and which one it is holding.
 *
 * Asked of the back end rather than worked out from the build. A phone keeps its corpus in its own
 * storage and can be given another; a hosted instance teaches what its operator generated it for,
 * and offering a choice that does nothing is worse than not offering one.
 */
let corpusState: { installable: boolean; installed: string | null } = {
  installable: false,
  installed: null,
};

/**
 * The courses a SERVER already holds, and which of them this account is in.
 *
 * The other half of the same question, and deliberately a second field rather than more keys on
 * corpusState — because the two answers lead to two different screens. A phone that can install a
 * course is being asked to spend four megabytes of somebody's connection; a server that holds
 * every course is being asked for a preference. Same words, "change my course", and nothing else
 * in common: one has a size, a progress bar and something to resume, the other is a list.
 *
 * `choosable: false` is what an older server, a phone, or any build serving one course all say,
 * and all three mean the same thing here — do not offer it.
 */
let serverCourses: {
  choosable: boolean;
  chosen: string | null;
  courses: { id: string; pair: Pair }[];
} = { choosable: false, chosen: null, courses: [] };

/**
 * Stories written for this learner, in the shape the reader already understands.
 *
 * Kept in their own list rather than spliced into `stories.stories`, because that array is the
 * shipped data file and a made story is not part of it — but resolved through the SAME lookup, so
 * the reader, the line highlighting, the tap-a-word panel and the keyboard all work on one without
 * knowing the difference.
 */
let madeStories: Story[] = [];

/** Which words in a made story the learner has not marked known — named, and underlined. */
const madeNewWords = new Map<string, Set<string>>();

/**
 * What this learner has marked known, held client-side so the toggle on a card can show its state
 * without asking the server every time a card is flipped.
 *
 * Kept in step by the toggle itself rather than refetched: the only thing that changes it is the
 * toggle, and a round trip before the tick fills in would make the control feel broken.
 */
const knownItems = { word: new Set<number>(), sentence: new Set<number>() };

async function loadKnown(): Promise<void> {
  for (const kind of ['word', 'sentence'] as const) {
    try {
      const reply = await api<{ items: { id: number }[] }>(`/api/known?kind=${kind}`);
      knownItems[kind] = new Set(reply.items.map((i) => i.id));
    } catch {
      // Not knowing is survivable: the toggle starts unset and setting it still works.
    }
  }
}

/** Level 0: made stories sort above the handwritten ones, which start at 1. */
const MADE_LEVEL = 0;

function storyById(id: string): Story | undefined {
  return stories.stories.find((s) => s.id === id) ?? madeStories.find((s) => s.id === id);
}

interface MadeStoriesReply {
  available: boolean;
  known: number;
  items: {
    id: number;
    title: string;
    titleEn: string;
    blurb: string;
    lines: { s: string; e: string; words: { t: string; id: number; fresh?: true }[] }[];
    madeAt: number;
  }[];
}

async function loadMadeStories(): Promise<MadeStoriesReply> {
  const reply = await api<MadeStoriesReply>('/api/made-stories');
  madeStories = reply.items.map((item) => {
    const id = `made-${item.id}`;
    madeNewWords.set(
      id,
      new Set(item.lines.flatMap((line) => line.words.filter((w) => w.fresh).map((w) => w.t))),
    );
    return {
    id,
    title: item.title,
    titleEn: item.titleEn,
    level: MADE_LEVEL,
    blurb: item.blurb,
    lines: item.lines.map((line) => ({
      s: line.s,
      en: line.e,
      w: line.words.map((w) => {
        const v = vocab.words[w.id];
        return { t: w.t, p: v?.p ?? '', tones: v?.tones ?? [] };
      }),
    })),
    };
  });
  return reply;
}

/** Which deck each level is held to, for the label on a story card. */
const LEVEL_NAMES: Record<number, string> = { 1: 'First words', 2: 'Getting going' };

/** The cover mark, tinted by the story's own hue so the grid reads as a shelf of different books. */
function storyBadge(story: Story): HTMLElement {
  const wrap = el('div', { class: 'story-badge' });
  wrap.style.color = `var(--${storyHue(story.id)})`;
  wrap.append(storyIcon(story.id));
  return wrap;
}

/**
 * Stories written for this learner, and the button that writes another.
 *
 * Above the handwritten shelf, because a story made from words you have actually marked is easier
 * than any of them by construction — and because it is the one on this page that did not exist
 * until you asked for it.
 */
function madeShelf(): HTMLElement {
  const wrap = el('div', { class: 'made-shelf' });
  const heading = el('h2', {}, t('Written for you'));
  const theme = el('input', {
    class: 'theme-input',
    type: 'text',
    placeholder: t('What should it be about? (optional)'),
    maxlength: '120',
  }) as HTMLInputElement;
  const button = el('button', { class: 'primary write-btn', type: 'button' }, t('Write me a story'));
  const status = el('p', { class: 'field-hint' }, '');
  const grid = el('div', { class: 'decks' });

  const paint = (reply: MadeStoriesReply) => {
    wrap.toggleAttribute('hidden', !reply.available);
    button.toggleAttribute('disabled', reply.known < 25);
    if (reply.known < 25 && !status.textContent) {
      status.textContent = `A story needs more to work with — mark ${25 - reply.known} more words as known.`;
    }
    grid.replaceChildren(
      ...madeStories.map((story) =>
        el(
          'a',
          {
            class: 'deck story-card',
            href: `#/story/${story.id}`,
            style: `color: var(--${storyHue(story.id)}); --tone-accent: var(--${storyHue(story.id)})`,
          },
          el(
            'div',
            { class: 'deck-well' },
            storyBadge(story),
            el('div', { class: 'deck-name' }, story.titleEn),
            el('div', { class: 'story-title-zh', lang: pair.learning.code }, story.title),
            el('div', { class: 'deck-blurb' }, story.blurb),
          ),
          el(
              'div',
              { class: 'deck-rail' },
              (() => {
                // Accurate, not flattering. It said "every word one you know", and then the story
                // budget was raised to five new ones — a caption that overstates the deal is worse
                // than no caption, because the whole feature is a promise about what is in there.
                const n = madeNewWords.get(story.id)?.size ?? 0;
                return `${story.lines.length} lines · ${n ? `${n} new words` : 'every word one you know'}`;
              })(),
          ),
        ),
      ),
    );
  };

  button.addEventListener('click', async () => {
    button.toggleAttribute('disabled', true);
    button.textContent = t('Writing…');
    status.textContent = t('This takes a few seconds — every line is checked against your words.');
    try {
      const reply = await api<MadeStoriesReply & { made: boolean; attempts: number }>(
        '/api/made-stories',
        { method: 'POST', body: JSON.stringify({ theme: theme.value }) },
      );
      // A story is all or nothing, so "it could not" is a real outcome and gets said plainly.
      status.textContent = reply.made
        ? `Done${reply.attempts > 1 ? ' — it took two goes to stay inside your words' : ''}.`
        : t('It could not write one that stays inside your words. Mark a few more and try again.');
      await loadMadeStories();
      paint(reply);
    } catch (err) {
      status.textContent = err instanceof Error ? err.message : String(err);
    } finally {
      button.toggleAttribute('disabled', false);
      button.textContent = t('Write me a story');
    }
  });

  wrap.append(heading, el('div', { class: 'make-bar' }, theme, button), status, grid);
  wrap.toggleAttribute('hidden', true);
  void loadMadeStories().then(paint);
  return wrap;
}

function storiesView(): HTMLElement {
  const view = el('div');
  view.append(
    el(
      'div',
      { class: 'page-head' },
      el('h1', {}, t('Stories')),
      el(
        'p',
        {},
        t("Short stories read aloud, one line at a time, with the line you are hearing lit up. Tap any word to see what it means. Start at the top — the stories get longer and harder as you go."),
      ),
    ),
  );

  view.append(madeShelf());

  const byLevel = [...new Set(stories.stories.map((s) => s.level))].sort((a, b) => a - b);
  for (const level of byLevel) {
    const grid = el('div', { class: 'decks' });
    for (const story of stories.stories.filter((s) => s.level === level)) {
      grid.append(
        // A story tile is a deck tile and is built the same way — well over rail, with the tone
        // taken from its own hue. They sit in the same grid; they should be the same object.
        el(
          'a',
          {
            class: 'deck story-card',
            href: `#/story/${story.id}`,
            style: `color: var(--${storyHue(story.id)}); --tone-accent: var(--${storyHue(story.id)})`,
          },
          el(
            'div',
            { class: 'deck-well' },
            storyBadge(story),
            el('div', { class: 'deck-name' }, story.titleEn),
            el('div', { class: 'story-title-zh', lang: pair.learning.code }, story.title),
            el('div', { class: 'deck-blurb' }, story.blurb),
          ),
          el('div', { class: 'deck-rail' }, t`${story.lines.length} lines`),
        ),
      );
    }
    trackPointer(grid);
    view.append(
      el('div', { class: 'section' }, el('h2', {}, LEVEL_NAMES[level] ?? t`Level ${level}`), grid),
    );
  }

  // A COURSE WITHOUT STORIES SAYS SO. This page was a heading, a paragraph describing stories, and
  // then nothing at all — which reads as broken rather than as empty, and that is exactly how it
  // looked when the English course shipped with none. The blurb above promises something; if there
  // is nothing to show, the promise has to be withdrawn in the same breath.
  if (stories.stories.length === 0 && madeStories.length === 0) {
    view.append(
      el(
        'div',
        { class: 'known-empty' },
        el('p', {}, t`There are no stories in the ${pair.learning.name} course yet.`),
        el(
          'p',
          { class: 'field-hint' },
          t('Everything else is here — the words, the sentences, the drills and the test. Stories are written by hand and this course has not had them written yet.'),
        ),
        el('a', { class: 'more', href: '#/' }, t('Go and study →')),
      ),
    );
  }
  return view;
}

/** Stop the reader and leave the highlight where it is. */
function pauseReader(): void {
  speech.stop();
  if (reader) reader.playing = false;
  paintReader();
}

/**
 * Play one line, and when it finishes either move on or stop.
 *
 * `advance` is what separates tapping a single line from running the story: a tap plays exactly
 * that line and stops, while the reader chains on to the next one.
 */
function playLine(index: number, advance: boolean): void {
  const story = reader && storyById(reader.storyId);
  const line = story?.lines[index];
  if (!story || !line || !reader) return;
  speech.stop();
  reader.line = index;
  reader.playing = advance;
  paintReader();
  const clip = `st/${story.id}-${index}`;
  speech.speak(line.s, clip, () => {
    // The view may have been left, or another line started, while this one was still speaking.
    if (!reader || reader.storyId !== story.id || reader.line !== index || !reader.playing) return;
    if (index + 1 < story.lines.length) playLine(index + 1, true);
    else pauseReader();
  });
}

/**
 * Move the highlight and the play/pause label without rebuilding the view.
 *
 * A full render would tear down and recreate every line on each advance, which loses the tapped
 * word's explanation panel mid-story and makes the highlight blink rather than move.
 */
function paintReader(): void {
  const lines = Array.from(document.querySelectorAll<HTMLElement>('.story-line'));
  lines.forEach((node, i) => node.classList.toggle('current', reader?.line === i));
  const btn = document.querySelector<HTMLButtonElement>('#reader-play');
  if (btn) {
    const playing = reader?.playing ?? false;
    btn.replaceChildren(icon(playing ? ICON_PAUSE : ICON_PLAY, 18), playing ? 'Pause' : 'Read aloud');
    btn.setAttribute('aria-label', playing ? 'Pause' : 'Read the story aloud');
  }
  if (reader?.playing) {
    lines[reader.line]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}

function storyView(id: string): HTMLElement {
  const story = storyById(id);
  if (!story) return el('p', { class: 'empty' }, t('No such story.'));
  // Opening a story starts at the top; coming back to the same one keeps the place.
  if (!reader || reader.storyId !== id) reader = { storyId: id, line: 0, playing: false };

  const view = el('div', { class: 'story' });
  view.append(
    el(
      'div',
      { class: 'page-head' },
      el('a', { class: 'back-link', href: '#/stories' }, t('← Stories')),
      el('h1', { lang: pair.learning.code }, story.title),
      el('p', {}, `${story.titleEn} — ${story.blurb}`),
    ),
  );

  const controls = el(
    'div',
    { class: 'reader-controls' },
    el(
      'button',
      {
        class: 'btn btn-primary',
        id: 'reader-play',
        type: 'button',
        onclick: () => {
          if (reader?.playing) pauseReader();
          else playLine(reader?.line ?? 0, true);
        },
      },
      icon(ICON_PLAY, 18),
      t('Read aloud'),
    ),
    el(
      'button',
      {
        class: 'btn',
        type: 'button',
        onclick: () => {
          pauseReader();
          if (reader) reader.line = 0;
          paintReader();
        },
      },
      t('Start over'),
    ),
    el(
      'label',
      { class: 'reader-toggle' },
      checkbox(prefs.storyPinyin, (on) => {
        prefs.storyPinyin = on;
        savePrefs();
        render();
      }),
      // What this pair calls its readings — pinyin, romaji, none at all. Not a phrase to translate.
      pair.reading?.Name ?? t('Reading'),
    ),
    el(
      'label',
      { class: 'reader-toggle' },
      checkbox(prefs.storyEnglish, (on) => {
        prefs.storyEnglish = on;
        savePrefs();
        render();
      }),
      t('English'),
    ),
  );
  view.append(controls);

  // Named before the story starts rather than discovered inside it. A made story is allowed a
  // handful of words the learner has not marked, and the deal is only fair if it says which.
  const fresh = madeNewWords.get(story.id);
  if (fresh?.size) {
    const strip = el('div', { class: 'new-words' }, el('b', {}, t('New here:')));
    for (const word of fresh) {
      const w = lookupWord(word);
      strip.append(
        el(
          'span',
          { class: 'new-word' },
          el('b', { lang: pair.learning.code }, word),
          el('i', {}, w?.p ?? ''),
          el('span', {}, w?.e[0] ?? ''),
        ),
      );
    }
    view.append(strip);
  }

  const body = el('div', { class: 'story-body' });
  story.lines.forEach((line, i) => {
    const zh = el('div', { class: 'line-zh' });
    zh.append(
      sentenceEl(line, { hidePinyin: !prefs.storyPinyin, newWords: madeNewWords.get(story.id) }),
    );
    const row = el(
      'div',
      { class: `story-line${reader && reader.line === i ? ' current' : ''}` },
      el(
        'button',
        {
          class: 'line-play',
          type: 'button',
          'aria-label': t`Listen to line ${i + 1}`,
          title: t('Listen to this line'),
          // One line only: someone tapping a single line is checking that line, not restarting
          // the story from it.
          onclick: () => playLine(i, false),
        },
        icon(ICON_SPEAK, 15),
      ),
      zh,
    );
    if (prefs.storyEnglish) row.append(el('div', { class: 'line-en' }, line.en));
    body.append(row);
  });
  view.append(body);
  return view;
}

// ---------------------------------------------------------------- drills

/**
 * A drill run: practice, deliberately outside the schedule.
 *
 * Drill answers are NOT fed to the spaced-repetition scheduler. Picking the right word from four is
 * a far easier task than recalling it from nothing, so treating a drill answer as a review would
 * push intervals out on the strength of a much weaker demonstration — and a quarter of the "right"
 * answers are guesses. Practice and measurement are different things, and the schedule should only
 * hear about the latter.
 */
interface DrillRun {
  kind: DrillKind;
  deckId: string;
  q: Question | null;
  /** The word id the learner picked, or null while the question is still open. */
  chosen: number | null;
  right: number;
  asked: number;
  streak: number;
  best: number;
  /** For the gap drill: the sentence the word was taken out of. */
  sentence: Sentence | null;
  /** Set when the pool cannot build a question, so the view can say why instead of going blank. */
  problem: string | null;
}
let drillRun: DrillRun | null = null;

/** headword -> vocabulary index, for finding the word a sentence is missing. */
let wordIndex: Map<string, number> | null = null;
/**
 * The vocabulary entry a word in a sentence is about.
 *
 * The word AS WRITTEN first, then its dictionary form. Chinese never needs the second try; English
 * needs it constantly, because a sentence says "going" and the course teaches "go". Without it the
 * gap drill silently skipped most English sentences — it looks for the missing word in the
 * vocabulary, found nothing, and moved on.
 */
function wordIdOf(word: SentenceWord | string): number | undefined {
  if (!wordIndex) wordIndex = new Map(vocab.words.map((w, i) => [w.s, i]));
  if (typeof word === 'string') return wordIndex.get(word);
  return wordIndex.get(word.t) ?? (word.b ? wordIndex.get(word.b) : undefined);
}

/** The word decks a drill can run over — sentence decks have no single word to ask about. */
function drillDecks(): AnyDeck[] {
  return allDecks().filter((d) => d.kind === 'word');
}

function nextDrillQuestion(): void {
  const run = drillRun;
  if (!run) return;
  run.chosen = null;
  run.sentence = null;
  run.problem = null;

  const deck = drillDecks().find((d) => d.id === run.deckId);
  const pool = deck ? vocab.decks.find((d) => d.id === deck.id)?.words ?? [] : [];
  if (pool.length < OPTIONS) {
    run.q = null;
    run.problem = 'This deck is too small to make a question from.';
    return;
  }

  if (run.kind === 'gap') {
    // Start from a SENTENCE rather than from a word: most words appear in no shipped sentence, so
    // picking the word first means repeatedly drawing words that cannot be asked about.
    for (let attempt = 0; attempt < 40; attempt++) {
      const s = sentences.sentences[Math.floor(Math.random() * sentences.sentences.length)];
      if (!s) continue;
      const inDeck = s.w
        .map((w) => wordIdOf(w))
        .filter((id): id is number => id !== undefined && pool.includes(id));
      if (!inDeck.length) continue;
      const answer = inDeck[Math.floor(Math.random() * inDeck.length)]!;
      const q = buildQuestion(vocab.words, pool, answer, 'gap', Math.random);
      if (!q) continue;
      run.q = q;
      run.sentence = s;
      return;
    }
    run.q = null;
    run.problem = 'No sentence in this deck had a word to take out. Try another deck.';
    return;
  }

  // DRAWN AGAIN RATHER THAN GIVEN UP ON. One word in the pool may be unaskable -- in the Reading
  // drill a word that reads as itself contains its own answer, which is 27.8% of the Japanese
  // vocabulary -- and one unlucky draw is not a reason to tell the learner the deck is broken.
  // Forty tries, the same number the gap drill uses for the same reason.
  for (let attempt = 0; attempt < 40; attempt++) {
    const answer = pool[Math.floor(Math.random() * pool.length)]!;
    const q = buildQuestion(vocab.words, pool, answer, run.kind, Math.random);
    if (q) {
      run.q = q;
      return;
    }
  }
  run.q = null;
  // NAMED FOR THE DECK IT IS, because for one deck this is not bad luck but the truth: every word
  // in Hiragana or Katakana is a letter that reads as itself, so the Reading drill has nothing it
  // can ask there and never will.
  run.problem =
    run.kind === 'pinyin'
      ? 'Every word in this deck is written the way it is read, so there is nothing to ask. Try another deck.'
      : 'Could not find four distinct options in this deck.';
}

function startDrill(kind: DrillKind, deckId: string, keepScore = false): void {
  drillRun = {
    kind,
    deckId,
    q: null,
    chosen: null,
    right: keepScore ? (drillRun?.right ?? 0) : 0,
    asked: keepScore ? (drillRun?.asked ?? 0) : 0,
    streak: keepScore ? (drillRun?.streak ?? 0) : 0,
    best: keepScore ? (drillRun?.best ?? 0) : 0,
    sentence: null,
    problem: null,
  };
  nextDrillQuestion();
}

function answerDrill(id: number): void {
  const run = drillRun;
  if (!run || !run.q || run.chosen !== null) return;
  run.chosen = id;
  run.asked++;
  if (id === run.q.answer) {
    run.right++;
    run.streak++;
    run.best = Math.max(run.best, run.streak);
  } else {
    run.streak = 0;
  }
  render();
  // Hearing the word right after answering is the whole reason to drill with audio on.
  if (run.kind === 'audio' || run.kind === 'meaning') speech.speak(vocab.words[run.q.answer]!.s);
}

/** What the question shows above the options. */
function drillPrompt(run: DrillRun, q: Question): HTMLElement {
  const word = vocab.words[q.answer]!;
  if (run.kind === 'gap' && run.sentence) {
    const row = el('div', { class: 'sent gap-sent' });
    for (const w of run.sentence.w) {
      const isGap = wordIdOf(w) === q.answer;
      const block = el('span', { class: `sw${isGap ? ' gap' : ''}` });
      block.append(
        // w.d ?? w.t, like every other view that draws a sentence. The drill built its row from
        // the matching key alone, so the English course asked "i have a ____" about a sentence
        // the card two screens back had shown as "I have a kitten" (MYN-139).
        el('span', { class: 'h', lang: pair.learning.code }, isGap ? '____' : (w.d ?? w.t)),
      );
      row.append(block);
    }
    return el('div', {}, row, el('div', { class: 'drill-en' }, run.sentence.en));
  }
  if (run.kind === 'hanzi') {
    return el('div', { class: 'drill-ask en' }, glossLabel(word));
  }
  if (run.kind === 'audio') {
    return el(
      'div',
      { class: 'drill-ask' },
      speakButton(word.s, { big: true, label: 'Play again' }),
    );
  }
  // meaning and pinyin both show the characters and ask about them.
  //
  // Only MEANING gets the reading and the speaker. In the Reading drill the pinyin IS the answer
  // and in Listening the sound is, so either would hand it over — a help on one card is a cheat on
  // the next, and which it is depends on what the card is asking.
  const prompt = el('div', { class: 'drill-ask han', lang: pair.learning.code }, word.s);
  if (run.kind !== 'meaning') return prompt;

  return el(
    'div',
    { class: 'drill-prompt' },
    prompt,
    el(
      'div',
      { class: 'prompt-aids' },
      // Revealed on hover or keyboard focus rather than shown outright: someone who wants to test
      // themselves on the characters alone should not have the answer's reading sitting under it.
      el('span', { class: 'prompt-pinyin', title: t('Reading') }, readingFor(word)),
      speakButton(word.s, { iconOnly: true }),
    ),
  );
}

/**
 * A gloss as an answer option reads.
 *
 * CC-CEDICT wraps its register notes in brackets — 个 is "(the general measure word)", 次 is
 * "(measure word for occasions); next" — which is right in a dictionary entry and wrong in a list
 * of four things to choose between, where the brackets read as punctuation noise rather than as
 * meaning. The words inside them are the definition, so they are kept and the brackets dropped.
 */
function glossLabel(word: Word): string {
  return (word.e[0] ?? '—').replace(/[()]/gu, '').trim();
}

/** How one option is labelled. */
function drillOptionLabel(kind: DrillKind, word: Word): HTMLElement {
  if (kind === 'meaning') return el('span', {}, glossLabel(word));
  if (kind === 'pinyin') return el('span', { class: 'opt-pinyin' }, word.p);
  // hanzi, audio and gap all answer with the characters.
  return el('span', { class: 'opt-han', lang: pair.learning.code }, word.s);
}

function drillsView(): HTMLElement {
  const decks = drillDecks();
  if (!drillRun) startDrill('meaning', decks[0]?.id ?? 'top-100');
  const run = drillRun!;

  const view = el('div');
  view.append(
    el(
      'div',
      { class: 'page-head' },
      el('h1', {}, t('Drills')),
      el(
        'p',
        {},
        t("Fast multiple-choice practice. Nothing here changes your review schedule — a drill is for reps, not for grading yourself, and picking from four is easier than recalling from none."),
      ),
    ),
  );

  const chips = el('div', { class: 'chips' });
  // A drill that asks how a word is SAID needs a written reading to ask it with. Most languages
  // have none, and then the chip is not there rather than being there and unanswerable.
  for (const d of DRILLS.filter((drill) => drill.id !== 'pinyin' || pair.reading !== null)) {
    chips.append(
      el(
        'button',
        {
          type: 'button',
          class: `chip${run.kind === d.id ? ' on' : ''}`,
          title: t(d.blurb),
          onclick: () => {
            startDrill(d.id, run.deckId, true);
            render();
          },
        },
        t(d.name),
      ),
    );
  }

  const deckSel = el('select', {
    class: 'select',
    'aria-label': t('Deck to drill'),
    onchange: (e: Event) => {
      startDrill(run.kind, (e.currentTarget as HTMLSelectElement).value, true);
      render();
    },
  }) as HTMLSelectElement;
  for (const d of decks) {
    deckSel.append(el('option', { value: d.id }, `${d.name} (${d.size})`));
  }
  deckSel.value = run.deckId;

  view.append(el('div', { class: 'drill-bar' }, chips, deckSel));
  view.append(
    el('p', { class: 'field-hint', style: 'margin:-6px 0 18px' }, t(DRILLS.find((d) => d.id === run.kind)?.blurb ?? '')),
  );

  const accuracy = run.asked ? Math.round((run.right / run.asked) * 100) : 0;
  view.append(
    el(
      'div',
      { class: 'drill-score-row' },
      el('div', { class: 'stat' }, el('b', {}, `${run.right}/${run.asked}`), t('correct')),
      el('div', { class: 'stat' }, el('b', {}, run.asked ? `${accuracy}%` : '—'), t('accuracy')),
      el('div', { class: 'stat' }, el('b', {}, String(run.streak)), t('streak')),
      el('div', { class: 'stat' }, el('b', {}, String(run.best)), t('best')),
      el(
        'button',
        {
          class: 'btn',
          type: 'button',
          onclick: () => {
            startDrill(run.kind, run.deckId, false);
            render();
          },
        },
        t('Reset score'),
      ),
    ),
  );

  if (!run.q) {
    view.append(el('p', { class: 'empty' }, run.problem ?? t('No question available.')));
    return view;
  }
  const q = run.q;

  const card = el('div', { class: 'quiz-card' });
  card.append(drillPrompt(run, q));

  const opts = el('div', { class: 'quiz-opts' });
  q.options.forEach((id, i) => {
    const btn = el(
      'button',
      {
        type: 'button',
        class: 'quiz-opt',
        onclick: () => answerDrill(id),
      },
      el('span', { class: 'opt-key' }, String(i + 1)),
      drillOptionLabel(run.kind, vocab.words[id]!),
    );
    if (run.chosen !== null) {
      btn.setAttribute('disabled', 'true');
      if (id === q.answer) btn.classList.add('right');
      else if (id === run.chosen) btn.classList.add('wrong');
    }
    opts.append(btn);
  });
  trackPointer(opts);
  card.append(opts);

  if (run.chosen !== null) {
    const word = vocab.words[q.answer]!;
    const got = run.chosen === q.answer;
    card.append(
      el(
        'div',
        { class: `quiz-feedback ${got ? 'good' : 'bad'}` },
        el('span', { class: 'verdict' }, got ? t('Correct') : t('Not quite')),
        el('span', { class: 'answer-han', lang: pair.learning.code }, word.s),
        el('span', { class: 'answer-p' }, readingFor(word)),
        el('span', { class: 'answer-en' }, word.e.slice(0, 2).join('; ')),
        speakButton(word.s, { iconOnly: true }),
      ),
      el(
        'button',
        {
          class: 'btn btn-primary quiz-next',
          type: 'button',
          onclick: () => {
            nextDrillQuestion();
            render();
            if (drillRun?.kind === 'audio' && drillRun.q) speech.speak(vocab.words[drillRun.q.answer]!.s);
          },
        },
        t('Next'),
      ),
    );
  }
  view.append(card);
  view.append(
    el('p', { class: 'field-hint', style: 'margin-top:14px' }, t('Keys 1–4 answer, Enter moves on.')),
  );
  return view;
}

// --- translating

/** Built once, lazily: 9,448 short English strings, and only when the translator is first used. */
let translateIndex: TranslateIndex | null = null;
function translationsFor(query: string): { phrases: TranslateMatch[]; words: WordMatch[] } {
  if (!translateIndex) translateIndex = buildIndex(sentences.sentences);
  return {
    phrases: translateSearch(translateIndex, sentences.sentences, query, 4),
    words: lookupWords(vocab.words, query, 6),
  };
}

/** One translated sentence: the Chinese, and — always — the English it actually means. */
function phraseAnswer(match: TranslateMatch): HTMLElement {
  const s = sentences.sentences[match.at]!;
  return el(
    'div',
    { class: 'answer translation' },
    el(
      'div',
      { class: 'translation-head' },
      match.exact ? el('span', { class: 'chip' }, t('exact')) : null,
      speakButton(s.s, { clip: `s/${s.tid}`, iconOnly: true }),
    ),
    sentenceEl(s, {}),
    // The English of the match is shown, always and prominently. It is the only way someone who
    // cannot read the Chinese can tell that the sentence found is not the one they meant — and with
    // a corpus search, that is a real possibility rather than a remote one.
    el('div', { class: 'translation-en' }, s.en),
  );
}

function wordAnswer(match: WordMatch): HTMLElement {
  const w = vocab.words[match.at]!;
  return el(
    'a',
    { class: 'translation-word', href: `#/study/${suggestedDeck(0)}`, onclick: (e: Event) => e.preventDefault() },
    el('span', { class: 'tw-han', lang: pair.learning.code }, w.s),
    el('span', { class: 'tw-p' }, readingFor(w)),
    el('span', { class: 'tw-en' }, match.sense),
    speakButton(w.s, { iconOnly: true }),
  );
}

function translateResults(query: string): HTMLElement {
  const view = el('div');
  const { phrases, words } = translationsFor(query);

  if (!phrases.length && !words.length) {
    view.append(
      el(
        'div',
        { class: 'answer none' },
        el('h3', {}, t('I cannot say that one')),
        el(
          'p',
          {},
          `Nothing here translates “${query}”. Mynah looks it up among ${sentences.sentences.length.toLocaleString()} ` +
            `sentences people have translated and the words it teaches — it does not make up ${pair.learning.everyday}, so when it has ` +
            'no real answer it says so rather than offering something close that means something else.',
        ),
        el('p', { class: 'field-hint' }, t('Shorter and plainer usually finds more: “I am hungry” rather than “I could really eat”.')),
      ),
    );
    return view;
  }

  if (phrases.length) {
    view.append(el('div', { class: 'answer-examples-head' }, t('Say it like this')));
    for (const p of phrases) view.append(phraseAnswer(p));
  }
  if (words.length) {
    view.append(el('div', { class: 'answer-examples-head' }, phrases.length ? t('Or the word on its own') : t('The word')));
    const list = el('div', { class: 'translation-words' });
    for (const w of words) list.append(wordAnswer(w));
    view.append(list);
  }
  return view;
}

// ---------------------------------------------------------------- placement test

/**
 * A test in progress.
 *
 * The test asks ONE kind of question — characters to meaning — for every band, on purpose. Mixing
 * question types would measure a blend of skills that changes with whatever the shuffle produced,
 * and two results taken a month apart could then differ because the test differed. A vocabulary
 * estimate is only worth storing if it means the same thing every time.
 */
interface TestRun {
  band: number;
  askedInBand: number;
  results: BandResult[];
  q: Question | null;
  chosen: number | null;
  /** How many bands in a row have scored nothing — the test gives up after GIVE_UP_AFTER. */
  blanks: number;
  done: boolean;
  saved: TestResult | null;
}
let testRun: TestRun | null = null;
let testHistoryCache: TestResult[] | null = null;

/** Words whose frequency rank falls in a band. Distractors come from the same band as the answer. */
function bandPool(band: Band): number[] {
  return vocab.words.reduce<number[]>((out, w, i) => {
    if (w.r >= band.from && w.r <= band.to) out.push(i);
    return out;
  }, []);
}

/**
 * The highest frequency rank this course has a word for.
 *
 * The placement test credits a band by how many words it covers, and the top band it asks about
 * runs past the end of three of the four courses: ja-en stops at rank 2,092 and en-ja and en-zh at
 * 2,000, while the band reaches 2,500. Without this a perfect run reported knowing more words than
 * the course contains. Mandarin's ranks come from a 42,376-word list and go nowhere near, so it is
 * unaffected (MYN-204).
 */
function rankReach(): number {
  return vocab.words.reduce((top, w) => (w.r > top ? w.r : top), 0);
}

function nextTestQuestion(): void {
  const run = testRun;
  if (!run) return;
  run.chosen = null;

  // Move on when the band is finished, and stop when the bands have stopped telling us anything.
  while (run.band < BANDS.length && run.askedInBand >= PER_BAND) {
    const scored = run.results[run.band];
    run.blanks = scored && learnedNothing(scored) ? run.blanks + 1 : 0;
    run.band++;
    run.askedInBand = 0;
    if (run.blanks >= GIVE_UP_AFTER) break;
  }
  if (run.band >= BANDS.length || run.blanks >= GIVE_UP_AFTER) {
    run.q = null;
    run.done = true;
    void saveTest();
    return;
  }

  const band = BANDS[run.band]!;
  const pool = bandPool(band);
  if (!run.results[run.band]) run.results[run.band] = { from: band.from, to: band.to, asked: 0, right: 0 };
  if (pool.length < OPTIONS) {
    // A band the shipped vocabulary cannot fill is skipped rather than half-asked.
    run.askedInBand = PER_BAND;
    nextTestQuestion();
    return;
  }
  const answer = pool[Math.floor(Math.random() * pool.length)]!;
  run.q = buildQuestion(vocab.words, pool, answer, 'meaning', Math.random);
  if (!run.q) {
    run.askedInBand++;
    nextTestQuestion();
  }
}

function startTest(): void {
  testRun = { band: 0, askedInBand: 0, results: [], q: null, chosen: null, blanks: 0, done: false, saved: null };
  nextTestQuestion();
}

function answerTest(id: number): void {
  const run = testRun;
  if (!run || !run.q || run.chosen !== null) return;
  run.chosen = id;
  const scored = run.results[run.band]!;
  scored.asked++;
  if (id === run.q.answer) scored.right++;
  run.askedInBand++;
  // No feedback between questions: being told the answer mid-test teaches the words the later
  // questions are about, and a test that coaches you is measuring itself.
  nextTestQuestion();
  render();
}

async function saveTest(): Promise<void> {
  const run = testRun;
  if (!run || run.saved) return;
  const results = run.results.filter((r): r is BandResult => Boolean(r) && r.asked > 0);
  const asked = results.reduce((n, r) => n + r.asked, 0);
  if (!asked) return;
  const { known } = estimate(results, undefined, rankReach());
  try {
    run.saved = await api<TestResult>('/api/test', {
      method: 'POST',
      body: JSON.stringify({
        known,
        asked,
        right: results.reduce((n, r) => n + r.right, 0),
        bands: results,
      }),
    });
    testHistoryCache = null;
    render();
  } catch (err) {
    // A result that could not be filed is still a result worth showing.
    toast(t`Could not save the result: ${(err as Error).message}`);
  }
}

function testResultPanel(run: TestRun): HTMLElement {
  const results = run.results.filter((r): r is BandResult => Boolean(r) && r.asked > 0);
  const est = estimate(results, undefined, rankReach());
  const deckId = suggestedDeck(est.known);
  const deck = allDecks().find((d) => d.id === deckId);
  const previous = (testHistoryCache ?? []).find((t) => t.id !== run.saved?.id);

  const panel = el('div', { class: 'test-result' });
  panel.append(
    el('div', { class: 'test-headline' }, el('b', {}, String(est.known)), t('words you can probably read')),
    el(
      'p',
      { class: 'field-hint' },
      `From ${results.reduce((n, r) => n + r.right, 0)} right out of ${results.reduce((n, r) => n + r.asked, 0)}, ` +
        'corrected for guessing — one in four right is what picking at random looks like, and counts as nothing.',
    ),
  );

  if (previous) {
    const delta = est.known - previous.known;
    panel.append(
      el(
        'p',
        { class: 'test-delta' },
        delta === 0
          ? t('The same as your last test.')
          : t`${delta > 0 ? '+' : ''}${delta} since your last test (${previous.known}).`,
      ),
    );
  }

  const chart = el('div', { class: 'band-chart' });
  for (const b of est.bands) {
    const pct = Math.round(b.share * 100);
    chart.append(
      el(
        'div',
        { class: `band-row${b.asked ? '' : ' unasked'}` },
        el('span', { class: 'band-label' }, BANDS.find((x) => x.from === b.from)?.label ?? `${b.from}–${b.to}`),
        el('span', { class: 'band-bar' }, el('span', { class: 'band-fill', style: `width:${pct}%` })),
        el('span', { class: 'band-pct' }, b.asked ? `${pct}%` : t('not asked')),
      ),
    );
  }
  panel.append(chart);

  panel.append(
    el(
      'div',
      { class: 'test-next' },
      el('p', {}, t`Start in ${deck?.name ?? deckId} — a step below where you tested, so the deck is not all new at once.`),
      el(
        'div',
        { class: 'test-actions' },
        el('a', { class: 'btn btn-primary', href: `#/study/${deckId}` }, t`Study ${deck?.name ?? deckId}`),
        el(
          'button',
          {
            class: 'btn',
            type: 'button',
            onclick: () => {
              startTest();
              render();
            },
          },
          t('Test again'),
        ),
      ),
    ),
  );
  return panel;
}

function testView(): HTMLElement {
  const view = el('div');
  view.append(
    el(
      'div',
      { class: 'page-head' },
      el('h1', {}, t('Where you are')),
      el(
        'p',
        {},
        t("A short placement test: words drawn from across the frequency list, commonest first, until you stop recognising them. It estimates how much you can read and where to start."),
      ),
    ),
  );

  if (testHistoryCache === null) {
    void api<{ tests: TestResult[] }>('/api/tests')
      .then((r) => {
        testHistoryCache = r.tests;
        render();
      })
      .catch(() => {
        testHistoryCache = [];
      });
  }

  const run = testRun;

  if (run && !run.done && run.q) {
    const band = BANDS[run.band]!;
    const total = BANDS.length * PER_BAND;
    const soFar = run.results.reduce((n, r) => n + (r?.asked ?? 0), 0);
    const card = el('div', { class: 'quiz-card' });
    card.append(
      el(
        'div',
        { class: 'test-progress' },
        el('span', {}, t`Question ${soFar + 1} of up to ${total}`),
        el('span', { class: 'band-tag' }, band.label),
      ),
      el('div', { class: 'drill-ask han', lang: pair.learning.code }, vocab.words[run.q.answer]!.s),
    );
    const opts = el('div', { class: 'quiz-opts' });
    run.q.options.forEach((id, i) => {
      opts.append(
        el(
          'button',
          { type: 'button', class: 'quiz-opt', onclick: () => answerTest(id) },
          el('span', { class: 'opt-key' }, String(i + 1)),
          el('span', {}, glossLabel(vocab.words[id]!)),
        ),
      );
    });
    trackPointer(opts);
    card.append(opts);
    card.append(
      el(
        'p',
        { class: 'field-hint', style: 'margin-top:14px' },
        t("No feedback until the end — being told the answers would teach you the later words. If you do not know one, guess; guessing is accounted for."),
      ),
    );
    view.append(card);
    return view;
  }

  if (run && run.done) {
    view.append(testResultPanel(run));
  } else {
    view.append(
      el(
        'div',
        { class: 'quiz-card test-intro' },
        el('p', {}, t`${BANDS.length * PER_BAND} questions at most, a few minutes. Characters in, meaning out.`),
        el(
          'p',
          { class: 'field-hint' },
          t('It stops early once two bands in a row come back empty, because the bands only get harder.'),
        ),
        el(
          'button',
          {
            class: 'btn btn-primary',
            type: 'button',
            onclick: () => {
              startTest();
              render();
            },
          },
          t('Start the test'),
        ),
      ),
    );
  }

  const past = testHistoryCache ?? [];
  if (past.length) {
    const list = el('div', { class: 'test-history' });
    for (const result of past.slice(0, 10)) {
      list.append(
        el(
          'div',
          { class: 'test-row' },
          el('span', { class: 'test-when' }, new Date(result.takenAt).toLocaleDateString()),
          el('span', { class: 'test-known' }, t`${result.known} words`),
          el('span', { class: 'test-score' }, `${result.right}/${result.asked}`),
        ),
      );
    }
    view.append(el('div', { class: 'section' }, el('h2', {}, t('Past tests')), list));
  }
  return view;
}

// ---------------------------------------------------------------- tutor

/**
 * What the learner last asked, and what came back.
 *
 * Kept outside the view so typing a question and pressing Enter does not lose the answer when the
 * view re-renders, and so switching tabs and coming back leaves the answer where it was.
 */
let tutorQuery = '';
let tutorHits: Hit[] | null = null;

/**
 * Which question the Ask page is answering.
 *
 * Two different questions live here — "what does this grammar do" and "how do I say this" — and
 * they want different answers from different data. One box that guessed between them would get it
 * wrong often enough to be annoying, and an eighth tab for the second one is worse.
 */
type AskMode = 'explain' | 'translate';

/**
 * Which of the two the course has anything to say in.
 *
 * `explain` reads tutor.json, and only Mandarin has one: it holds 206 entries, and ja-en, en-ja
 * and en-zh hold none. Offering the mode there gave the learner a page that promised "grammar
 * questions, answered from what Mynah already knows" and then showed two empty headings, and
 * answered every question with "not something I can answer" — which is the right thing to say
 * about a gap in a knowledge base that exists, and the wrong thing to say when there is no
 * knowledge base at all. It tells someone their question is unusual when the truth is that this
 * course has no grammar notes.
 *
 * `translate` reads the sentences, and every course has those — 8,386 at the fewest — so the list
 * is never empty and the page always has somewhere to land (MYN-205).
 */
function askModes(): AskMode[] {
  const modes: AskMode[] = [];
  // Asked in this order because it is the order of ambition: explain what a thing does, then how
  // to say one. Where there is no tutor the second is the only one, and it lands there.
  if (tutor.entries.length) modes.push('explain');
  modes.push('translate');
  return modes;
}

/**
 * The mode being asked in, which is never one the course cannot answer.
 *
 * Held as a preference rather than read from it: the learner's choice has to survive a re-render,
 * but it must not survive a change of course into a course where it means nothing.
 */
let askModeChoice: AskMode = 'explain';
function currentAskMode(): AskMode {
  const modes = askModes();
  return modes.includes(askModeChoice) ? askModeChoice : modes[0]!;
}

function askTutor(query: string): void {
  tutorQuery = query;
  tutorHits = query.trim() && currentAskMode() === 'explain' ? search(tutor.entries, query) : null;
}

/** One explanation, with the corpus sentences that show it. */
function tutorAnswer(hit: Hit): HTMLElement {
  const e = hit.entry;
  const card = el(
    'div',
    { class: 'answer' },
    el(
      'div',
      { class: 'answer-head' },
      el('h3', {}, e.title),
      el('span', { class: 'chip' }, e.kind === 'pair' ? t('word pair') : t('grammar')),
    ),
    el('p', { class: 'answer-body' }, e.body),
  );

  if (e.examples.length) {
    const examples = el('div', { class: 'answer-examples' });
    // Real sentences from the decks, not invented ones — so an explanation doubles as revision.
    for (const i of e.examples) {
      const s = sentences.sentences[i];
      if (!s) continue;
      examples.append(
        el(
          'div',
          { class: 'answer-example' },
          // Pinyin always, whatever the card setting says: this sentence is here to be READ as
          // evidence for an explanation, not to be recalled, so blurring it hides the evidence.
          sentenceEl(s, {}),
          el('div', { class: 'example-en' }, s.en),
          speakButton(s.s, { clip: `s/${s.tid}`, iconOnly: true }),
        ),
      );
    }
    card.append(el('div', { class: 'answer-examples-head' }, t('From the decks')), examples);
  }
  return card;
}

function tutorView(): HTMLElement {
  const view = el('div');
  view.append(
    el(
      'div',
      { class: 'page-head' },
      el('h1', {}, t('Ask')),
      el(
        'p',
        {},
        // THROUGH t(), like every other sentence. These two were plain concatenation, so the
        // catalogue's translation of the first could never match and the page stayed in English
        // inside a course that speaks Chinese — the exact failure the English-as-key design is
        // supposed to make loud, gone quiet because nothing asked.
        currentAskMode() === 'explain'
          ? t`Grammar questions, answered from what Mynah already knows — offline, with examples pulled from the sentences in your decks. Type a question, or just type the ${pair.learning.everyday} you are stuck on.`
          : // And named from the pair rather than from Mandarin. "in characters and pinyin" is
            // true of one course and wrong in an English one, where there are neither.
            t`Type ${pair.explainedIn.everyday} and see how to say it in ${pair.learning.everyday}. Answers come from sentences people have translated and the words Mynah teaches — so they are real ${pair.learning.everyday}, not invented.`,
      ),
    ),
  );

  const modes = el('div', { class: 'chips', style: 'margin-bottom:14px' });
  // Labels are sentences too. These two sat outside t() and stayed English inside a course that
  // speaks Chinese, which is the same omission the paragraph above them had.
  const labels = { explain: t('Explain grammar'), translate: t('How do I say…') } as const;
  for (const id of askModes()) {
    modes.append(
      el(
        'button',
        {
          type: 'button',
          class: `chip${currentAskMode() === id ? ' on' : ''}`,
          onclick: () => {
            askModeChoice = id;
            // The question carries over, because "会 vs 能" and "how do I say can" are often the
            // same wondering asked two ways.
            askTutor(tutorQuery);
            render();
          },
        },
        labels[id],
      ),
    );
  }
  // ONE MODE IS NOT A CHOICE. A row of chips with a single chip in it is furniture that asks the
  // learner to make a decision they do not have.
  if (askModes().length > 1) view.append(modes);

  const input = el('input', {
    class: 'ask-input',
    type: 'search',
    // Examples of the KIND of question, not of one language's grammar. These were 了, 会 vs 能 and
    // 把 — Mandarin questions that read as nonsense in a course teaching English — and the comment
    // here used to say making them belong to the pair was corpus work. It is not: what a learner
    // needs to see is the shape of a question they may ask, and that shape is the same in every
    // language. The Japanese catalogue had already worked this out and translated them generically
    // rather than literally; this is the English catching up (MYN-144).
    placeholder:
      currentAskMode() === 'explain'
        ? t('What does this word mean?  ·  when do I use it  ·  why is it in that order')
        : t('I am hungry  ·  how much is it  ·  where is the toilet'),
    'aria-label': t('Ask a question'),
    onkeydown: (e: Event) => {
      if ((e as KeyboardEvent).key !== 'Enter') return;
      askTutor((e.currentTarget as HTMLInputElement).value);
      render();
    },
  }) as HTMLInputElement;
  input.value = tutorQuery;

  view.append(
    el(
      'div',
      { class: 'ask-bar' },
      input,
      el(
        'button',
        {
          class: 'btn btn-primary',
          type: 'button',
          onclick: () => {
            askTutor($<HTMLInputElement>('.ask-input').value);
            render();
          },
        },
        t('Ask'),
      ),
    ),
  );

  if (currentAskMode() === 'translate') {
    if (tutorQuery.trim()) view.append(translateResults(tutorQuery));
    else {
      view.append(
        el(
          'p',
          { class: 'field-hint' },
          t('Short and plain finds the most: “I am hungry” rather than “I could really eat”.'),
        ),
      );
    }
    return view;
  }

  if (tutorHits === null) {
    // Nothing asked yet: show what is here rather than an empty page. Browsing is how anyone finds
    // out what a knowledge base covers — a bare search box only helps if you already know.
    const points = tutor.entries.filter((e) => e.kind === 'point');
    const pairs = tutor.entries.filter((e) => e.kind === 'pair');
    for (const [name, group] of [
      [t('Grammar'), points],
      [t('Words people mix up'), pairs],
    ] as const) {
      const list = el('div', { class: 'ask-browse' });
      for (const e of group) {
        list.append(
          el(
            'button',
            {
              class: 'browse-chip',
              type: 'button',
              onclick: () => {
                askTutor(e.words[0] ?? e.title);
                render();
              },
            },
            e.title,
          ),
        );
      }
      view.append(el('div', { class: 'section' }, el('h2', {}, name), list));
    }
    return view;
  }

  if (!tutorHits.length) {
    // The honest answer. A knowledge base that reaches for its nearest entry rather than admitting
    // the gap is worse than no tutor: a learner asking about grammar cannot tell a wrong
    // explanation from a right one, which is exactly why they asked.
    view.append(
      el(
        'div',
        { class: 'answer none' },
        el('h3', {}, t('Not something I can answer')),
        el(
          'p',
          {},
          `Nothing here covers “${tutorQuery}”. Mynah answers grammar questions and pairs of words ` +
            'people mix up — it is a fixed set of explanations, not a conversation, so it says nothing ' +
            'rather than guessing.',
        ),
        el(
          'button',
          {
            class: 'btn',
            type: 'button',
            onclick: () => {
              tutorQuery = '';
              tutorHits = null;
              render();
            },
          },
          t('See what it does cover'),
        ),
      ),
    );
    return view;
  }

  for (const hit of tutorHits) view.append(tutorAnswer(hit));
  return view;
}

// ---------------------------------------------------------------- router

function currentRoute(): { name: string; arg?: string } {
  const hash = location.hash.replace(/^#/u, '') || '/';
  const parts = hash.split('/').filter(Boolean);
  if (parts[0] === 'study' && parts[1]) return { name: 'study', arg: parts[1] };
  if (parts[0] === 'decks' && parts[1]) return { name: 'area', arg: parts[1] };
  if (parts[0] === 'stories') return { name: 'stories' };
  if (parts[0] === 'drills') return { name: 'drills' };
  if (parts[0] === 'ask') return { name: 'ask' };
  if (parts[0] === 'test') return { name: 'test' };
  if (parts[0] === 'story' && parts[1]) return { name: 'story', arg: parts[1] };
  if (parts[0] === 'pronounce') return { name: 'pronounce' };
  if (parts[0] === 'progress') return { name: 'progress' };
  if (parts[0] === 'known') return { name: 'known' };
  if (parts[0] === 'made') return { name: 'made' };
  if (parts[0] === 'next') return { name: 'next' };
  if (parts[0] === 'languages') return { name: 'languages' };
  // Anything unrecognised, and the bare root, is home.
  return { name: 'home' };
}

function render(): void {
  // Anything the previous view mounted is about to be discarded.
  liveCard = null;
  liveActions = null;
  const route = currentRoute();
  const view = $('#view');
  view.className = `view${route.name === 'study' ? ' study' : ''}`;
  // Leaving a story must silence it: the reader plays on its own, so navigating away from it
  // otherwise leaves a voice reading a page nobody is looking at.
  if (route.name !== 'story') {
    if (reader) pauseReader();
    reader = null;
  }
  // A table rather than a ternary chain. With ten routes the nested form had drifted out of shape
  // and a stale arm was still pointing at a view that no longer exists.
  const VIEWS: Record<string, (arg?: string) => HTMLElement> = {
    area: (arg) => areaView(arg!),
    study: (arg) => studyView(arg!),
    stories: () => storiesView(),
    story: (arg) => storyView(arg!),
    ask: () => tutorView(),
    drills: () => drillsView(),
    test: () => testView(),
    pronounce: () => pronounceView(),
    progress: () => progressView(),
    known: () => knownView(),
    made: () => madeView(),
    next: () => nextView(),
    languages: () => languagesView(),
    home: () => homeView(),
  };
  // Cleared before the view is built, so a view that puts nothing there leaves nothing behind.
  document.querySelector('#bar-tools')?.replaceChildren();
  view.replaceChildren((VIEWS[route.name] ?? VIEWS.home!)(route.arg));

}

// ---------------------------------------------------------------- keyboard

function onKey(e: KeyboardEvent): void {
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
  if ($('#settings').hasAttribute('open')) return;
  const route = currentRoute();

  if (route.name === 'drills' && drillRun) {
    const run = drillRun;
    if (run.chosen === null && run.q && ['1', '2', '3', '4'].includes(e.key)) {
      const id = run.q.options[Number(e.key) - 1];
      if (id !== undefined) {
        e.preventDefault();
        answerDrill(id);
      }
      return;
    }
    if (run.chosen !== null && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      nextDrillQuestion();
      render();
      return;
    }
    if (e.key.toLowerCase() === 's' && run.q) {
      speech.speak(vocab.words[run.q.answer]!.s);
      return;
    }
  }

  if (route.name === 'test' && testRun?.q && testRun.chosen === null) {
    if (['1', '2', '3', '4'].includes(e.key)) {
      const id = testRun.q.options[Number(e.key) - 1];
      if (id !== undefined) {
        e.preventDefault();
        answerTest(id);
      }
      return;
    }
  }

  if (route.name === 'study' && session) {
    if ((e.key === ' ' || e.key === 'Enter') && !session.flipped && currentCard()) {
      e.preventDefault();
      flipCard();
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goBack();
      return;
    }
    if (e.key === 'ArrowRight' || e.key === 'n' || e.key === 'N') {
      e.preventDefault();
      void goForward();
      return;
    }
    // K does exactly what the toggle on the card does — one behaviour, not two that nearly agree.
    if (session.flipped && e.key.toLowerCase() === 'k') {
      const card = currentCard();
      if (card) {
        e.preventDefault();
        void toggleKnown(card.kind, card.id);
      }
      return;
    }
    // F is the "I forgot" control on the answer rail — only once the answer is showing, because
    // forgetting is a judgement about it. Moving on any other way stays 'good'.
    if (session.flipped && e.key.toLowerCase() === 'f') {
      e.preventDefault();
      forgetCard();
      return;
    }

    const playing = currentCard();
    if (e.key.toLowerCase() === 's' && playing) {
      const { text, clip } = spokenOf(playing.kind, playing.id);
      speech.speak(text, clip);
      return;
    }
    if (e.key.toLowerCase() === 'p') {
      prefs.showPinyin = !prefs.showPinyin;
      savePrefs();
      render();
    }
  }
}

// ---------------------------------------------------------------- settings dialog

interface ModelState {
  configured: boolean;
  source: 'settings' | 'environment' | null;
  hint: string | null;
  model: string;
  baseUrl: string;
  editable: boolean;
  defaults: { model: string; baseUrl: string };
}

/**
 * The model settings.
 *
 * THE KEY BOX IS ALWAYS EMPTY, and that is not a bug to be fixed later. A settings page that
 * renders the key back so you can check it has put the key in the DOM, in the browser's memory, in
 * any screenshot of the window and in the next person's view over your shoulder. What comes back is
 * whether one is set, where it came from, and its last four characters — which is the only thing
 * anybody actually squints at a key for.
 */
function wireModelSettings(): void {
  const row = $('#model-row');
  const state = $('#model-state');
  const key = $<HTMLInputElement>('#set-llm-key');
  const model = $<HTMLInputElement>('#set-llm-model');
  const base = $<HTMLInputElement>('#set-llm-base');
  const check = $<HTMLButtonElement>('#btn-llm-check');
  const clear = $<HTMLButtonElement>('#btn-llm-clear');
  let current: ModelState | null = null;

  const describe = (s: ModelState) => {
    current = s;
    row.toggleAttribute('hidden', !s.editable);
    model.value = s.model;
    base.value = s.baseUrl === s.defaults.baseUrl ? '' : s.baseUrl;
    clear.toggleAttribute('hidden', s.source !== 'settings');
    check.toggleAttribute('disabled', !s.configured);
    state.textContent = s.configured
      ? s.source === 'environment'
        ? `A key is set by the server (${s.hint}). Anything you put here replaces it.`
        : `A key is saved (${s.hint}).`
      : t('No key yet — this section is off.');
  };

  const save = async (patch: Record<string, string>) => {
    const next = await api<ModelState>('/api/model', {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
    key.value = '';
    describe({ ...(current as ModelState), ...next, source: next.configured ? 'settings' : null });
    toast(t('Saved'));
  };

  // On blur rather than on every keystroke: a key pasted character by character would be saved
  // sixty times and half of those saves would be of a truncated key.
  key.addEventListener('change', () => {
    if (key.value.trim()) void save({ apiKey: key.value.trim() });
  });
  model.addEventListener('change', () => void save({ model: model.value.trim() }));
  base.addEventListener('change', () => void save({ baseUrl: base.value.trim() }));
  clear.addEventListener('click', () => void save({ apiKey: '' }));

  check.addEventListener('click', async () => {
    check.toggleAttribute('disabled', true);
    const was = state.textContent;
    state.textContent = t('Asking the model…');
    try {
      const out = await api<{ ok: boolean; model?: string; error?: string }>('/api/model/check', {
        method: 'POST',
      });
      state.textContent = out.ok ? `Working — ${out.model} answered.` : `Not working: ${out.error}`;
    } catch (err) {
      state.textContent = `Not working: ${(err as Error).message}`;
    } finally {
      check.toggleAttribute('disabled', false);
      if (!state.textContent) state.textContent = was;
    }
  });

  void api<ModelState>('/api/model').then(describe);
}

function wireSettings(): void {
  const dlg = $<HTMLDialogElement>('#settings');
  const intro = $<HTMLInputElement>('#set-intro');
  const voiceSel = $<HTMLSelectElement>('#set-voice');
  const cardSel = $<HTMLSelectElement>('#set-card');
  const cardHint = $('#set-card-hint');
  const themeSel = $<HTMLSelectElement>('#set-theme');
  const voiceHint = $('#set-voice-hint');
  const clips = $<HTMLInputElement>('#set-clips');
  const clipsHint = $('#set-clips-hint');
  const fetchRow = $('#set-fetch');
  const fetchFill = $('#set-fetch-fill');
  const fetchHint = $('#set-fetch-hint');
  const fetchGo = $<HTMLButtonElement>('#set-fetch-go');
  const fetchClear = $<HTMLButtonElement>('#set-fetch-clear');
  const courseRow = $('#set-course');
  const courseName = $('#set-course-name');

  // Where somebody goes looking for it. The picker is a page of its own — two questions and a
  // download do not belong in a sheet that also holds the theme — so this is the door to it, named
  // after what it changes rather than after the machinery.
  // EITHER WAY OF CHANGING IT COUNTS. A phone installs one; a server already holds them and picks
  // (MYN-142). MYN-109 hid this row wherever a swap could not work, and on a hosted instance it
  // could not — until the server learned to carry more than one course.
  const swappable = corpusState.installable || serverCourses.choosable;
  courseRow.hidden = !swappable;
  $('#btn-course').addEventListener('click', () => {
    dlg.close();
    location.hash = '#/languages';
  });

  /**
   * What is left to fetch, and what it is doing.
   *
   * Deliberately NOT a switch that runs on its own. A hundred and fifty megabytes is somebody
   * else's data allowance, and on a phone the app cannot tell whether it is on wifi — so the size
   * is stated and the learner presses the button. Once pressed it resumes by itself on every
   * launch until it is finished.
   */
  const paintFetch = (): void => {
    const total = speech.clips?.absent?.clips ?? 0;
    if (!total || !clipCache.supported()) {
      fetchRow.hidden = true;
      return;
    }
    fetchRow.hidden = false;
    const p = clipCache.progress(speech.clips?.absent?.bytes ?? 0);
    const done = p.done >= p.total;
    fetchFill.style.width = `${percent(p)}%`;
    fetchGo.textContent = done ? t('Done') : clipCache.fetching() ? t('Pause') : t('Download');
    fetchGo.disabled = done;
    fetchClear.hidden = p.done === 0;
    fetchHint.textContent = done
      ? `All ${p.total.toLocaleString()} sentence recordings are on this device.`
      : clipCache.lastTrouble() === 'unreachable'
        ? t('Could not reach the recordings. They will carry on when the connection is back.')
        : p.done === 0
          ? `Sentences speak in a system voice until these arrive. ${mb(p.totalBytes)}, fetched in deck order — studying never waits for it.`
          : `${p.done.toLocaleString()} of ${p.total.toLocaleString()} · ${mb(p.bytes)} of ${mb(p.totalBytes)}`;
  };
  clipCache.watch(paintFetch);

  fetchGo.addEventListener('click', () => {
    if (clipCache.fetching()) clipCache.stop();
    else void clipCache.start(speech.clips?.origin ?? '');
    paintFetch();
  });
  fetchClear.addEventListener('click', () => {
    void clipCache.clear().then(paintFetch);
  });
  const pinyin = $<HTMLInputElement>('#set-pinyin');
  const trad = $<HTMLInputElement>('#set-trad');
  const autoplay = $<HTMLInputElement>('#set-autoplay');

  for (const style of CARD_STYLES) {
    const opt = el('option', { value: style.id }, t(style.name));
    if (style.id === prefs.cardStyle) opt.selected = true;
    cardSel.append(opt);
  }
  const describeCard = () => {
    cardHint.textContent = t(CARD_STYLES.find((c) => c.id === prefs.cardStyle)?.blurb ?? '');
  };
  describeCard();
  cardSel.addEventListener('change', () => {
    prefs.cardStyle = cardSel.value as CardStyle;
    savePrefs();
    describeCard();
    render(); // the style lives on the stage, so the card has to be rebuilt
  });

  // Built once: the theme list is fixed for the life of the page.
  for (const id of availableThemes()) {
    const opt = el('option', { value: id }, id.charAt(0).toUpperCase() + id.slice(1));
    if (id === prefs.theme) opt.selected = true;
    themeSel.append(opt);
  }
  // A stored theme this build does not ship leaves a <select> showing its FIRST option, which is
  // indistinguishable from the app having forgotten — and worse, the next change would save that
  // arbitrary first theme as a deliberate choice.
  if (!availableThemes().includes(prefs.theme)) {
    prefs.theme = DEFAULT_PREFS.theme;
    themeSel.value = prefs.theme;
    applyTheme(prefs.theme);
  }
  themeSel.addEventListener('change', () => {
    prefs.theme = themeSel.value;
    savePrefs();
    applyTheme(prefs.theme);
  });

  $('#btn-settings').addEventListener('click', () => {
    courseName.textContent = `${pair.learning.name} · ${t`explained in ${pair.explainedIn.name}`}`;
    // Here rather than once at boot, for the same reason the line above is: switching the app's
    // language re-opens this sheet, and a hint translated at boot would still be in the language
    // somebody just left.
    $('#set-course-hint').textContent = corpusState.installable
      ? t('One course at a time. Changing it downloads the new one and removes what the old one had on this device. Your progress in each is kept separately.')
      : t('Every course is already installed here, so switching is immediate and nothing is downloaded. Your progress in each is kept separately.');
    intro.value = String(progress.introLimit);
    pinyin.checked = prefs.showPinyin;
    trad.checked = prefs.showTraditional;
    autoplay.checked = prefs.autoplay;

    clips.checked = prefs.useClips && speech.clips !== null;
    clips.disabled = speech.clips === null;
    paintFetch();
    clipsHint.textContent = speech.clips
      ? `${speech.clips.count} words recorded with ${speech.clips.voice}. Same audio on every device, no internet needed.`
      : t('Not included in this build — falling back to a system voice.');

    voiceSel.replaceChildren();
    // hasVoice, not "can anything be played": with recordings present this used to take the other
    // branch and render an empty list of voices as though some were installed.
    if (!speech.hasVoice()) {
      voiceSel.append(el('option', { value: '' }, t`No ${pair.learning.everyday} voice installed`));
      voiceSel.disabled = true;
    } else {
      voiceSel.disabled = false;
      // Voices arrive best-first, so the head of the list is what auto-selection would choose.
      speech.voices.forEach((v, i) => {
        const q = voiceQuality(v);
        const label = `${v.name} · ${q}${i === 0 ? ' · best available' : ''}`;
        const opt = el('option', { value: v.voiceURI }, label);
        if (prefs.voice === v.voiceURI) opt.selected = true;
        voiceSel.append(opt);
      });
    }
    // Apple's compact voices are the robotic ones. Say so, and say exactly how to fix it, rather
    // than leaving the learner to conclude the app simply sounds bad.
    voiceHint.textContent = speech.usingClips()
      ? t('Only used if a recording is missing.')
      : !speech.hasVoice()
      ? t`No ${pair.learning.everyday} voice is installed on this device.`
      : speech.onlyCompact()
        ? t`Only Apple’s low-quality “compact” voices are installed. For a far better voice, open System Settings → Accessibility → Spoken Content → System Voice → Manage Voices, expand ${pair.learning.everyday}, and download a better one. Reload this page afterwards.`
        // Named from the pair, not from Mandarin: this line is about whichever language is on the
        // cards, and "Chinese voices installed" is wrong the moment that is not Chinese.
        : t`${pair.learning.everyday} voices installed on this device. Premium and Enhanced voices sound markedly better than Compact ones.`;
    dlg.showModal();
  });

  intro.addEventListener('change', () => {
    const n = Number(intro.value);
    if (!Number.isFinite(n) || n < 0) return;
    void api('/api/settings', { method: 'PUT', body: JSON.stringify({ introLimit: n }) })
      .then(refreshProgress)
      .then(() => toast(t`New words per day: ${Math.round(n)}`))
      .catch((err: Error) => toast(err.message));
  });

  // WHAT A VOICE SAYS WHEN YOU PICK IT. 你好 was hardcoded, so choosing a Japanese voice tested it
  // by handing it a Chinese word — which it will either mangle or refuse, and either way tells you
  // nothing about the voice you just chose. The endonym is the one word guaranteed to exist, to be
  // in the language being taught, and to be worth hearing: 中文, 日本語, English.
  const sample = (): string => pair.learning.endonym;
  voiceSel.addEventListener('change', () => {
    prefs.voice = voiceSel.value || null;
    savePrefs();
    speech.speak(sample());
  });
  clips.addEventListener('change', () => {
    prefs.useClips = clips.checked;
    savePrefs();
    speech.speak(sample());
    render();
  });
  // Stroke order is a property of the CORPUS, not of the pair — a build can teach Mandarin and
  // still have been built without running the generator. So the row goes when there is no data,
  // rather than offering a setting that changes nothing.
  const strokeRow = $('#set-strokes-row');
  const strokeBox = $<HTMLInputElement>('#set-strokes');
  strokeRow.hidden = !strokes;
  strokeBox.checked = prefs.showStrokes;
  strokeBox.addEventListener('change', () => {
    prefs.showStrokes = strokeBox.checked;
    savePrefs();
    render();
  });

  // The same rule as stroke order, and for the same reason — it was simply never applied here.
  // Character etymology reads hints.hints, which only the Chinese corpus fills (1,840 entries;
  // the other three have none), so everywhere else this was a switch that changed nothing and an
  // explanation in a language the course does not teach. Gated, the example below it is finally
  // true: 女 + 马 makes 妈 is only ever said to somebody learning Mandarin.
  // Each row asks its own question of its own data, the way the stroke row does. A picture is not
  // an etymology and a course can have either, both or neither.
  const pictureRow = $('#set-pictures-row');
  const pictureBox = $<HTMLInputElement>('#set-pictures');
  pictureRow.hidden = Object.keys(pair.pictures ?? {}).length === 0;
  pictureBox.checked = prefs.showPictures;
  pictureBox.addEventListener('change', () => {
    prefs.showPictures = pictureBox.checked;
    savePrefs();
    render();
  });

  const hintRow = $('#set-hints-row');
  const hintBox = $<HTMLInputElement>('#set-hints');
  hintRow.hidden = Object.keys(hints.hints).length === 0;
  hintBox.checked = prefs.showHints;
  hintBox.addEventListener('change', () => {
    prefs.showHints = hintBox.checked;
    savePrefs();
    render();
  });

  pinyin.addEventListener('change', () => {
    prefs.showPinyin = pinyin.checked;
    savePrefs();
    // The card on screen shows it or does not; there is nothing to re-fetch.
    render();
  });
  trad.addEventListener('change', () => {
    prefs.showTraditional = trad.checked;
    savePrefs();
    render();
  });
  autoplay.addEventListener('change', () => {
    prefs.autoplay = autoplay.checked;
    savePrefs();
  });

  // The account row exists only in hosted mode; in local mode there is nobody to sign out as.
  if (me?.canSignOut) {
    $('#account-row').removeAttribute('hidden');
    $('#account-name').textContent = me.displayName;
    $('#btn-signout').addEventListener('click', () => {
      void api('/api/signout', { method: 'POST', body: '{}' })
        .then(() => location.reload())
        .catch((err: Error) => toast(err.message));
    });
  }

  $('#btn-reset').addEventListener('click', () => {
    if (!confirm('Clear all scheduling and review history? The decks themselves are not affected.')) return;
    void api<{ cleared: number }>('/api/reset', { method: 'POST', body: JSON.stringify({}) })
      .then(async (r) => {
        await refreshProgress();
        session = null;
        render();
        toast(t`Cleared ${r.cleared} cards`);
      })
      .catch((err: Error) => toast(err.message));
  });
}

// ---------------------------------------------------------------- boot

/**
 * Reconcile the cached preferences with the stored ones.
 *
 * An empty response means this machine has never stored any — including every learner who used the
 * app before preferences moved to the server — so whatever the local cache holds is pushed up
 * rather than discarded. Losing someone's theme while fixing the bug that loses their theme would
 * be a poor trade.
 */
async function syncPrefs(): Promise<void> {
  try {
    const stored = await api<Partial<Prefs>>('/api/prefs');
    if (stored && Object.keys(stored).length) {
      prefs = { ...DEFAULT_PREFS, ...stored };
      try {
        localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
      } catch {
        /* the cache is an optimisation, not a requirement */
      }
      return;
    }
    savePrefs();
  } catch {
    // Reaching the server is different from the server having nothing, and conflating them is how
    // a learner gets shown the default theme as if it were their choice. Say so.
    toast(t('Could not load your settings — showing the defaults for now.'));
  }
}

/**
 * Dress the settings button.
 *
 * There used to be a row of eight nav icons here as well. They went: the home page IS the
 * navigation — every section is a tile on it with a picture and a sentence saying what it is — so
 * the bar was a second, worse copy of it, spending the top of every screen to repeat what the
 * first screen already says better.
 */
function dressNav(): void {
  const gear = document.querySelector<HTMLElement>('#btn-settings');
  if (gear) {
    gear.dataset.tip = gear.title || 'Settings';
    gear.removeAttribute('title');
  }
}

/**
 * The screen for somebody who is not signed in.
 *
 * A hosted instance answers 401 to every API call from a browser with no session, and before this
 * existed the app met that with "could not reach the Mynah server" — which is both wrong and
 * unactionable. The server is fine. You are nobody.
 *
 * It says invitation-only out loud rather than letting somebody find out after signing in. That is
 * the same rule the back end enforces (an identity is not an invitation), said before it costs
 * anyone a round trip through an identity provider.
 */
function signInScreen(state: { provider: string | null }): HTMLElement {
  const view = el('div', { class: 'sign-in' });
  view.append(
    el('h1', {}, t('Mynah')),
    el('p', {}, t('Mandarin, one card at a time.')),
    state.provider
      ? el(
          'a',
          { class: 'primary sign-in-btn', href: `/auth/sign-in?return=${encodeURIComponent(location.hash || '#/')}` },
          t`Continue with ${state.provider}`,
        )
      : el('p', { class: 'empty' }, t('This instance has no way to sign anybody in.')),
    el(
      'p',
      { class: 'field-hint' },
      t("Mynah is invitation-only. Signing in identifies you; it does not admit you — whoever runs this copy has to have invited your address first."),
    ),
  );
  return view;
}

/**
 * Whether a failure was the network rather than the server.
 *
 * fetch rejects with a TypeError when the request never got a response at all — no DNS, no route,
 * no connection. An error that came BACK, however unhappy, is the server having an opinion. The
 * two need different words: one is "you are offline", the other is "Mynah is broken", and saying
 * the second when the first is true blames a machine that is usually fine and leaves the learner
 * with nothing to do about it.
 */
function lostTheNetwork(err: unknown): boolean {
  return !navigator.onLine || err instanceof TypeError;
}

/**
 * What to show when the app cannot start, and how to get out of it.
 *
 * The old message was "Could not reach the Mynah server", said for every cause. Kennard hit it
 * twice in one day, both times because his connection had dropped the host — the server was up
 * throughout.
 */
function bootFailed(err: unknown): void {
  const offline = lostTheNetwork(err);
  // THREE CAUSES, NOT TWO, and the third used to be no screen at all — the app simply waited. A
  // server that takes the request and never answers is not the same as one you cannot reach: the
  // network is fine, the machine is there, and what to do about it is different. Saying "you are
  // offline" to somebody whose wifi is working sends them to fix the wrong thing.
  const silent = err instanceof NoAnswer;
  const headline = offline
    ? t('You are offline')
    : silent
      ? t('Mynah’s server is not answering')
      : t('Mynah could not start');
  const detail = offline
    ? t('Mynah needs its server for the cards themselves. This page is here because it was saved on your device; the words are not.')
    : silent
      ? t('It took the request and then said nothing. The page you are looking at was saved on your device; the words come from the server, and it has gone quiet.')
      : t`Its server answered, but with an error: ${(err as Error).message}`;

  const panel = el('div', { class: 'boot-failed' });
  const retry = el(
    'button',
    { class: 'more', type: 'button', onclick: () => location.reload() },
    t('Try again'),
  );
  panel.append(el('h1', {}, headline), el('p', {}, detail), retry);
  $('#view').replaceChildren(panel);

  // Come back on its own. Somebody who walks back into signal should not have to know to reload —
  // and if they are already reloading, this costs nothing.
  if (offline) {
    window.addEventListener('online', () => location.reload(), { once: true });
  }
}

/**
 * Pick the recordings download back up where it left off.
 *
 * Only in the build that lacks them, and only once a download has been started by hand: the first
 * press is the learner agreeing to spend the data, and every launch after that is the app keeping
 * the promise it made. Awaited only as far as reading the bookmark — the fetching itself is left
 * running behind whatever the learner does next, which is the whole design.
 */
async function resumeAudioFetch(): Promise<void> {
  const manifest = speech.clips;
  if (!manifest?.absent?.clips || !clipCache.supported()) return;
  await clipCache.load(planFetch(vocab, sentences, stories), manifest.format, manifest.prefix);
  const p = clipCache.progress(manifest.absent.bytes);
  if (p.done > 0 && p.done < p.total) void clipCache.start(manifest.origin ?? '');
  // And again whenever the connection comes back, since going offline is what stops it.
  window.addEventListener('online', () => {
    const now = clipCache.progress(manifest.absent!.bytes);
    if (now.done > 0 && now.done < now.total) void clipCache.start(manifest.origin ?? '');
  });
}

/**
 * Pick up the app's own voice, in the languages THIS PAIR needs.
 *
 * The build ships every catalogue it has and this chooses two of them: the language the app
 * explains things in, and the language being learned — the second only for the handful of places
 * that show a taste of it rather than explaining in it. Chosen here rather than by the bundler
 * because the pair can now arrive at runtime, and an app whose corpus changed while its prose did
 * not is the exact thing MYN-104 caught.
 *
 * Never fatal: no catalogue means English, which is what the app did before any of this existed.
 */
async function findVoice(declared: Pair, speakTo: string = uiLanguage(declared)): Promise<void> {
  const load = async (code: string): Promise<Record<string, string> | null> => {
    // English is the source. Every t() in this codebase is already English, so there is nothing
    // to fetch and a 404 would only be noise in the console.
    if (code.split('-')[0] === 'en') return null;
    try {
      const res = await fetch(`messages/${code}.json`, { cache: 'no-cache' });
      return res.ok ? ((await res.json()) as Record<string, string>) : null;
    } catch {
      return null;
    }
  };
  const [voice, learning] = await Promise.all([load(speakTo), load(declared.learning.code)]);
  speakIn(voice);
  speakInTarget(learning);
}

// ---------------------------------------------------------------- the way out

/**
 * Which language the app is talking to you in.
 *
 * The course's, unless somebody has said otherwise from the title bar. Read from the preference
 * that lives in this browser's own storage, so it is answerable before a single request — the
 * escape hatch cannot depend on the thing somebody is escaping from.
 */
const uiLanguage = (declared: Pair): string => prefs.readIn ?? declared.explainedIn.code;

/**
 * Every language the app can explain itself in.
 *
 * Seeded with English and with whatever the course uses, so there is ALWAYS somewhere to go even
 * if the catalogue cannot be read — English is the source language and needs no catalogue at all,
 * which makes it the one option that can never be missing. The rest come from the catalogue's
 * courses, because a language with no course behind it would hand somebody a half-translated app.
 */
function explainableIn(declared: Pair, catalogue: Catalogue | null): Language[] {
  const found = new Map<string, Language>();
  found.set('en', { code: 'en', name: 'English', endonym: 'English', everyday: 'English' });
  found.set(declared.explainedIn.code, declared.explainedIn);
  for (const p of catalogue?.pairs ?? []) {
    if (!found.has(p.explainedIn.code)) found.set(p.explainedIn.code, p.explainedIn);
  }
  return [...found.values()];
}

/**
 * Put the language control in the bar and keep it working.
 *
 * Rendered as soon as the pair is known, before the sign-in probe and before any card, because the
 * one moment somebody most needs it is the moment everything else has gone wrong.
 */
function wireLanguageBar(declared: Pair): void {
  const pick = $<HTMLSelectElement>('#pick-ui-lang');

  const fill = (catalogue: Catalogue | null): void => {
    const languages = explainableIn(declared, catalogue);
    pick.replaceChildren();
    for (const lang of languages) {
      // THE ENDONYM, NEVER TRANSLATED. A list that said "Chinese" to somebody who reads only
      // Chinese would be the same trap one level down.
      pick.append(el('option', { value: lang.code }, lang.endonym));
    }
    pick.value = uiLanguage(declared);
    // One option is not a choice, and an unusable control in the bar is furniture.
    pick.hidden = languages.length < 2;
    pick.setAttribute('aria-label', t('The language this app speaks to you'));
    pick.dataset.tip = t('The language this app speaks to you');
  };

  fill(null);
  // The fuller list, when it arrives. Never waited for: the seeded two are enough to get out with.
  void corpusInstall.loadCatalogue().then(fill).catch(() => undefined);

  pick.addEventListener('change', () => {
    void switchUiLanguage(declared, pick.value);
  });
}

/**
 * Change the language the app speaks, now, without downloading anything.
 *
 * No reload. A reload here would be a round trip through whatever is broken — and this is what
 * somebody reaches for when things are broken. Every catalogue is already in the build, so this is
 * a fetch of a few tens of kilobytes from the app's own origin and a re-render.
 */
async function switchUiLanguage(declared: Pair, code: string): Promise<void> {
  prefs.readIn = code;
  savePrefs();

  // THE PAIR IS DERIVED FROM THE TWO CHOICES. Changing the language the app speaks changes which
  // course explains itself that way — so if the server holds the SAME subject explained in the
  // language just chosen, that is the course the learner now wants. Learning English explained in
  // Japanese, switching the app to 中文: en-zh exists, so go there.
  //
  // Only when it exists. Learning Japanese and switching the app to 中文 asks for a course nobody
  // has built, and the answer to that is to leave the course alone and let the picker say so,
  // not to strand somebody on a course that cannot be loaded (MYN-171).
  const matching = courseWith(serverCourses.courses, declared.learning.code, code);
  if (matching && matching.id !== serverCourses.chosen) {
    try {
      await api('/api/courses', { method: 'PUT', body: JSON.stringify({ id: matching.id }) });
      // A FULL RELOAD, for the same reason the picker does one: every corpus in hand belongs to
      // the course just left, and the preference that forced this language is now satisfied by the
      // course itself.
      prefs.readIn = null;
      savePrefs();
      location.hash = '#/';
      location.reload();
      return;
    } catch {
      // The course could not be changed; the app still speaks the new language, which is what was
      // asked for and is the half that never needed the server.
    }
  }
  await findVoice(declared, code);
  document.documentElement.lang = code;
  // Same order as boot: the shell goes back to its English keys and forward into the new language,
  // and only then are the pair's own nouns put into it again.
  sayTheShell();
  applyPair(declared);
  dressNav();
  render();
}

/**
 * Dress the document in the pair that actually loaded.
 *
 * The build writes the shipped pair into the head as a default, because the title, the description
 * and the document's own language are read before a line of script runs. This is the same job done
 * again from the pair in hand — which is how a corpus installed later renames the app instead of
 * leaving it calling itself Mandarin over a deck of English words.
 *
 * The body's placeholders are only resolved here. Everything carrying one lives in the settings
 * sheet, which is closed until somebody opens it, so there is nothing on screen to flicker.
 */
function applyPair(declared: Pair): void {
  // The language on the document is the one being READ, which is the title bar's answer rather
  // than the corpus's — it is what a screen reader announces and what the typography follows.
  document.documentElement.lang = uiLanguage(declared);
  document.title = `Mynah — ${declared.learning.name} flashcards`;
  // The wallpaper is the course's own marks, not the stylesheet's: it was six Chinese characters
  // baked into body::after and it stayed Chinese through every switch (MYN-161). Set here, with
  // the title and the document language, because it answers the same question they do -- what is
  // this app teaching right now.
  document.documentElement.style.setProperty(
    '--wallpaper',
    wallpaperTile(declared.wallpaper, declared.learning.code),
  );
  // And the face that draws this course's characters. The stylesheet answers this per element for
  // everything that declares its language; this is the answer for everything that does not, which
  // is most of the nineteen rules reading --han. PingFang SC contains Japanese kanji and draws
  // them in Chinese shapes, so "a CJK stack" is not an answer (MYN-162).
  document.documentElement.style.setProperty('--han', faceFor(declared.learning.code));
  const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (description) {
    description.content = `${declared.learning.name} flashcards and a pronunciation trainer that run entirely on your own machine.`;
  }

  // What this pair has nothing to put in goes, rather than standing there empty. Same rule as
  // fillPair's, against a live document instead of a string.
  for (const node of document.querySelectorAll<HTMLElement>('[data-pair-needs]')) {
    node.hidden = !pairHas(declared, node.dataset.pairNeeds ?? '');
  }

  const fill = (text: string): string =>
    text.replace(/\{\{([a-zA-Z.]+)\}\}/gu, (_whole, path: string) => pairValue(declared, path));
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.nodeValue;
    if (text && text.includes('{{')) node.nodeValue = fill(text);
  }
}

/**
 * Translate the static shell.
 *
 * index.html carries prose the app cannot reach with t() — the settings sheet is written in HTML
 * and read before a line of script runs. Marking an element `data-say` says "this is a sentence,
 * not a label", and its English is its own key exactly as everywhere else. The English stays IN
 * the HTML, where it is readable, rather than moving to a table of ids.
 */
function sayTheShell(): void {
  for (const node of document.querySelectorAll<HTMLElement>('[data-say]')) {
    // The English is kept the first time through. Without it this is a one-way door: the second
    // call would look up an already-translated string, find nothing, and leave the shell stuck in
    // whichever language it was first rendered in — which is exactly the trap the title bar's
    // language control exists to open.
    // WHITESPACE COLLAPSED BEFORE THE LOOKUP. A sentence written across four lines of HTML carries
    // the indentation with it, and "Everything else in Mynah\n  works…" is a different string from
    // the one in the catalogue — so the longest paragraph in the settings sheet stayed English
    // while every short one beside it translated.
    const english = node.dataset.sayEn ?? node.textContent?.replace(/\s+/gu, ' ').trim();
    if (!english) continue;
    node.dataset.sayEn = english;
    node.textContent = t(english);
  }
}

async function boot(): Promise<void> {
  // THE PAIR COMES FIRST, before the voice and before the sign-in probe. Everything the app says
  // about itself is chosen from it — which language it explains in, what it calls the course, what
  // the settings sheet offers — so asking anything else first means asking it in the wrong
  // language. It is ungated on the server for exactly this reason; a build that cannot answer
  // falls back to the pair it was written for, which is what every render did before it landed.
  try {
    pair = await api<Pair>('/api/pair');
  } catch (err) {
    // THE FIRST SILENCE SETTLES IT. This used to swallow everything and carry on, so a mute server
    // was waited out three times over — /api/pair, then /auth/state, then the corpus — and the
    // screen said "Loading…" for a full minute before admitting anything. Asking a second time
    // learns nothing the first answer did not already say.
    //
    // Only silence stops boot. Every other failure still falls through to the pair this build was
    // written for, which is the case this catch was put here for: a server too old to answer the
    // route at all, where carrying on is exactly right.
    if (err instanceof NoAnswer) return bootFailed(err);
    pair = DEFAULT_PAIR;
  }
  // Before the sign-in probe and before a single card: the moment somebody most needs the way out
  // is the moment everything after this has failed.
  wireLanguageBar(pair);
  await findVoice(pair);
  learningIs(pair.learning.code);
  // TRANSLATE, THEN FILL, and in that order for a reason. The shell's sentences are keyed on their
  // English INCLUDING the placeholder — "Show {{reading.name}} on cards" — so the catalogue holds
  // one entry for a row whatever the pair puts in it. Filling first would leave sayTheShell looking
  // up "Show pinyin on cards", which is in no catalogue and never will be.
  sayTheShell();
  applyPair(pair);
  // Asked FIRST and on its own, because everything below it needs an account and this is the one
  // question a browser with no account may ask.
  try {
    const state = await api<{ mode: string; signedIn: boolean; provider: string | null }>(
      '/auth/state',
    );
    if (!state.signedIn) {
      applyTheme(prefs.theme);
      // The bar is navigation to pages that will all answer 401, and its icons are dressed later
      // in boot — so left up it renders as a row of raw labels pointing at nothing.
      document.documentElement.setAttribute('data-signed-out', '');
      $('#view').replaceChildren(signInScreen(state));
      return;
    }
  } catch {
    // An older server, or one that does not answer this at all: carry on and let the calls below
    // report whatever is actually wrong. A missing sign-in probe is not itself a reason to stop.
  }

  try {
    [vocab, sentences, stories, tutor, hints, strokes, progress, me, corpusState, serverCourses] =
      await Promise.all([
      api<Vocab>('/api/vocab'),
      api<Sentences>('/api/sentences'),
      api<Stories>('/api/stories'),
      api<Tutor>('/api/tutor'),
      api<Hints>('/api/hints'),
      api<Strokes | null>('/api/strokes'),
      api<Progress>('/api/progress'),
      api<typeof me>('/api/me'),
      // Whether this copy can be handed a different course. An older server does not know the
      // route, and a build that cannot swap is the same answer as a server that never could.
      api<typeof corpusState>('/api/corpus').catch(() => ({ installable: false, installed: null })),
      // And whether the server holds others it could teach instead. A server too old to know the
      // route says no by failing, which is the same answer as a server with one course.
      api<typeof serverCourses>('/api/courses').catch(() => ({
        choosable: false,
        chosen: null,
        courses: [],
      })),
    ]);
  } catch (err) {
    bootFailed(err);
    return;
  }
  phonology = phonologyFor(pair.learning.code);
  await syncPrefs();
  await loadKnown();
  // So a link straight to a made story resolves without going through the list first.
  await loadMadeStories().catch(() => undefined);
  applyTheme(prefs.theme);
  dressNav();
  await speech.ready();
  // AFTER speech.ready(), which is what decides whether anything can be spoken at all.
  settleMode();
  // AFTER speech.ready(), which is what reads the manifest — and the manifest is what says whether
  // there is anything to fetch and where from. Before it, this quietly decided there was nothing.
  await resumeAudioFetch();
  wireSettings();
  wireModelSettings();
  window.addEventListener('hashchange', render);
  window.addEventListener('keydown', onKey);
  render();

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('sw.js').catch(() => {
      /* offline support is a bonus, not a requirement */
    });
  }
}

void boot();
